import { useState, useEffect, Component, type ReactNode, type ErrorInfo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Eye, EyeOff, Cpu, Mail, Phone, Globe, Plus, Pencil, Trash2, Users, Shield, MessageSquare, X as XIcon, GripVertical, RefreshCw, RotateCcw, ChevronUp, ChevronDown, Copy, Check, KeyRound } from 'lucide-react'
import { api } from '../../api/client'
import { adminApi, assignmentApi, authApi, metaApi, playbookApi, usersApi, ivrApi, teamsApi, voipApi, type User, type IVRMenu, type IVROption, type Team, type AccessLogEntry } from '../../api/endpoints'
import Button from '../../components/ui/Button'
import Input, { Select, Textarea } from '../../components/ui/Input'
import Modal from '../../components/ui/Modal'
import { useScoringSegments } from '../../hooks/usePipelineStages'
import Badge from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import ScrollableTabs from '../../components/ui/ScrollableTabs'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AppSettings {
  [key: string]: string
}

interface AssignmentRule {
  id: number
  name: string
  strategy: string
  product: string | null  // backend field name
  product_filter: string | null  // legacy frontend alias
  lead_source_filter: string | null
  quality_segment_filter: string | null
  sla_minutes: number | null
  priority: number
  is_active: boolean
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Settings API
// ---------------------------------------------------------------------------

const settingsApi = {
  getAll: () => api.get<{ settings: AppSettings }>('/settings'),
  save: (key: string, value: string) =>
    api.post<{ success: boolean }>('/settings/batch', { settings: { [key]: value } }),
  saveBatch: (settings: Record<string, string>) =>
    api.post<{ success: boolean }>('/settings/batch', { settings }),
}

// ---------------------------------------------------------------------------
// MCP Access API (per-user bearer tokens for connecting Claude)
// ---------------------------------------------------------------------------

interface McpToken {
  id: number
  created_at: string | null
  expires_at: string | null
  active: boolean
}

interface McpTokenCreated {
  id: number
  token: string
  created_at: string | null
  expires_at: string | null
  note: string
}

const mcpApi = {
  list: () => api.get<{ success: boolean; data: McpToken[] }>('/mcp/tokens'),
  create: () => api.post<{ success: boolean; data: McpTokenCreated }>('/mcp/tokens'),
  revoke: (id: number) =>
    api.delete<{ success: boolean; data: { revoked: number } }>(`/mcp/tokens/${id}`),
  getToolConfig: () => api.get<{ success: boolean; data: McpToolConfig }>('/mcp/tool-config'),
  updateToolConfig: (partial: Partial<McpToolConfig>) =>
    api.put<{ success: boolean; data: McpToolConfig }>('/mcp/tool-config', partial),
}

interface McpTierConfig {
  enabled: boolean
  roles: string[]
  daily_cap: number | null
}

interface McpToolConfig {
  meta_read: McpTierConfig
  meta_live: McpTierConfig
  meta_graph_raw: McpTierConfig
  ig_discovery: McpTierConfig
  meta_management: McpTierConfig
  monthly_cost_ceiling_usd: number | null
}

const MCP_ALL_ROLES = ['admin', 'manager', 'marketing', 'sales_rep'] as const

// Tier metadata for the UI. `available` tiers are live in this build; others
// are shown but locked until their phase ships.
const MCP_TIERS: { key: keyof McpToolConfig; label: string; desc: string; available: boolean }[] = [
  { key: 'meta_read', label: 'Meta Ads — Read (synced)', desc: 'Query already-synced campaigns, ad sets, ads and insights from the OpsNest database.', available: true },
  { key: 'meta_live', label: 'Meta Ads — Live API', desc: 'Purpose-built read-only calls to the live Meta Graph API for verification / fresh data.', available: true },
  { key: 'meta_graph_raw', label: 'Meta Ads — Raw Graph query', desc: 'Guarded generic Graph GET for ad-hoc ad queries (admin power tool, strict allowlist).', available: true },
  { key: 'ig_discovery', label: 'Instagram Discovery', desc: 'Search, enrich and convert IG prospects (performs writes and uses paid API calls).', available: true },
  { key: 'meta_management', label: 'Meta Campaign Management', desc: 'Create / edit / pause campaigns and budgets (writes that affect live ad spend).', available: false },
]

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'email', label: 'Email' },
  { id: 'voip', label: 'VoIP' },
  { id: 'ivr', label: 'IVR Menus' },
  { id: 'lead-assignment', label: 'Lead Assignment' },
  { id: 'scoring', label: 'Scoring' },
  { id: 'callpulse', label: 'CallPulse' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security' },
  { id: 'mcp', label: 'MCP Access' },
  { id: 'mcp-tools', label: 'MCP Tools' },
  { id: 'help', label: 'Help' },
] as const

type TabId = (typeof TABS)[number]['id']

const STRATEGIES = [
  { value: 'round_robin', label: 'Round Robin', description: 'Distribute leads evenly across all available reps' },
  { value: 'performance_based', label: 'Performance Based', description: 'Route leads to reps with highest conversion rates' },
  { value: 'skill_based', label: 'Skill Based', description: 'Match leads to reps based on product expertise' },
  { value: 'load_balanced', label: 'Load Balanced', description: 'Assign to the rep with most available capacity' },
  { value: 'score_based', label: 'Score Based', description: 'Route hot leads to top performers, others via round robin' },
  { value: 'hybrid', label: 'Hybrid', description: 'Combine multiple strategies with weighted scoring' },
]

const PRODUCT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'storepecker', label: 'Storepecker' },
  { value: 'ugcommerce', label: 'UGCommerce' },
]

const LEAD_SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'meta_ads', label: 'Meta Ads' },
  { value: 'organic', label: 'Organic' },
  { value: 'referral', label: 'Referral' },
  { value: 'csv_import', label: 'CSV Import' },
]

// Quality segment options are now loaded dynamically from scoring config
// in LeadAssignmentTab via useScoringSegments()

const SLA_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hour' },
  { value: '240', label: '4 hours' },
  { value: '480', label: '8 hours' },
  { value: '1440', label: '24 hours' },
]

const EMPTY_RULE_FORM = {
  name: '',
  strategy: 'round_robin',
  product_filter: 'all',
  lead_source_filter: 'all',
  quality_segment_filter: 'all',
  sla_minutes: '60',
  priority: '10',
  is_active: true,
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function SettingsPage() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = localStorage.getItem('settings_tab')
    return (saved && TABS.some(t => t.id === saved) ? saved : 'general') as TabId
  })
  const [values, setValues] = useState<AppSettings>({})
  const [dirty, setDirty] = useState(false)
  const [saved, setSaved] = useState(false)
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set())

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getAll,
  })

  useEffect(() => {
    if (data?.settings) {
      setValues(data.settings)
    }
  }, [data])

  const saveMutation = useMutation({
    mutationFn: (settings: AppSettings) => settingsApi.saveBatch(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setDirty(false)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    },
  })

  const updateValue = (key: string, value: string) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'voip_provider' || key === 'email_provider' || key === 'mask_contact_info') {
        settingsApi.save(key, value).then(() => {
          setSaved(true)
          setTimeout(() => setSaved(false), 2000)
        })
      } else {
        setDirty(true)
        setSaved(false)
      }
      return next
    })
  }

  const toggleVisible = (key: string) => {
    setVisibleKeys((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleSave = () => {
    saveMutation.mutate(values)
  }

  if (isLoading) {
    return <LoadingState message="Loading settings..." />
  }

  const showSaveBar = activeTab !== 'lead-assignment' && activeTab !== 'callpulse' && activeTab !== 'ivr' && activeTab !== 'security' && activeTab !== 'scoring'

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        {showSaveBar && (
          <div className="flex items-center gap-3">
            {saved && <span className="text-sm text-green-600">Settings saved!</span>}
            <Button onClick={handleSave} disabled={!dirty} loading={saveMutation.isPending}>
              <Save size={16} /> Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <ScrollableTabs
        tabs={TABS.map(t => ({ key: t.id, label: t.label }))}
        activeKey={activeTab}
        onChange={(key) => { setActiveTab(key as TabId); localStorage.setItem('settings_tab', key) }}
        className="mb-6"
      />

      {/* Tab content — wrapped in error boundary to prevent blank page on crash */}
      <TabErrorBoundary key={activeTab} tab={activeTab}>
        {activeTab === 'general' && (
          <GeneralTab
            values={values}
            updateValue={updateValue}
            visibleKeys={visibleKeys}
            toggleVisible={toggleVisible}
          />
        )}
        {activeTab === 'email' && (
          <EmailTab
            values={values}
            updateValue={updateValue}
            visibleKeys={visibleKeys}
            toggleVisible={toggleVisible}
          />
        )}
        {activeTab === 'voip' && (
          <VoIPTab
            values={values}
            updateValue={updateValue}
            visibleKeys={visibleKeys}
            toggleVisible={toggleVisible}
          />
        )}
        {activeTab === 'ivr' && <IVRMenusTab />}
        {activeTab === 'lead-assignment' && <LeadAssignmentTab />}
        {activeTab === 'scoring' && <ScoringTab />}
        {activeTab === 'callpulse' && <CallPulseTab />}
        {activeTab === 'integrations' && (
          <IntegrationsTab
            values={values}
            updateValue={updateValue}
            visibleKeys={visibleKeys}
            toggleVisible={toggleVisible}
          />
        )}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'security' && <SecurityTab values={values} updateValue={updateValue} />}
        {activeTab === 'mcp' && <MCPAccessTab />}
        {activeTab === 'mcp-tools' && <MCPToolsTab />}
        {activeTab === 'help' && <HelpManualTab />}
      </TabErrorBoundary>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Error boundary — catches render crashes in any tab, shows error instead of blank page
// ---------------------------------------------------------------------------

class TabErrorBoundary extends Component<{ children: ReactNode; tab: string }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error(`[SettingsPage] ${this.props.tab} tab crashed:`, error, info) }
  render() {
    if (this.state.error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">This tab encountered an error</p>
          <p className="text-xs text-red-600 mt-1 font-mono">{this.state.error.message}</p>
          <button onClick={() => this.setState({ error: null })} className="mt-2 text-xs text-red-700 underline">Try again</button>
        </div>
      )
    }
    return this.props.children
  }
}

// ---------------------------------------------------------------------------
// Tab: General
// ---------------------------------------------------------------------------

function GeneralTab({ values, updateValue, visibleKeys, toggleVisible }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <SettingsSection title="LLM Provider" icon={Cpu}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'openai', label: 'OpenAI' },
              { value: 'anthropic', label: 'Anthropic' },
              { value: 'gemini', label: 'Google Gemini' },
            ].map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => updateValue('llm_provider', p.value)}
                className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors text-sm ${
                  (values.llm_provider || 'openai') === p.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <span className="font-medium">{p.label}</span>
              </button>
            ))}
          </div>
        </div>
        <SecretInput
          label="OpenAI API Key"
          settingKey="openai_api_key"
          value={values.openai_api_key || ''}
          onChange={(v) => updateValue('openai_api_key', v)}
          visible={visibleKeys.has('openai_api_key')}
          onToggleVisible={() => toggleVisible('openai_api_key')}
        />
        <SecretInput
          label="Anthropic API Key"
          settingKey="anthropic_api_key"
          value={values.anthropic_api_key || ''}
          onChange={(v) => updateValue('anthropic_api_key', v)}
          visible={visibleKeys.has('anthropic_api_key')}
          onToggleVisible={() => toggleVisible('anthropic_api_key')}
        />
        <SecretInput
          label="Gemini API Key"
          settingKey="gemini_api_key"
          value={values.gemini_api_key || ''}
          onChange={(v) => updateValue('gemini_api_key', v)}
          visible={visibleKeys.has('gemini_api_key')}
          onToggleVisible={() => toggleVisible('gemini_api_key')}
        />
        <Input
          label="Model"
          value={values.llm_model || ''}
          onChange={(e) => updateValue('llm_model', e.target.value)}
          placeholder="e.g. gpt-4o, claude-sonnet-4-20250514"
        />
        <Input
          label="Temperature"
          type="number"
          value={values.llm_temperature || '0.7'}
          onChange={(e) => updateValue('llm_temperature', e.target.value)}
          placeholder="0.0 - 2.0"
        />
      </SettingsSection>

    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Email
// ---------------------------------------------------------------------------

function EmailTab({ values, updateValue, visibleKeys, toggleVisible }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <SettingsSection title="Email Provider" icon={Mail}>
        <Select
          label="Provider"
          options={[
            { value: 'resend', label: 'Resend' },
            { value: 'ses', label: 'AWS SES' },
          ]}
          value={values.email_provider || 'resend'}
          onChange={(e) => updateValue('email_provider', e.target.value)}
        />
        <Input
          label="From Address"
          value={values.email_from_address || ''}
          onChange={(e) => updateValue('email_from_address', e.target.value)}
          placeholder="sales@yourdomain.com"
        />
        <SecretInput
          label="Resend API Key"
          settingKey="resend_api_key"
          value={values.resend_api_key || ''}
          onChange={(v) => updateValue('resend_api_key', v)}
          visible={visibleKeys.has('resend_api_key')}
          onToggleVisible={() => toggleVisible('resend_api_key')}
        />
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-3">AWS SES (fallback)</p>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="SES Region"
              value={values.aws_ses_region || ''}
              onChange={(e) => updateValue('aws_ses_region', e.target.value)}
              placeholder="ap-south-1"
            />
            <SecretInput
              label="SES Access Key"
              settingKey="aws_ses_access_key"
              value={values.aws_ses_access_key || ''}
              onChange={(v) => updateValue('aws_ses_access_key', v)}
              visible={visibleKeys.has('aws_ses_access_key')}
              onToggleVisible={() => toggleVisible('aws_ses_access_key')}
            />
          </div>
          <SecretInput
            label="SES Secret Key"
            settingKey="aws_ses_secret_key"
            value={values.aws_ses_secret_key || ''}
            onChange={(v) => updateValue('aws_ses_secret_key', v)}
            visible={visibleKeys.has('aws_ses_secret_key')}
            onToggleVisible={() => toggleVisible('aws_ses_secret_key')}
          />
        </div>
      </SettingsSection>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: VoIP
// ---------------------------------------------------------------------------

function VoIPTab({ values, updateValue, visibleKeys, toggleVisible }: SettingsTabProps) {
  return (
    <div className="space-y-6">
      <SettingsSection title="VoIP Provider" icon={Phone}>
        {/* Active provider selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Active Provider</label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              { value: 'plivo', label: 'Plivo', configured: !!(values.plivo_auth_id && values.plivo_auth_token) },
              { value: 'twilio', label: 'Twilio', configured: !!(values.twilio_account_sid && values.twilio_auth_token) },
              { value: 'signalwire', label: 'SignalWire', configured: !!(values.signalwire_project_id && values.signalwire_api_token) },
              { value: 'freeswitch', label: 'FreeSWITCH', configured: !!(values.freeswitch_wss_url && values.freeswitch_esl_host) },
            ].map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => updateValue('voip_provider', p.value)}
                className={`relative flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors text-sm ${
                  values.voip_provider === p.value
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                }`}
              >
                <span className="font-medium">{p.label}</span>
                <span className={`flex items-center gap-1 text-[10px] ${p.configured ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${p.configured ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {p.configured ? 'Configured' : 'Not configured'}
                </span>
                {values.voip_provider === p.value && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 text-white rounded-full flex items-center justify-center text-[8px] font-bold">✓</span>
                )}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Configure multiple providers below. Only the active provider is used for calls. Switch anytime without reconfiguring.
          </p>
        </div>

        {/* Plivo */}
        <ProviderSection
          title="Plivo / Zentrunk"
          isActive={values.voip_provider === 'plivo'}
          configured={!!(values.plivo_auth_id && values.plivo_auth_token)}
        >
          <div className="grid grid-cols-2 gap-3">
            <SecretInput
              label="Auth ID"
              value={values.plivo_auth_id || ''}
              onChange={(v) => updateValue('plivo_auth_id', v)}
              visible={visibleKeys.has('plivo_auth_id')}
              onToggleVisible={() => toggleVisible('plivo_auth_id')}
            />
            <SecretInput
              label="Auth Token"
              value={values.plivo_auth_token || ''}
              onChange={(v) => updateValue('plivo_auth_token', v)}
              visible={visibleKeys.has('plivo_auth_token')}
              onToggleVisible={() => toggleVisible('plivo_auth_token')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Phone Number"
              value={values.plivo_phone_number || ''}
              onChange={(e) => updateValue('plivo_phone_number', e.target.value)}
              placeholder="+91..."
            />
            <Input
              label="Application ID"
              value={values.plivo_app_id || ''}
              onChange={(e) => updateValue('plivo_app_id', e.target.value)}
              placeholder="e.g. 63280XXXXXXX"
            />
          </div>
          <p className="text-[11px] text-gray-400 -mt-1">
            Application ID is from Plivo Console &rarr; Voice &rarr; Applications. Required for auto-provisioning VoIP endpoints.
          </p>
          <div className="pt-2 border-t border-gray-100 mt-3">
            <p className="text-xs text-gray-500 mb-2">Browser Calling (WebRTC)</p>
            <div className="grid grid-cols-2 gap-3">
              <SecretInput
                label="Endpoint Username"
                value={values.plivo_endpoint_username || ''}
                onChange={(v) => updateValue('plivo_endpoint_username', v)}
                visible={visibleKeys.has('plivo_endpoint_username')}
                onToggleVisible={() => toggleVisible('plivo_endpoint_username')}
              />
              <SecretInput
                label="Endpoint Password"
                value={values.plivo_endpoint_password || ''}
                onChange={(v) => updateValue('plivo_endpoint_password', v)}
                visible={visibleKeys.has('plivo_endpoint_password')}
                onToggleVisible={() => toggleVisible('plivo_endpoint_password')}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Create an endpoint at console.plivo.com → Voice → Endpoints → Add New Endpoint. Use the endpoint username and password here.
            </p>
          </div>
        </ProviderSection>

        {/* Twilio */}
        <ProviderSection
          title="Twilio"
          isActive={values.voip_provider === 'twilio'}
          configured={!!(values.twilio_account_sid && values.twilio_auth_token)}
        >
          <div className="grid grid-cols-2 gap-3">
            <SecretInput
              label="Account SID"
              value={values.twilio_account_sid || ''}
              onChange={(v) => updateValue('twilio_account_sid', v)}
              visible={visibleKeys.has('twilio_account_sid')}
              onToggleVisible={() => toggleVisible('twilio_account_sid')}
            />
            <SecretInput
              label="Auth Token"
              value={values.twilio_auth_token || ''}
              onChange={(v) => updateValue('twilio_auth_token', v)}
              visible={visibleKeys.has('twilio_auth_token')}
              onToggleVisible={() => toggleVisible('twilio_auth_token')}
            />
          </div>
          <Input
            label="Phone Number"
            value={values.twilio_phone_number || ''}
            onChange={(e) => updateValue('twilio_phone_number', e.target.value)}
            placeholder="+1... or +91..."
          />
          <div className="pt-2 border-t border-gray-100 mt-3">
            <p className="text-xs text-gray-500 mb-2">Browser Calling (WebRTC)</p>
            <SecretInput
              label="TwiML App SID"
              value={values.twilio_twiml_app_sid || ''}
              onChange={(v) => updateValue('twilio_twiml_app_sid', v)}
              visible={visibleKeys.has('twilio_twiml_app_sid')}
              onToggleVisible={() => toggleVisible('twilio_twiml_app_sid')}
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <SecretInput
                label="API Key SID"
                value={values.twilio_api_key_sid || ''}
                onChange={(v) => updateValue('twilio_api_key_sid', v)}
                visible={visibleKeys.has('twilio_api_key_sid')}
                onToggleVisible={() => toggleVisible('twilio_api_key_sid')}
              />
              <SecretInput
                label="API Key Secret"
                value={values.twilio_api_key_secret || ''}
                onChange={(v) => updateValue('twilio_api_key_secret', v)}
                visible={visibleKeys.has('twilio_api_key_secret')}
                onToggleVisible={() => toggleVisible('twilio_api_key_secret')}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              TwiML App: console.twilio.com → Voice → TwiML Apps. API Key: console.twilio.com → Account → API Keys.
            </p>
          </div>
        </ProviderSection>

        {/* SignalWire */}
        <ProviderSection
          title="SignalWire"
          isActive={values.voip_provider === 'signalwire'}
          configured={!!(values.signalwire_project_id && values.signalwire_api_token)}
        >
          <Input
            label="Space URL"
            value={values.signalwire_space_url || ''}
            onChange={(e) => updateValue('signalwire_space_url', e.target.value)}
            placeholder="yourspace.signalwire.com"
          />
          <div className="grid grid-cols-2 gap-3">
            <SecretInput
              label="Project ID"
              value={values.signalwire_project_id || ''}
              onChange={(v) => updateValue('signalwire_project_id', v)}
              visible={visibleKeys.has('signalwire_project_id')}
              onToggleVisible={() => toggleVisible('signalwire_project_id')}
            />
            <SecretInput
              label="API Token"
              value={values.signalwire_api_token || ''}
              onChange={(v) => updateValue('signalwire_api_token', v)}
              visible={visibleKeys.has('signalwire_api_token')}
              onToggleVisible={() => toggleVisible('signalwire_api_token')}
            />
          </div>
          <Input
            label="Phone Number"
            value={values.signalwire_phone_number || ''}
            onChange={(e) => updateValue('signalwire_phone_number', e.target.value)}
            placeholder="+1... or +91..."
          />
          <p className="text-[11px] text-gray-400 mt-2">
            SignalWire uses Twilio-compatible APIs. Get credentials from your SignalWire Space dashboard.
          </p>
        </ProviderSection>

        {/* FreeSWITCH */}
        <ProviderSection
          title="FreeSWITCH (Self-Hosted)"
          isActive={values.voip_provider === 'freeswitch'}
          configured={!!(values.freeswitch_wss_url && values.freeswitch_esl_host)}
        >
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="ESL Host"
              value={values.freeswitch_esl_host || ''}
              onChange={(e) => updateValue('freeswitch_esl_host', e.target.value)}
              placeholder="127.0.0.1 or internal IP"
            />
            <Input
              label="ESL Port"
              value={values.freeswitch_esl_port || '8021'}
              onChange={(e) => updateValue('freeswitch_esl_port', e.target.value)}
              placeholder="8021"
            />
          </div>
          <SecretInput
            label="ESL Password"
            value={values.freeswitch_esl_password || ''}
            onChange={(v) => updateValue('freeswitch_esl_password', v)}
            visible={visibleKeys.has('freeswitch_esl_password')}
            onToggleVisible={() => toggleVisible('freeswitch_esl_password')}
          />
          <Input
            label="WebSocket URL (WSS)"
            value={values.freeswitch_wss_url || ''}
            onChange={(e) => updateValue('freeswitch_wss_url', e.target.value)}
            placeholder="wss://voip.yourdomain.com:8082"
          />
          <Input
            label="SIP Gateway Name"
            value={values.freeswitch_sip_gateway || ''}
            onChange={(e) => updateValue('freeswitch_sip_gateway', e.target.value)}
            placeholder="india-trunk"
          />
          <Input
            label="Caller ID Number"
            value={values.freeswitch_caller_id || ''}
            onChange={(e) => updateValue('freeswitch_caller_id', e.target.value)}
            placeholder="+91..."
          />
          <p className="text-[11px] text-gray-400 mt-2">
            Self-hosted FreeSWITCH with SIP trunk. Browser connects via Verto/WSS. See docs/exec-plans/active/freeswitch-implementation.md for setup guide.
          </p>
        </ProviderSection>
      </SettingsSection>

      {/* Data Sync / Backfill */}
      <BackfillSection />

      {/* IVR note */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-700">
        Inbound call routing (IVR) is now configured in the <strong>IVR Menus</strong> tab. You can route calls to teams or directly to individual users.
      </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Tab: IVR Menus
// ---------------------------------------------------------------------------

const FALLBACK_ACTIONS = [
  { value: 'voicemail', label: 'Go to voicemail' },
  { value: 'transfer_operator', label: 'Transfer to operator' },
  { value: 'hangup', label: 'Hang up' },
]

const OPTION_ACTIONS = [
  { value: 'route_to_team', label: 'Route to team' },
  { value: 'route_to_user', label: 'Route to user' },
  { value: 'submenu', label: 'Go to sub-menu' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'transfer', label: 'Transfer to number' },
]

const DIGIT_CHOICES = ['1','2','3','4','5','6','7','8','9','0','*','#']

const EMPTY_MENU_FORM = {
  name: '',
  phone_number: '',
  welcome_message: 'Welcome! Please listen to the following options.',
  timeout_seconds: '5',
  max_retries: '3',
  fallback_action: 'voicemail',
  fallback_team_id: '',
  is_active: true,
}

const EMPTY_OPTION_FORM = {
  digit: '1',
  label: '',
  action: 'route_to_team',
  target_team_id: '',
  target_user_id: '',
  transfer_number: '',
  is_active: true,
}

function IVRMenusTab() {
  const queryClient = useQueryClient()
  const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null)
  const [menuModalOpen, setMenuModalOpen] = useState(false)
  const [editingMenu, setEditingMenu] = useState<IVRMenu | null>(null)
  const [menuForm, setMenuForm] = useState(EMPTY_MENU_FORM)
  const [optionModalOpen, setOptionModalOpen] = useState(false)
  const [editingOption, setEditingOption] = useState<IVROption | null>(null)
  const [optionForm, setOptionForm] = useState(EMPTY_OPTION_FORM)
  const [optionMenuId, setOptionMenuId] = useState<number | null>(null)

  // Queries
  const { data: menusData, isLoading } = useQuery({
    queryKey: ['ivr-menus'],
    queryFn: ivrApi.listMenus,
  })

  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
  })

  const menus = (menusData?.menus ?? []) as IVRMenu[]
  const teams = (teamsData?.teams ?? []) as Team[]

  const { data: usersData } = useQuery({
    queryKey: ['users-for-ivr'],
    queryFn: () => usersApi.list(),
  })
  const users: User[] = usersData?.users ?? []

  // Seed default IVR menu
  const seedDefaultsMutation = useMutation({
    mutationFn: () => ivrApi.seedDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
    },
  })

  // Menu mutations
  const createMenuMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => ivrApi.createMenu(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
      closeMenuModal()
    },
  })

  const updateMenuMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      ivrApi.updateMenu(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
      closeMenuModal()
    },
  })

  const deleteMenuMutation = useMutation({
    mutationFn: (id: number) => ivrApi.deleteMenu(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
    },
  })

  // Option mutations
  const addOptionMutation = useMutation({
    mutationFn: ({ menuId, data }: { menuId: number; data: Record<string, unknown> }) =>
      ivrApi.addOption(menuId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
      closeOptionModal()
    },
  })

  const updateOptionMutation = useMutation({
    mutationFn: ({ menuId, digit, data }: { menuId: number; digit: string; data: Record<string, unknown> }) =>
      ivrApi.updateOption(menuId, digit, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
      closeOptionModal()
    },
  })

  const deleteOptionMutation = useMutation({
    mutationFn: ({ menuId, digit }: { menuId: number; digit: string }) =>
      ivrApi.deleteOption(menuId, digit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
    },
  })

  const uploadGreetingMutation = useMutation({
    mutationFn: ({ menuId, file }: { menuId: number; file: File }) =>
      ivrApi.uploadGreeting(menuId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
    },
    onError: (err: Error) => alert(err.message),
  })

  const deleteGreetingMutation = useMutation({
    mutationFn: (menuId: number) => ivrApi.deleteGreeting(menuId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ivr-menus'] })
    },
  })

  // Menu modal helpers
  const closeMenuModal = () => {
    setMenuModalOpen(false)
    setEditingMenu(null)
    setMenuForm(EMPTY_MENU_FORM)
  }

  const openCreateMenu = () => {
    setEditingMenu(null)
    setMenuForm(EMPTY_MENU_FORM)
    setMenuModalOpen(true)
  }

  const openEditMenu = (menu: IVRMenu) => {
    setEditingMenu(menu)
    setMenuForm({
      name: menu.name,
      phone_number: menu.phone_number || '',
      welcome_message: menu.welcome_message,
      timeout_seconds: String(menu.timeout_seconds),
      max_retries: String(menu.max_retries),
      fallback_action: menu.fallback_action,
      fallback_team_id: menu.fallback_team_id ? String(menu.fallback_team_id) : '',
      is_active: menu.is_active,
    })
    setMenuModalOpen(true)
  }

  const handleMenuSubmit = () => {
    const payload: Record<string, unknown> = {
      name: menuForm.name,
      phone_number: menuForm.phone_number || null,
      welcome_message: menuForm.welcome_message,
      timeout_seconds: parseInt(menuForm.timeout_seconds, 10),
      max_retries: parseInt(menuForm.max_retries, 10),
      fallback_action: menuForm.fallback_action,
      fallback_team_id: menuForm.fallback_team_id ? parseInt(menuForm.fallback_team_id, 10) : null,
      is_active: menuForm.is_active,
    }

    if (editingMenu) {
      updateMenuMutation.mutate({ id: editingMenu.id, data: payload })
    } else {
      createMenuMutation.mutate(payload)
    }
  }

  // Option modal helpers
  const closeOptionModal = () => {
    setOptionModalOpen(false)
    setEditingOption(null)
    setOptionForm(EMPTY_OPTION_FORM)
    setOptionMenuId(null)
  }

  const openAddOption = (menuId: number) => {
    const menu = menus.find(m => m.id === menuId)
    const usedDigits = new Set(menu?.options.map(o => o.digit) ?? [])
    const nextDigit = DIGIT_CHOICES.find(d => !usedDigits.has(d)) || '1'
    setEditingOption(null)
    setOptionForm({ ...EMPTY_OPTION_FORM, digit: nextDigit })
    setOptionMenuId(menuId)
    setOptionModalOpen(true)
  }

  const openEditOption = (menuId: number, option: IVROption) => {
    setEditingOption(option)
    setOptionForm({
      digit: option.digit,
      label: option.label,
      action: option.action,
      target_team_id: option.target_team_id ? String(option.target_team_id) : '',
      target_user_id: option.target_user_id ? String(option.target_user_id) : '',
      transfer_number: option.transfer_number || '',
      is_active: option.is_active,
    })
    setOptionMenuId(menuId)
    setOptionModalOpen(true)
  }

  const handleOptionSubmit = () => {
    if (!optionMenuId) return
    const payload: Record<string, unknown> = {
      digit: optionForm.digit,
      label: optionForm.label,
      action: optionForm.action,
      target_team_id: optionForm.target_team_id ? parseInt(optionForm.target_team_id, 10) : null,
      target_user_id: optionForm.target_user_id ? parseInt(optionForm.target_user_id, 10) : null,
      transfer_number: optionForm.transfer_number || null,
      is_active: optionForm.is_active,
    }

    if (editingOption) {
      updateOptionMutation.mutate({ menuId: optionMenuId, digit: editingOption.digit, data: payload })
    } else {
      addOptionMutation.mutate({ menuId: optionMenuId, data: payload })
    }
  }

  const teamName = (id: number | null) => {
    if (!id) return '--'
    return teams.find(t => t.id === id)?.name ?? `Team #${id}`
  }

  const isMenuSaving = createMenuMutation.isPending || updateMenuMutation.isPending
  const isOptionSaving = addOptionMutation.isPending || updateOptionMutation.isPending

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading IVR menus...</div>
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
    <div className="space-y-6">
      {/* Menus list */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Phone size={18} className="text-gray-400" /> IVR Menus
          </h2>
          <Button size="sm" onClick={openCreateMenu}>
            <Plus size={14} /> Create Menu
          </Button>
        </div>

        {menus.length === 0 ? (
          <EmptyState
            icon={Phone}
            title="No IVR menus configured"
            description="Create a menu or load a default IVR with Press 1 for Sales, Press 2 for Support. If Sales/Support teams exist, options are auto-linked. Otherwise, create teams first from the Teams page."
            action={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => seedDefaultsMutation.mutate()} disabled={seedDefaultsMutation.isPending}>
                  {seedDefaultsMutation.isPending ? 'Loading...' : 'Load Defaults'}
                </Button>
                <Button size="sm" onClick={openCreateMenu}>
                  <Plus size={14} /> Create Menu
                </Button>
              </div>
            }
          />
        ) : (
          <div className="space-y-3">
            {menus.map((menu) => {
              const isExpanded = expandedMenuId === menu.id
              return (
                <div key={menu.id} className={`border rounded-lg overflow-hidden ${menu.is_active ? 'border-gray-200' : 'border-gray-100 opacity-70'}`}>
                  {/* Menu summary row */}
                  <button
                    type="button"
                    onClick={() => setExpandedMenuId(isExpanded ? null : menu.id)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-indigo-500 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-gray-900">{menu.name}</span>
                        {menu.phone_number && (
                          <span className="ml-2 text-gray-500">{menu.phone_number}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="default">{menu.options.length} option{menu.options.length !== 1 ? 's' : ''}</Badge>
                      <Badge variant={menu.is_active ? 'success' : 'default'}>
                        {menu.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 px-4 py-4 bg-gray-50/50">
                      {/* Menu info */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><span className="font-medium text-gray-700">Welcome message:</span> {menu.welcome_message}</p>
                          <p className="flex items-center gap-2">
                            <span className="font-medium text-gray-700">Greeting audio:</span>
                            {menu.has_welcome_audio ? (
                              <>
                                <Badge variant="success">Custom recording</Badge>
                                <button
                                  type="button"
                                  className="text-red-500 hover:underline"
                                  onClick={() => {
                                    if (confirm('Remove the recorded greeting? The menu will revert to the spoken welcome message.')) {
                                      deleteGreetingMutation.mutate(menu.id)
                                    }
                                  }}
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <span className="text-gray-400">Text-to-speech</span>
                            )}
                            <label className="text-indigo-600 hover:underline cursor-pointer">
                              {uploadGreetingMutation.isPending ? 'Uploading…' : (menu.has_welcome_audio ? 'Replace' : 'Upload MP3/WAV')}
                              <input
                                type="file"
                                accept=".mp3,.wav,audio/mpeg,audio/wav"
                                className="hidden"
                                disabled={uploadGreetingMutation.isPending}
                                onChange={(e) => {
                                  const file = e.target.files?.[0]
                                  if (file) uploadGreetingMutation.mutate({ menuId: menu.id, file })
                                  e.target.value = ''
                                }}
                              />
                            </label>
                          </p>
                          <p><span className="font-medium text-gray-700">Timeout:</span> {menu.timeout_seconds}s &middot; <span className="font-medium text-gray-700">Max retries:</span> {menu.max_retries}</p>
                          <p><span className="font-medium text-gray-700">Fallback:</span> {FALLBACK_ACTIONS.find(a => a.value === menu.fallback_action)?.label ?? menu.fallback_action}
                            {menu.fallback_team_id && <> &middot; {teamName(menu.fallback_team_id)}</>}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEditMenu(menu)}>
                            <Pencil size={14} /> Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm(`Delete menu "${menu.name}"? This cannot be undone.`)) {
                                deleteMenuMutation.mutate(menu.id)
                              }
                            }}
                          >
                            <Trash2 size={14} className="text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {/* Options table */}
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Keypad Options</span>
                          <Button size="sm" variant="ghost" onClick={() => openAddOption(menu.id)}>
                            <Plus size={14} /> Add Option
                          </Button>
                        </div>
                        {menu.options.length === 0 ? (
                          <p className="text-sm text-gray-400 text-center py-6">
                            No options yet. Add a keypad option to route callers.
                          </p>
                        ) : (
                          <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-100">
                                <th className="text-left py-2 px-3 font-medium text-gray-500 w-16">Digit</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-500">Label</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-500">Action</th>
                                <th className="text-left py-2 px-3 font-medium text-gray-500">Target</th>
                                <th className="text-right py-2 px-3 font-medium text-gray-500 w-24">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {menu.options
                                .slice()
                                .sort((a, b) => {
                                  const order = DIGIT_CHOICES
                                  return order.indexOf(a.digit) - order.indexOf(b.digit)
                                })
                                .map((opt) => (
                                <tr key={opt.digit} className="border-b border-gray-50 hover:bg-gray-50/50">
                                  <td className="py-2 px-3">
                                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-indigo-100 text-indigo-700 font-bold text-sm">
                                      {opt.digit}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 font-medium text-gray-900">{opt.label}</td>
                                  <td className="py-2 px-3">
                                    <Badge variant="purple">
                                      {OPTION_ACTIONS.find(a => a.value === opt.action)?.label ?? opt.action}
                                    </Badge>
                                  </td>
                                  <td className="py-2 px-3 text-gray-600">
                                    {opt.action === 'route_to_team' && teamName(opt.target_team_id)}
                                    {opt.action === 'route_to_user' && (opt.target_user_name || `User #${opt.target_user_id}`)}
                                    {opt.action === 'transfer' && (opt.transfer_number || '--')}
                                    {opt.action === 'submenu' && `Menu #${opt.target_menu_id ?? '--'}`}
                                    {opt.action === 'voicemail' && 'Voicemail'}
                                  </td>
                                  <td className="py-2 px-3 text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => openEditOption(menu.id, opt)}>
                                        <Pencil size={14} />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          if (confirm(`Remove digit ${opt.digit} (${opt.label})?`)) {
                                            deleteOptionMutation.mutate({ menuId: menu.id, digit: opt.digit })
                                          }
                                        }}
                                      >
                                        <Trash2 size={14} className="text-red-500" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Menu Modal */}
      <Modal
        open={menuModalOpen}
        onClose={closeMenuModal}
        title={editingMenu ? 'Edit IVR Menu' : 'Create IVR Menu'}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Menu Name"
            value={menuForm.name}
            onChange={(e) => setMenuForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Main Menu"
          />
          <Input
            label="Phone Number"
            value={menuForm.phone_number}
            onChange={(e) => setMenuForm(f => ({ ...f, phone_number: e.target.value }))}
            placeholder="e.g. +919876543210 (which Plivo number this menu serves)"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Welcome Message</label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              value={menuForm.welcome_message}
              onChange={(e) => setMenuForm(f => ({ ...f, welcome_message: e.target.value }))}
              placeholder="The message callers hear when they reach this menu"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Timeout (seconds)"
              type="number"
              value={menuForm.timeout_seconds}
              onChange={(e) => setMenuForm(f => ({ ...f, timeout_seconds: e.target.value }))}
              placeholder="5"
            />
            <Input
              label="Max Retries"
              type="number"
              value={menuForm.max_retries}
              onChange={(e) => setMenuForm(f => ({ ...f, max_retries: e.target.value }))}
              placeholder="3"
            />
          </div>
          <Select
            label="If caller doesn't press anything"
            options={FALLBACK_ACTIONS}
            value={menuForm.fallback_action}
            onChange={(e) => setMenuForm(f => ({ ...f, fallback_action: e.target.value }))}
          />
          {(menuForm.fallback_action === 'transfer_operator') && (
            <Select
              label="Fallback Team"
              options={[
                { value: '', label: '-- Select a team --' },
                ...teams.map(t => ({ value: String(t.id), label: t.name })),
              ]}
              value={menuForm.fallback_team_id}
              onChange={(e) => setMenuForm(f => ({ ...f, fallback_team_id: e.target.value }))}
            />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={menuForm.is_active}
              onChange={(e) => setMenuForm(f => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-gray-700">Menu is active</span>
          </label>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeMenuModal}>Cancel</Button>
            <Button onClick={handleMenuSubmit} loading={isMenuSaving} disabled={!menuForm.name.trim()}>
              <Save size={14} /> {editingMenu ? 'Update Menu' : 'Create Menu'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create / Edit Option Modal */}
      <Modal
        open={optionModalOpen}
        onClose={closeOptionModal}
        title={editingOption ? 'Edit Keypad Option' : 'Add Keypad Option'}
      >
        <div className="space-y-4">
          <Select
            label="Digit"
            options={DIGIT_CHOICES.map(d => ({ value: d, label: `Press ${d}` }))}
            value={optionForm.digit}
            onChange={(e) => setOptionForm(f => ({ ...f, digit: e.target.value }))}
          />
          <Input
            label="Label (what callers hear)"
            value={optionForm.label}
            onChange={(e) => setOptionForm(f => ({ ...f, label: e.target.value }))}
            placeholder="e.g. Sales, Support, Billing"
          />
          <Select
            label="What happens when they press this"
            options={OPTION_ACTIONS}
            value={optionForm.action}
            onChange={(e) => setOptionForm(f => ({ ...f, action: e.target.value }))}
          />
          {optionForm.action === 'route_to_team' && (
            <Select
              label="Route to Team"
              options={[
                { value: '', label: '-- Select a team --' },
                ...teams.map(t => ({ value: String(t.id), label: t.name })),
              ]}
              value={optionForm.target_team_id}
              onChange={(e) => setOptionForm(f => ({ ...f, target_team_id: e.target.value }))}
            />
          )}
          {optionForm.action === 'route_to_user' && (
            <Select
              label="Route to User"
              options={[
                { value: '', label: '-- Select a user --' },
                ...users.map(u => ({ value: String(u.id), label: u.full_name || u.username })),
              ]}
              value={optionForm.target_user_id}
              onChange={(e) => setOptionForm(f => ({ ...f, target_user_id: e.target.value }))}
            />
          )}
          {optionForm.action === 'transfer' && (
            <Input
              label="Transfer to Number"
              value={optionForm.transfer_number}
              onChange={(e) => setOptionForm(f => ({ ...f, transfer_number: e.target.value }))}
              placeholder="e.g. +919876543210"
            />
          )}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={optionForm.is_active}
              onChange={(e) => setOptionForm(f => ({ ...f, is_active: e.target.checked }))}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-gray-700">Option is active</span>
          </label>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeOptionModal}>Cancel</Button>
            <Button onClick={handleOptionSubmit} loading={isOptionSaving} disabled={!optionForm.label.trim()}>
              <Save size={14} /> {editingOption ? 'Update Option' : 'Add Option'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
    <div className="hidden xl:block">
      <div className="sticky top-6"><IVRGuide /></div>
    </div>
    </div>
  )
}

function IVRGuide() {
  const [open, setOpen] = useState<string | null>('what-is-ivr')
  const sections = [
    { id: 'what-is-ivr', title: 'What is IVR?', content: 'IVR (Interactive Voice Response) menus greet inbound callers with options like "Press 1 for Sales, Press 2 for Support". Each option routes the call to a team or submenu.' },
    { id: 'setup', title: 'Setting Up Menus', items: ['Create a main menu first — this is what callers hear on inbound calls', 'Add options (1-9) — each routes to a team or another submenu', 'Use "Load Defaults" to create a standard Sales/Support menu', 'Link options to teams — calls route to available agents in that team'] },
    { id: 'vip', title: 'VIP Routing', items: ['HOT leads skip the IVR entirely and route directly to their assigned rep', 'This is automatic based on lead score — no extra configuration needed'] },
    { id: 'tips', title: 'Tips', items: ['Keep menus simple — 2-3 options max. Long menus frustrate callers', 'Always have a fallback if no agent is available', 'Test your IVR by calling your own number after setup'] },
  ]
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">IVR Guide</h3>
      <div className="space-y-1">
        {sections.map(s => (
          <div key={s.id} className="border-b border-gray-50 last:border-0">
            <button onClick={() => setOpen(open === s.id ? null : s.id)} className="w-full flex items-center justify-between py-2 text-left">
              <span className="text-xs font-medium text-gray-700">{s.title}</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${open === s.id ? 'rotate-180' : ''}`} />
            </button>
            {open === s.id && (
              <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                {s.content && <p>{s.content}</p>}
                {s.items && <ul className="list-disc list-outside ml-3.5 space-y-1.5">{s.items.map((item, i) => <li key={i}>{item}</li>)}</ul>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Lead Assignment
// ---------------------------------------------------------------------------

function LeadAssignmentTab() {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<AssignmentRule | null>(null)
  const [form, setForm] = useState(EMPTY_RULE_FORM)

  // Dynamic quality segments from scoring config
  const scoringSegments = useScoringSegments()
  const segmentOptions = [
    { value: 'all', label: 'All' },
    ...(scoringSegments.length > 0
      ? scoringSegments.map((s: any) => ({ value: s.segment, label: s.segment }))
      : [{ value: 'HOT', label: 'HOT' }, { value: 'WARM', label: 'WARM' }, { value: 'COLD', label: 'COLD' }]),
  ]

  const { data: rulesData, isLoading } = useQuery({
    queryKey: ['assignment-rules'],
    queryFn: assignmentApi.listRules,
  })

  const rules = (rulesData?.data ?? []) as AssignmentRule[]
  const activeRule = rules.find((r) => r.is_active)

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => assignmentApi.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment-rules'] })
      closeModal()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      assignmentApi.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment-rules'] })
      closeModal()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => assignmentApi.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment-rules'] })
    },
  })

  const [seedSelectorOpen, setSeedSelectorOpen] = useState(false)
  const [selectedSeeds, setSelectedSeeds] = useState<Set<string>>(new Set())

  const { data: defaultsData } = useQuery({
    queryKey: ['assignment-defaults'],
    queryFn: () => assignmentApi.getDefaults(),
    enabled: seedSelectorOpen,
  })

  const seedMutation = useMutation({
    mutationFn: (names?: string[]) => assignmentApi.seedDefaults(names),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assignment-rules'] })
      setSeedSelectorOpen(false)
    },
  })

  const closeModal = () => {
    setModalOpen(false)
    setEditingRule(null)
    setForm(EMPTY_RULE_FORM)
  }

  const openCreate = () => {
    setEditingRule(null)
    setForm(EMPTY_RULE_FORM)
    setModalOpen(true)
  }

  const openEdit = (rule: AssignmentRule) => {
    setEditingRule(rule)
    setForm({
      name: rule.name || '',
      strategy: rule.strategy || 'round_robin',
      product_filter: rule.product || rule.product_filter || 'all',
      lead_source_filter: rule.lead_source_filter || 'all',
      quality_segment_filter: rule.quality_segment_filter || 'all',
      sla_minutes: String(rule.sla_minutes ?? 60),
      priority: String(rule.priority ?? 10),
      is_active: rule.is_active ?? true,
    })
    setModalOpen(true)
  }

  const handleSubmit = () => {
    const payload: Record<string, unknown> = {
      name: form.name,
      strategy: form.strategy,
      product: form.product_filter === 'all' ? null : form.product_filter,
      lead_source_filter: form.lead_source_filter === 'all' ? null : form.lead_source_filter,
      quality_segment_filter: form.quality_segment_filter === 'all' ? null : form.quality_segment_filter,
      sla_minutes: parseInt(form.sla_minutes, 10),
      priority: parseInt(form.priority, 10),
      is_active: form.is_active,
    }

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  const formatSla = (minutes: number | null) => {
    if (!minutes) return '--'
    if (minutes < 60) return `${minutes} min`
    if (minutes === 60) return '1 hour'
    if (minutes < 1440) return `${minutes / 60} hours`
    return '24 hours'
  }

  const strategyLabel = (value: string) =>
    STRATEGIES.find((s) => s.value === value)?.label ?? value

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading assignment rules...</div>
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
    <div className="space-y-6">
      {/* Active Strategy Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Shield size={18} className="text-gray-400" /> Active Strategy
        </h2>
        {activeRule ? (
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{activeRule.name}</p>
              <p className="text-sm text-gray-500 mt-1">
                {STRATEGIES.find((s) => s.value === activeRule.strategy)?.description ??
                  activeRule.strategy}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Badge variant="success">{strategyLabel(activeRule.strategy)}</Badge>
                {activeRule.product_filter && (
                  <Badge variant="info">{activeRule.product_filter}</Badge>
                )}
                <Badge variant="default">SLA: {formatSla(activeRule.sla_minutes)}</Badge>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => openEdit(activeRule)}>
              <Pencil size={14} /> Edit
            </Button>
          </div>
        ) : (
          <EmptyState
            icon={Users}
            title="No active assignment rule"
            description="Create your first rule to start automatically assigning leads to your sales team."
            action={
              <Button size="sm" onClick={openCreate}>
                <Plus size={14} /> Create your first rule
              </Button>
            }
          />
        )}
      </div>

      {/* Rules List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Users size={18} className="text-gray-400" /> Assignment Rules
          </h2>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setSeedSelectorOpen(!seedSelectorOpen)}>
              Load Defaults
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} /> New Rule
            </Button>
          </div>
        </div>

        {seedSelectorOpen && (
          <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm font-medium text-indigo-800 mb-3">Select rules to create:</p>
            <div className="space-y-2 mb-3">
              {(defaultsData?.data || []).map((d: any) => (
                <label key={d.name} className={`flex items-center gap-2 text-sm ${d.exists ? 'text-gray-400' : 'text-gray-700'}`}>
                  <input
                    type="checkbox"
                    disabled={d.exists}
                    checked={d.exists || selectedSeeds.has(d.name)}
                    onChange={(e) => {
                      const next = new Set(selectedSeeds)
                      e.target.checked ? next.add(d.name) : next.delete(d.name)
                      setSelectedSeeds(next)
                    }}
                    className="rounded"
                  />
                  <span>{d.name}</span>
                  <span className="text-xs text-gray-400">({d.strategy})</span>
                  {d.exists && <span className="text-[10px] text-green-600 font-medium">Already exists</span>}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => seedMutation.mutate(selectedSeeds.size > 0 ? Array.from(selectedSeeds) : undefined)}
                loading={seedMutation.isPending}
                disabled={selectedSeeds.size === 0 && !(defaultsData?.data || []).some((d: any) => !d.exists)}
              >
                {selectedSeeds.size > 0 ? `Create ${selectedSeeds.size} Rule${selectedSeeds.size > 1 ? 's' : ''}` : 'Create All'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setSeedSelectorOpen(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {seedMutation.isSuccess && !seedSelectorOpen && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 text-sm rounded-lg">
            {(seedMutation.data as any)?.message || 'Default rules loaded.'}
          </div>
        )}

        {rules.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            No assignment rules configured yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Name</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Strategy</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Filters</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">SLA</th>
                  <th className="text-center py-2 px-2 font-medium text-gray-500">Priority</th>
                  <th className="text-center py-2 px-2 font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-2 font-medium text-gray-900">{rule.name}</td>
                    <td className="py-2.5 px-2">
                      <Badge variant="purple">{strategyLabel(rule.strategy)}</Badge>
                    </td>
                    <td className="py-2.5 px-2">
                      <div className="flex flex-wrap gap-1">
                        {(rule.product || rule.product_filter) && (
                          <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{rule.product || rule.product_filter}</span>
                        )}
                        {rule.lead_source_filter && (
                          <span className="text-[10px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded">{rule.lead_source_filter}</span>
                        )}
                        {rule.quality_segment_filter && (
                          <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded">{rule.quality_segment_filter}</span>
                        )}
                        {!(rule.product || rule.product_filter) && !rule.lead_source_filter && !rule.quality_segment_filter && (
                          <span className="text-xs text-gray-400">All</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 px-2 text-gray-600">
                      {formatSla(rule.sla_minutes)}
                    </td>
                    <td className="py-2.5 px-2 text-center text-gray-600">{rule.priority}</td>
                    <td className="py-2.5 px-2 text-center">
                      <Badge variant={rule.is_active ? 'success' : 'default'}>
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(rule)}>
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm(`Delete rule "${rule.name}"?`)) {
                              deleteMutation.mutate(rule.id)
                            }
                          }}
                        >
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingRule ? 'Edit Assignment Rule' : 'Create Assignment Rule'}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Rule Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Default Round Robin"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Strategy</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={form.strategy}
              onChange={(e) => setForm((f) => ({ ...f, strategy: e.target.value }))}
            >
              {STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {STRATEGIES.find((s) => s.value === form.strategy)?.description}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Product Filter"
              options={PRODUCT_OPTIONS}
              value={form.product_filter}
              onChange={(e) => setForm((f) => ({ ...f, product_filter: e.target.value }))}
            />
            <Select
              label="Lead Source Filter"
              options={LEAD_SOURCE_OPTIONS}
              value={form.lead_source_filter}
              onChange={(e) => setForm((f) => ({ ...f, lead_source_filter: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Quality Segment Filter"
              options={segmentOptions}
              value={form.quality_segment_filter}
              onChange={(e) =>
                setForm((f) => ({ ...f, quality_segment_filter: e.target.value }))
              }
            />
            <Select
              label="SLA Time"
              options={SLA_OPTIONS}
              value={form.sla_minutes}
              onChange={(e) => setForm((f) => ({ ...f, sla_minutes: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Priority"
              type="number"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              placeholder="Lower number = higher priority"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Active</label>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors mt-1 ${
                  form.is_active ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form.is_active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <p className="text-xs text-gray-500 mt-1">
                {form.is_active ? 'This rule is active' : 'This rule is inactive'}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!form.name.trim()} loading={isSaving}>
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
    <div className="hidden xl:block">
      <div className="sticky top-6"><LeadAssignmentGuide /></div>
    </div>
    </div>
  )
}

function LeadAssignmentGuide() {
  const [open, setOpen] = useState<string | null>('how-it-works')
  const sections = [
    { id: 'how-it-works', title: 'How Assignment Works', content: 'Assignment rules determine which sales rep gets a new lead. Rules are matched by priority — the first matching rule assigns the lead. Each rule has a strategy (how to pick the rep) and filters (which leads it applies to).' },
    { id: 'strategies', title: 'Strategies', items: ['Round Robin — distributes leads evenly across reps in rotation', 'Performance-Based — assigns to the rep with the highest conversion rate', 'Skill-Based — matches lead attributes to rep skills (language, product expertise)', 'Load Balanced — assigns to the rep with the fewest active leads', 'Score-Based — assigns HOT leads to top performers', 'Hybrid — combines multiple strategies with weighted scoring'] },
    { id: 'setup', title: 'Setting Up Rules', items: ['Create rules from most specific to most general — specific rules get higher priority', 'Use filters: product, lead source, quality segment to target the right leads', 'Set SLA minutes — leads not contacted within SLA trigger alerts', 'The catch-all rule (lowest priority, no filters) ensures no lead is unassigned'] },
    { id: 'tips', title: 'Tips', items: ['Start with Round Robin for small teams, move to Performance-Based as you get data', 'Set tight SLAs for HOT leads (30 min) and relaxed for COLD (24 hours)', 'Review assignment distribution weekly — if one rep is overloaded, adjust', 'Use "Seed Defaults" to create recommended starter rules'] },
  ]
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Assignment Guide</h3>
      <div className="space-y-1">
        {sections.map(s => (
          <div key={s.id} className="border-b border-gray-50 last:border-0">
            <button onClick={() => setOpen(open === s.id ? null : s.id)} className="w-full flex items-center justify-between py-2 text-left">
              <span className="text-xs font-medium text-gray-700">{s.title}</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${open === s.id ? 'rotate-180' : ''}`} />
            </button>
            {open === s.id && (
              <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                {s.content && <p>{s.content}</p>}
                {s.items && <ul className="list-disc list-outside ml-3.5 space-y-1.5">{s.items.map((item, i) => <li key={i}>{item}</li>)}</ul>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Integrations
// ---------------------------------------------------------------------------

function IntegrationsTab({ values, updateValue, visibleKeys, toggleVisible }: SettingsTabProps) {
  const [capiStats, setCapiStats] = useState<Record<string, unknown> | null>(null)
  const [capiLoading, setCapiLoading] = useState(false)

  const loadCapiStats = async () => {
    setCapiLoading(true)
    try {
      const conn = await metaApi.getConnection()
      if (conn?.data) {
        setCapiStats(conn.data)
      }
    } catch {
      // Ignore — connection may not exist yet
    } finally {
      setCapiLoading(false)
    }
  }

  useEffect(() => {
    loadCapiStats()
  }, [])

  return (
    <div className="space-y-6">
      {/* Webhooks & Storage */}
      <SettingsSection title="Webhooks & Storage" icon={Globe}>
        <Input
          label="Webhook Base URL"
          value={values.voip_webhook_base_url || ''}
          onChange={(e) => updateValue('voip_webhook_base_url', e.target.value)}
          placeholder="https://your-domain.com (or ngrok URL for dev)"
        />
        <Select
          label="Recording Storage"
          options={[
            { value: 'local', label: 'Local (data/recordings/)' },
            { value: 's3', label: 'AWS S3' },
          ]}
          value={values.voip_recording_storage || 'local'}
          onChange={(e) => updateValue('voip_recording_storage', e.target.value)}
        />
        {values.voip_recording_storage === 's3' && (
          <p className="text-xs text-gray-500">
            Recordings will be stored in S3 using credentials from <strong>Settings &rarr; Integrations &rarr; Data Storage (S3)</strong>.
            Path: <code className="bg-gray-100 px-1 rounded">{'{s3_path_prefix}'}/recordings/</code>
          </p>
        )}
      </SettingsSection>

      {/* Meta Conversions API */}
      <SettingsSection title="Meta Conversions API (CAPI)" icon={Globe}>
        <Input
          label="Pixel ID"
          value={values.meta_pixel_id || ''}
          onChange={(e) => updateValue('meta_pixel_id', e.target.value)}
          placeholder="Enter your Meta Pixel ID"
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">CAPI Enabled</label>
          <button
            type="button"
            onClick={() =>
              updateValue('meta_capi_enabled', values.meta_capi_enabled === 'true' ? 'false' : 'true')
            }
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              values.meta_capi_enabled === 'true' ? 'bg-indigo-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                values.meta_capi_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <p className="text-xs text-gray-500 mt-1">
            {values.meta_capi_enabled === 'true'
              ? 'Conversions API events are being sent to Meta'
              : 'Enable to send server-side events to Meta for improved attribution'}
          </p>
        </div>

        {/* Connection stats */}
        {capiLoading ? (
          <p className="text-xs text-gray-400">Loading connection info...</p>
        ) : capiStats ? (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-medium text-gray-500 mb-2">Connection Status</p>
            <div className="grid grid-cols-2 gap-3">
              {(capiStats as any).ad_account_name && (
                <div className="text-sm">
                  <span className="text-gray-500">Ad Account:</span>{' '}
                  <span className="text-gray-900 font-medium">{String((capiStats as any).ad_account_name)}</span>
                </div>
              )}
              {(capiStats as any).page_name && (
                <div className="text-sm">
                  <span className="text-gray-500">Page:</span>{' '}
                  <span className="text-gray-900 font-medium">{String((capiStats as any).page_name)}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-400">
            No Meta connection found. Connect your Meta account from the Meta Ads page first.
          </p>
        )}

        {/* Per-event toggles */}
        <div className="pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-3">Event Controls</p>
          <p className="text-xs text-gray-400 mb-3">
            Choose which pipeline events are sent to Meta. Disabling low-quality events improves optimization signal.
          </p>
          <div className="space-y-2.5">
            {[
              { event: 'Lead', stage: 'New', desc: 'New lead created from Meta Ads form. Confirms the lead was received.' },
              { event: 'Contact', stage: 'Contacted', desc: 'First human contact made. Signals lead engagement quality to Meta.' },
              { event: 'QualifiedLead', stage: 'Qualified', desc: 'Lead qualified after discovery. Strong signal — helps Meta find similar high-quality leads.' },
              { event: 'Schedule', stage: 'Demo Scheduled', desc: 'Demo or meeting booked. High-intent signal for optimization.' },
              { event: 'InitiateCheckout', stage: 'Proposal Sent', desc: 'Proposal sent to lead. Indicates purchase intent.' },
              { event: 'Purchase', stage: 'Won', desc: 'Deal closed. Sends deal value to Meta for ROAS optimization. Most valuable signal.' },
            ].map(({ event, stage, desc }) => {
              const config = (() => {
                try { return JSON.parse(values.capi_event_config || '{}') } catch { return {} }
              })()
              const enabled = config[event] !== false
              return (
                <div key={event} className="flex items-start gap-3 py-2 px-3 rounded-lg hover:bg-gray-50">
                  <button
                    type="button"
                    onClick={() => {
                      const next = { ...config, [event]: !enabled }
                      updateValue('capi_event_config', JSON.stringify(next))
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 mt-0.5 ${
                      enabled ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{event}</span>
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-medium">{stage}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </SettingsSection>

      {/* Data Storage */}
      <SettingsSection title="Data Storage" icon={Globe}>
        <Select
          label="File Storage Mode"
          options={[
            { value: 'local', label: 'Local (data/transfers/)' },
            { value: 's3', label: 'AWS S3' },
          ]}
          value={values.data_storage_mode || 'local'}
          onChange={(e) => updateValue('data_storage_mode', e.target.value)}
        />
        {values.data_storage_mode === 'local' && (
          <p className="text-xs text-gray-500">
            Imported/exported CSV files are stored locally at <code className="bg-gray-100 px-1 rounded">data/transfers/</code>. Accessible from the Data Transfers page.
          </p>
        )}
        {values.data_storage_mode === 's3' && (
          <>
            <Input
              label="S3 Bucket Name"
              value={values.s3_bucket || ''}
              onChange={(e) => updateValue('s3_bucket', e.target.value)}
              placeholder="my-opsnest-bucket"
            />
            <Input
              label="AWS Region"
              value={values.s3_region || ''}
              onChange={(e) => updateValue('s3_region', e.target.value)}
              placeholder="ap-south-1"
            />
            <SecretInput
              label="AWS Access Key ID"
              value={values.s3_access_key || ''}
              onChange={(v) => updateValue('s3_access_key', v)}
              visible={visibleKeys.has('s3_access_key')}
              onToggleVisible={() => toggleVisible('s3_access_key')}
            />
            <SecretInput
              label="AWS Secret Access Key"
              value={values.s3_secret_key || ''}
              onChange={(v) => updateValue('s3_secret_key', v)}
              visible={visibleKeys.has('s3_secret_key')}
              onToggleVisible={() => toggleVisible('s3_secret_key')}
            />
            <Input
              label="Path Prefix"
              value={values.s3_path_prefix || ''}
              onChange={(e) => updateValue('s3_path_prefix', e.target.value)}
              placeholder="opsnest"
            />
            <p className="text-xs text-gray-500">
              Files are stored in S3:
            </p>
            <ul className="text-xs text-gray-500 list-disc list-inside ml-2 space-y-0.5">
              <li>CSV imports/exports: <code className="bg-gray-100 px-1 rounded">{'{prefix}'}/transfers/</code></li>
              <li>Call recordings (when S3 is selected in VoIP): <code className="bg-gray-100 px-1 rounded">{'{prefix}'}/recordings/</code></li>
            </ul>
          </>
        )}
      </SettingsSection>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared types & helper components
// ---------------------------------------------------------------------------

interface SettingsTabProps {
  values: AppSettings
  updateValue: (key: string, value: string) => void
  visibleKeys: Set<string>
  toggleVisible: (key: string) => void
}

function BackfillSection() {
  const [recordingsLoading, setRecordingsLoading] = useState(false)
  const [billingLoading, setBillingLoading] = useState(false)
  const [recordingsResult, setRecordingsResult] = useState<string | null>(null)
  const [billingResult, setBillingResult] = useState<string | null>(null)

  const handleBackfillRecordings = async () => {
    setRecordingsLoading(true)
    setRecordingsResult(null)
    try {
      const resp = await voipApi.backfillRecordings({ days_back: 30 })
      const d = resp?.data
      if (!d) {
        setRecordingsResult('No data returned — check server logs')
      } else if (d.checked === 0) {
        setRecordingsResult('All recordings are up to date — nothing to sync')
      } else {
        setRecordingsResult(`Checked ${d.checked} calls: ${d.downloaded} downloaded, ${d.found} found, ${d.failed} failed`)
      }
    } catch (err: any) {
      setRecordingsResult(`Error: ${err?.message || 'Request failed'}`)
    } finally {
      setRecordingsLoading(false)
    }
  }

  const handleBackfillBilling = async () => {
    setBillingLoading(true)
    setBillingResult(null)
    try {
      const resp = await voipApi.backfillBilling({ days_back: 7 })
      const d = resp?.data
      if (!d) {
        setBillingResult('No data returned — check server logs')
      } else if (d.total_checked === 0) {
        setBillingResult('All billing data is up to date — nothing to sync')
      } else {
        setBillingResult(`Checked ${d.total_checked} calls: ${d.synced} synced, ${d.skipped} pending, ${d.failed} failed`)
      }
    } catch (err: any) {
      setBillingResult(`Error: ${err?.message || 'Request failed'}`)
    } finally {
      setBillingLoading(false)
    }
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-5">
        <Phone size={18} className="text-gray-400" /> Data Sync
      </h2>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Sync Missing Recordings</p>
            <p className="text-xs text-gray-500 mt-0.5">Fetch recordings from Plivo for calls that completed without a recording (last 30 days)</p>
          </div>
          <button
            onClick={handleBackfillRecordings}
            disabled={recordingsLoading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
          >
            {recordingsLoading ? 'Syncing...' : 'Sync Recordings'}
          </button>
        </div>
        {recordingsResult && (
          <p className={`text-xs px-3 py-2 rounded-lg ${recordingsResult.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {recordingsResult}
          </p>
        )}

        <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-800">Sync Missing Billing &amp; Duration</p>
            <p className="text-xs text-gray-500 mt-0.5">Fetch call duration and billing data from Plivo CDR for calls with 0 duration or missing costs (last 7 days)</p>
          </div>
          <button
            onClick={handleBackfillBilling}
            disabled={billingLoading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 transition-colors"
          >
            {billingLoading ? 'Syncing...' : 'Sync Billing'}
          </button>
        </div>
        {billingResult && (
          <p className={`text-xs px-3 py-2 rounded-lg ${billingResult.startsWith('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
            {billingResult}
          </p>
        )}
      </div>
    </div>
  )
}


function SettingsSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: React.ComponentType<{ size?: number; className?: string }>
  children: React.ReactNode
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2 mb-5">
        <Icon size={18} className="text-gray-400" /> {title}
      </h2>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function ProviderSection({
  title,
  isActive,
  configured,
  children,
}: {
  title: string
  isActive: boolean
  configured: boolean
  children: React.ReactNode
}) {
  const [expanded, setExpanded] = useState(isActive)

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        isActive ? 'border-indigo-300 bg-indigo-50/30' : 'border-gray-200'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-left hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <span className={isActive ? 'text-indigo-700' : 'text-gray-700'}>{title}</span>
          {isActive && (
            <span className="px-1.5 py-0.5 bg-indigo-500 text-white text-[9px] font-bold rounded">
              ACTIVE
            </span>
          )}
          <span
            className={`flex items-center gap-1 text-[10px] ${
              configured ? 'text-green-600' : 'text-gray-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${configured ? 'bg-green-500' : 'bg-gray-300'}`}
            />
            {configured ? 'Configured' : 'Not configured'}
          </span>
        </div>
        <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  )
}

function SecretInput({
  label,
  value,
  onChange,
  visible,
  onToggleVisible,
}: {
  label: string
  settingKey?: string
  value: string
  onChange: (v: string) => void
  visible: boolean
  onToggleVisible: () => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        <button
          type="button"
          onClick={onToggleVisible}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {visible ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: MCP Access — per-user bearer tokens for connecting Claude
// ---------------------------------------------------------------------------

function MCPAccessTab() {
  const queryClient = useQueryClient()
  const [newToken, setNewToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['mcp-tokens'],
    queryFn: () => mcpApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: () => mcpApi.create(),
    onSuccess: (res) => {
      setNewToken(res.data.token)
      setCopied(false)
      queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (id: number) => mcpApi.revoke(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['mcp-tokens'] }),
  })

  const tokens = data?.data ?? []
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://your-opsnest-host'
  const connectCommand = `claude mcp add --transport http opsnest ${origin}/mcp \\\n  --header "Authorization: Bearer ${newToken ?? 'opsn_YOUR_TOKEN'}"`

  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const fmtDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'

  return (
    <div className="space-y-4">
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-gray-900">MCP Access</h2>
        <p className="text-sm text-gray-500 mt-1">
          Connect Claude (Claude Code or Desktop) to query your OpsNest data conversationally.
          Tokens are <strong>read-only</strong> and authenticate as <strong>you</strong> — results respect your role and contact-masking settings.
        </p>
      </div>

      {/* What Claude can query */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <div className="flex items-center gap-2 font-semibold mb-1">
          <Cpu className="w-4 h-4" /> What Claude can query
        </div>
        <p className="text-blue-800">
          Leads &amp; pipeline, lead detail (notes, calls, stage history), call analytics, your tasks,
          and Meta Ads performance (admin/manager/marketing). Claude can read but never change data.
        </p>
      </div>

      {/* Newly created token — shown once */}
      {newToken && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-green-900">
              <KeyRound className="w-4 h-4" /> New token created
            </div>
            <button onClick={() => setNewToken(null)} className="text-green-700 hover:text-green-900">
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-green-800 mb-2">
            Copy it now — for your security it is <strong>shown only once</strong> and cannot be retrieved again.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-green-200 rounded px-2 py-1.5 font-mono text-gray-800 break-all">
              {newToken}
            </code>
            <Button size="sm" variant="secondary" onClick={() => copy(newToken)}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Generate */}
      <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
        <div>
          <p className="text-sm font-medium text-gray-800">Personal access tokens</p>
          <p className="text-xs text-gray-500 mt-0.5">Tokens expire after 365 days. Revoke anytime.</p>
        </div>
        <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
          <Plus className="w-4 h-4 mr-1" />
          {createMutation.isPending ? 'Generating…' : 'Generate Token'}
        </Button>
      </div>

      {/* Token list */}
      {isLoading ? (
        <LoadingState />
      ) : tokens.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No tokens yet"
          description="Generate a token to connect Claude to OpsNest."
        />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Token</th>
                <th className="px-4 py-2 text-left font-semibold">Created</th>
                <th className="px-4 py-2 text-left font-semibold">Expires</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tokens.map((t) => (
                <tr key={t.id}>
                  <td className="px-4 py-2 font-mono text-gray-500">opsn_••••••••  (#{t.id})</td>
                  <td className="px-4 py-2 text-gray-700">{fmtDate(t.created_at)}</td>
                  <td className="px-4 py-2 text-gray-700">{fmtDate(t.expires_at)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={t.active ? 'success' : 'default'}>{t.active ? 'Active' : 'Expired'}</Badge>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Revoke token #${t.id}? Any Claude client using it will stop working.`)) {
                          revokeMutation.mutate(t.id)
                        }
                      }}
                      disabled={revokeMutation.isPending}
                      className="text-red-600 hover:text-red-800 inline-flex items-center gap-1 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* How to connect */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm">
        <p className="font-semibold text-gray-800 mb-2">Connect with Claude Code</p>
        <div className="relative">
          <pre className="text-xs bg-gray-900 text-gray-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">{connectCommand}</pre>
          <button
            onClick={() => copy(connectCommand)}
            className="absolute top-2 right-2 text-gray-300 hover:text-white"
            title="Copy command"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          For Claude Desktop, use the <code className="bg-gray-100 px-1 rounded">mcp-remote</code> bridge with the same URL and header.
          For claude.ai / Claude Desktop, add a custom connector to <code className="bg-gray-100 px-1 rounded">{origin}/mcp</code> instead (OAuth — no token needed). This bearer token is for Claude Code.
        </p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: MCP Tools — tier-2 access control (admin)
// ---------------------------------------------------------------------------

function MCPToolsTab() {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<McpToolConfig | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['mcp-tool-config'],
    queryFn: () => mcpApi.getToolConfig(),
  })

  useEffect(() => {
    if (data?.data) setDraft(data.data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: (cfg: McpToolConfig) => mcpApi.updateToolConfig(cfg),
    onSuccess: (res) => {
      setDraft(res.data)
      queryClient.invalidateQueries({ queryKey: ['mcp-tool-config'] })
    },
  })

  if (isLoading || !draft) return <LoadingState />

  const setTier = (key: keyof McpToolConfig, patch: Partial<McpTierConfig>) => {
    const tier = draft[key] as McpTierConfig
    setDraft({ ...draft, [key]: { ...tier, ...patch } })
  }

  const toggleRole = (key: keyof McpToolConfig, role: string) => {
    const tier = draft[key] as McpTierConfig
    const roles = tier.roles.includes(role)
      ? tier.roles.filter((r) => r !== role)
      : [...tier.roles, role]
    setTier(key, { roles })
  }

  return (
    <div className="space-y-4">
      <div className="mb-2">
        <h2 className="text-lg font-semibold text-gray-900">MCP Tools</h2>
        <p className="text-sm text-gray-500 mt-1">
          Control which advanced (tier-2) tools Claude can use over MCP, who can use them, and per-day limits.
          All are <strong>off by default</strong>. Core read-only CRM tools are always available.
        </p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
        Enabling a tier lets connected Claude clients call those tools as the authenticated user (subject to their role and your caps). Some tiers perform writes or use paid external APIs — review each before enabling.
      </div>

      {MCP_TIERS.map(({ key, label, desc, available }) => {
        const tier = draft[key] as McpTierConfig
        return (
          <div
            key={key}
            className={`bg-white border rounded-lg p-4 ${available ? 'border-gray-200' : 'border-gray-200 opacity-70'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-800">{label}</span>
                  {available ? (
                    <Badge variant="success">Active</Badge>
                  ) : (
                    <Badge variant="default">Coming soon</Badge>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
              </div>
              <label className="inline-flex items-center gap-2 shrink-0">
                <input
                  type="checkbox"
                  checked={tier.enabled}
                  disabled={!available}
                  onChange={(e) => setTier(key, { enabled: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <span className="text-xs text-gray-600">Enabled</span>
              </label>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Allowed roles</label>
                <div className="flex flex-wrap gap-2">
                  {MCP_ALL_ROLES.map((role) => (
                    <label key={role} className="inline-flex items-center gap-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={tier.roles.includes(role)}
                        disabled={!available}
                        onChange={() => toggleRole(key, role)}
                        className="rounded border-gray-300"
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Daily cap per user <span className="text-gray-400">(blank = unlimited)</span>
                </label>
                <input
                  type="number"
                  min={0}
                  value={tier.daily_cap ?? ''}
                  disabled={!available}
                  onChange={(e) =>
                    setTier(key, { daily_cap: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  className="w-32 px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-50"
                  placeholder="∞"
                />
              </div>
            </div>
          </div>
        )
      })}

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-800 mb-1">Monthly cost ceiling (USD)</label>
        <p className="text-xs text-gray-500 mb-2">Estimated spend across paid external calls (live Meta + IG). Cost tiers refuse once exceeded. Enforced once those tiers ship.</p>
        <input
          type="number"
          min={0}
          value={draft.monthly_cost_ceiling_usd ?? ''}
          onChange={(e) =>
            setDraft({ ...draft, monthly_cost_ceiling_usd: e.target.value === '' ? null : Number(e.target.value) })
          }
          className="w-40 px-2 py-1 border border-gray-300 rounded text-sm"
          placeholder="No limit"
        />
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(draft)} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {saveMutation.isSuccess && <span className="text-xs text-green-600">Saved</span>}
        {saveMutation.isError && <span className="text-xs text-red-600">Save failed</span>}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Help Manual
// ---------------------------------------------------------------------------

function HelpSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed space-y-3 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  )
}

function HelpBadge({ children, color = 'gray' }: { children: React.ReactNode; color?: 'gray' | 'blue' | 'green' | 'amber' | 'red' | 'purple' }) {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
  }
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${colors[color]}`}>{children}</span>
}

function NotificationsTab() {
  const hasNotificationAPI = typeof window !== 'undefined' && 'Notification' in window
  const [permission, setPermission] = useState<NotificationPermission>(
    hasNotificationAPI ? Notification.permission : 'denied'
  )

  // Simple localStorage-backed prefs (will be replaced by Zustand store)
  const storageKey = 'opsnest-notif-prefs'
  const defaultPrefs = { inbound_call: true, task_assigned: true, lead_assigned: true, task_overdue: true }
  const [prefs, setPrefs] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      return stored ? { ...defaultPrefs, ...JSON.parse(stored) } : defaultPrefs
    } catch { return defaultPrefs }
  })

  const updatePref = (key: string, value: boolean) => {
    const next = { ...prefs, [key]: value }
    setPrefs(next)
    localStorage.setItem(storageKey, JSON.stringify(next))
  }

  const handleRequestPermission = async () => {
    if (!hasNotificationAPI) return
    const result = await Notification.requestPermission()
    setPermission(result)
  }

  const NOTIFICATION_TYPES = [
    { key: 'inbound_call', label: 'Incoming call alerts', description: 'Browser alert when an inbound call rings' },
    { key: 'task_assigned', label: 'Task assigned to me', description: 'When a task is assigned to you' },
    { key: 'lead_assigned', label: 'Lead assigned to me', description: 'When a lead is assigned to you' },
    { key: 'task_overdue', label: 'Task overdue', description: 'When a task passes its due date' },
  ]

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Browser Notifications</h3>

        {/* Permission status */}
        <div className="mb-6">
          {!hasNotificationAPI ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
              <span className="w-2 h-2 rounded-full bg-gray-400" />
              <span className="text-sm text-gray-600">Browser notifications not supported</span>
            </div>
          ) : permission === 'granted' ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg border border-green-200">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-sm text-green-700">Notifications enabled</span>
            </div>
          ) : permission === 'denied' ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              <span className="text-sm text-amber-700">
                Notifications blocked — update your browser settings to enable
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <span className="text-sm text-blue-700">Enable browser notifications for real-time alerts</span>
              <button
                onClick={handleRequestPermission}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Enable
              </button>
            </div>
          )}
        </div>

        {/* Per-type toggles */}
        <div className="space-y-3">
          {NOTIFICATION_TYPES.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div>
                <p className="text-sm font-medium text-gray-900">{label}</p>
                <p className="text-xs text-gray-500">{description}</p>
              </div>
              <button
                onClick={() => updatePref(key, !(prefs as Record<string, boolean>)[key])}
                className={`relative w-10 h-5 rounded-full transition-colors ${
                  (prefs as Record<string, boolean>)[key] ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  (prefs as Record<string, boolean>)[key] ? 'translate-x-5' : ''
                }`} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function SecurityTab({ values, updateValue }: { values: AppSettings; updateValue: (key: string, value: string) => void }) {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['blocked-ips'],
    queryFn: () => authApi.getBlockedIps(),
    refetchInterval: 30_000,
  })
  const unblockMutation = useMutation({
    mutationFn: (ip: string) => authApi.unblockIp(ip),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blocked-ips'] }),
  })

  const ips = data?.data ?? []

  return (
    <div className="space-y-6">
      {/* Data Privacy */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Data Privacy</h2>
        <div className="flex items-center justify-between py-2">
          <div>
            <p className="text-sm font-medium text-gray-800">Mask contact information</p>
            <p className="text-xs text-gray-500 mt-0.5">
              When enabled, phone numbers, emails, and addresses are masked in the UI for all users.
              Individual users can be granted full access via their profile.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={values.mask_contact_info === 'true'}
              onChange={(e) => updateValue('mask_contact_info', e.target.checked ? 'true' : 'false')}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600" />
          </label>
        </div>
        <p className="text-[11px] text-gray-400 mt-1">
          Masking affects: phone numbers (+91******3210), emails (p****@example.com), addresses, city, WhatsApp numbers, and call log from/to numbers.
          Outbound calling still works — the system dials using the real number from the database.
        </p>
      </div>

      {/* Blocked IPs */}
      <div>
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Shield size={20} /> Blocked IPs</h2>
        <p className="text-sm text-gray-500 mt-1">IP addresses that have been rate-limited or blocked due to too many failed login attempts.</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32 text-gray-400">Loading...</div>
      ) : ips.length === 0 ? (
        <EmptyState icon={Shield} title="No rate-limited IPs" description="No IP addresses are currently being tracked for suspicious activity." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Attempts</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Block Remaining</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {ips.map((entry) => (
                <tr key={entry.ip_address}>
                  <td className="px-4 py-3 text-sm font-mono text-gray-900">{entry.ip_address}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{entry.attempt_count}</td>
                  <td className="px-4 py-3">
                    {entry.is_blocked
                      ? <Badge variant="danger">Blocked</Badge>
                      : <Badge variant="warning">Monitoring</Badge>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {entry.is_blocked && entry.block_remaining_minutes > 0
                      ? `${entry.block_remaining_minutes}m remaining`
                      : '--'}
                  </td>
                  <td className="px-4 py-3">
                    {entry.is_blocked && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => unblockMutation.mutate(entry.ip_address)}
                        loading={unblockMutation.isPending && unblockMutation.variables === entry.ip_address}
                      >
                        Unblock
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* Access Log */}
      <AccessLogSection />
    </div>
  )
}

function AccessLogSection() {
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [page, setPage] = useState(0)

  const params: Record<string, string> = { limit: '25', offset: String(page * 25) }
  if (filters.user_id) params.user_id = filters.user_id
  if (filters.action_type) params.action_type = filters.action_type
  if (filters.country_code) params.country_code = filters.country_code

  const { data, isLoading } = useQuery({
    queryKey: ['access-log', params],
    queryFn: () => adminApi.getAccessLog(params),
  })

  const logs: AccessLogEntry[] = data?.data ?? []
  const total = data?.total ?? 0

  const actionLabels: Record<string, string> = {
    login: 'Web Login',
    mobile_login: 'Mobile Login',
    voip_call: 'VoIP Call',
    data_import: 'CSV Import',
    data_export: 'CSV Export',
  }

  function fmtTime(iso: string) {
    try {
      const d = new Date(iso)
      return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return iso }
  }

  function parseUA(ua: string | null) {
    if (!ua) return '--'
    if (ua.includes('Mobile')) return 'Mobile'
    if (ua.includes('Chrome')) return 'Chrome'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Safari')) return 'Safari'
    if (ua.includes('Edge')) return 'Edge'
    return ua.slice(0, 30)
  }

  return (
    <div>
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Globe size={20} /> Access Log</h2>
          <p className="text-sm text-gray-500 mt-1">Track where users access OpsNest from — login, calls, imports, and exports.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filters.action_type || ''}
            onChange={(e) => { setFilters(f => ({ ...f, action_type: e.target.value })); setPage(0) }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white"
          >
            <option value="">All actions</option>
            <option value="login">Web Login</option>
            <option value="mobile_login">Mobile Login</option>
            <option value="voip_call">VoIP Call</option>
            <option value="data_import">CSV Import</option>
            <option value="data_export">CSV Export</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">Loading access log...</div>
      ) : logs.length === 0 ? (
        <div className="flex items-center justify-center py-12 text-gray-400">No access events found</div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">ISP / Org</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        <p className="font-medium text-gray-900">{log.full_name}</p>
                        <p className="text-xs text-gray-400">{log.username}</p>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.action_type === 'login' || log.action_type === 'mobile_login'
                            ? 'bg-blue-100 text-blue-700'
                            : log.action_type === 'voip_call'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {actionLabels[log.action_type] || log.action_type}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 font-mono text-xs">{log.ip_address}</td>
                      <td className="px-4 py-2.5">
                        {log.geo_resolved ? (
                          <div>
                            <p className="text-gray-900">{[log.city, log.region].filter(Boolean).join(', ')}</p>
                            <p className="text-xs text-gray-400">{log.country_name} {log.country_code ? `(${log.country_code})` : ''}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Resolving...</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 max-w-[150px] truncate" title={log.org || ''}>{log.org || '--'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{parseUA(log.user_agent)}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtTime(log.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-gray-500">{total} total events</p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page + 1}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={(page + 1) * 25 >= total}
                className="px-3 py-1 text-xs border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function HelpManualTab() {
  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Help Manual</h2>
        <p className="text-sm text-gray-500 mt-1">Everything you need to know about OpsNest — features, workflows, and configuration.</p>
      </div>

      {/* ── Getting Started ── */}
      <HelpSection title="Getting Started">
        <p><strong>OpsNest</strong> is an AI-powered marketing, sales, and CRM platform built for Storepecker and UGCommerce. It combines AI content generation, lead management, VoIP calling, email campaigns, Meta Ads integration, and team management into a single tool.</p>
        <p><strong>First-time setup:</strong></p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Create your admin account on the login page (first user becomes admin)</li>
          <li>Go to <strong>Settings &rarr; General</strong> to configure your LLM provider (OpenAI, Anthropic, or Gemini) and API key</li>
          <li>Set up email and VoIP providers if needed</li>
          <li>Create team members in <strong>Users</strong> with appropriate roles</li>
          <li>Set up VoIP calling in <strong>Settings &rarr; VoIP</strong> (see VoIP Setup Guide below)</li>
          <li>Create teams in <strong>Teams</strong> page or click <strong>Load Defaults</strong> for Sales + Support</li>
          <li>Configure IVR menus in <strong>Settings &rarr; IVR Menus</strong> or click <strong>Load Defaults</strong></li>
          <li>Configure lead assignment rules in <strong>Settings &rarr; Lead Assignment</strong></li>
          <li>Connect Meta Ads in <strong>Meta Ads</strong> page if running Facebook/Instagram campaigns</li>
        </ol>
      </HelpSection>

      {/* ── Roles & Permissions ── */}
      <HelpSection title="Roles & Permissions">
        <p>OpsNest has four roles, each with different access levels:</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Feature</th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700"><HelpBadge color="purple">Admin</HelpBadge></th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700"><HelpBadge color="blue">Manager</HelpBadge></th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700"><HelpBadge color="green">Marketing</HelpBadge></th>
                <th className="px-3 py-2 text-center font-semibold text-gray-700"><HelpBadge color="amber">Sales Rep</HelpBadge></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {[
                ['Dashboard & Reports', true, true, false, false],
                ['Leads & Companies', true, true, false, true],
                ['Tasks', true, true, true, true],
                ['Email', true, true, true, false],
                ['VoIP / Calls', true, true, false, true],
                ['Teams & Call Routing', true, true, false, false],
                ['Team Analytics', true, true, false, false],
                ['Team Activity (Presence)', true, true, false, false],
                ['AI Tools (Blog, Social, etc.)', true, true, true, false],
                ['Meta Ads', true, true, true, false],
                ['Ad Analytics', true, true, false, false],
                ['Social Analytics', true, true, true, false],
                ['Team Workload', true, true, false, false],
                ['Data Transfers', true, true, false, false],
                ['Data Import (CSV)', true, true, false, false],
                ['Data Export (CSV)', true, false, false, false],
                ['Task Monitor', true, false, false, false],
                ['User Management', true, false, false, false],
                ['Settings', true, false, false, false],
              ].map(([feature, admin, manager, marketing, sales], i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                  <td className="px-3 py-1.5 text-gray-700">{feature as string}</td>
                  {[admin, manager, marketing, sales].map((has, j) => (
                    <td key={j} className="px-3 py-1.5 text-center">{has ? <span className="text-green-600">&#10003;</span> : <span className="text-gray-300">&mdash;</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </HelpSection>

      {/* ── Lead Management ── */}
      <HelpSection title="Lead Management & CRM">
        <p><strong>Leads</strong> are the core of OpsNest. Each lead tracks a potential customer through your sales pipeline.</p>

        <p className="font-semibold text-gray-800 mt-2">Pipeline Stages</p>
        <div className="flex flex-wrap gap-1.5">
          {['New', 'Contacted', 'Qualified', 'Demo Scheduled', 'Proposal Sent', 'Negotiation', 'Won', 'Lost'].map((s, i) => (
            <HelpBadge key={s} color={s === 'Won' ? 'green' : s === 'Lost' ? 'red' : i < 3 ? 'blue' : 'purple'}>{s}</HelpBadge>
          ))}
        </div>

        <p className="font-semibold text-gray-800 mt-3">Lead Scoring (0-100)</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Fit Score (0-40):</strong> How well the lead matches your ideal customer profile</li>
          <li><strong>Engagement Score (0-35):</strong> How actively the lead interacts (calls, emails, meetings)</li>
          <li><strong>Urgency Score (0-25):</strong> How quickly the lead is likely to buy</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Quality Segments</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><HelpBadge color="red">HOT</HelpBadge> Score 75-100 &mdash; High priority, contact immediately</li>
          <li><HelpBadge color="amber">WARM</HelpBadge> Score 45-74 &mdash; Qualified, needs nurturing</li>
          <li><HelpBadge color="blue">COLD</HelpBadge> Score 0-44 &mdash; Low priority, may need more time</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Filtering, Search &amp; Sort</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Search:</strong> Searches by name, email, phone number, and lead ID (LD-XXXXXXXX format)</li>
          <li><strong>Agent filter:</strong> Filter leads by assigned owner &mdash; the owner/agent dropdown lists all team members</li>
          <li><strong>Date filters:</strong> Filter by Created date range, Updated date range, or Submitted (form fill) date range</li>
          <li><strong>Sort:</strong> Sort by Created, Updated, Submitted, Last Call, Score, or Name &mdash; toggle Asc/Desc for each</li>
          <li><strong>Stage / Segment / Source:</strong> Additional filters for pipeline stage, quality segment (HOT/WARM/COLD), and lead source</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Key Features</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Quick View:</strong> Click the eye icon on any lead for a summary without leaving the list</li>
          <li><strong>Quick Call:</strong> Click the phone icon on a lead &mdash; this navigates to the VoIP page and starts the call there</li>
          <li><strong>Assign:</strong> Click the person icon to assign the lead to a team member</li>
          <li><strong>Auto-Assign:</strong> Click "Auto-Assign" in the header to distribute unassigned leads using your assignment rules</li>
          <li><strong>CSV Import:</strong> 4-step wizard &mdash; Upload → Map columns → Preview (validate/clean/deduplicate) → Import. Only valid rows are committed. See the <strong>Import &amp; Export</strong> section below for details.</li>
          <li><strong>CSV Export:</strong> Export your current filtered view as CSV. The export respects all active filters.</li>
          <li><strong>Lead Detail Page:</strong> Click a lead name to see the unified activity timeline, paginated tabs (calls/emails/tasks/submissions), and edit history</li>
          <li><strong>Activity Timeline:</strong> Unified chronological feed of calls, notes, CallPulse responses, and stage changes on the lead detail page</li>
          <li><strong>Lead Notes:</strong> Append-only notes on each lead &mdash; every note tracks the author and timestamp. Notes cannot be edited or deleted. All notes render as Markdown. Meta Ads form submission notes show a blue card with a Megaphone icon so they are easy to distinguish from manual notes.</li>
          <li><strong>Create with Owner:</strong> When creating a lead manually, you can set the <strong>Owner</strong> field to assign the lead to a specific rep immediately rather than relying on auto-assignment.</li>
          <li><strong>Immutable Call Logs:</strong> Call logs are permanent records. Once created they cannot be edited or deleted, ensuring a complete audit trail.</li>
          <li><strong>Edit History:</strong> Every field change on a lead (name, stage, score, assignment, etc.) is recorded in an immutable audit trail visible under &ldquo;Edit History&rdquo; on the lead detail page. Admin and manager only.</li>
          <li><strong>Merge &amp; Unmerge:</strong> Merge duplicate leads into a single record (combines history, notes, calls, and CallPulse responses). Unmerge restores the original lead from a snapshot if the merge was a mistake.</li>
          <li><strong>Bulk Update:</strong> Select multiple leads using the checkboxes on the Leads page, then click <strong>Bulk Update</strong> to change the source, channel, or pipeline stage for all selected leads at once. All changes are tracked in edit history.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Lead Source &amp; Channel</p>
        <p>Each lead has a <strong>Source</strong> and <strong>Channel</strong> field that you can set when creating or editing a lead. These help you track where your leads come from.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Source:</strong> The origin of the lead (e.g. Meta Ads, Organic, Referral, Website, Event)</li>
          <li><strong>Channel:</strong> How the lead entered OpsNest &mdash; Manual, CSV Import, Meta Webhook, Referral, Organic, Paid Social, and more</li>
          <li>Both fields are available in the lead edit form and can also be set via bulk update</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Unknown Callers</p>
        <p>Go to <strong>Unknown Callers</strong> in the sidebar to see inbound phone numbers that don&apos;t match any lead in your CRM. Each row shows the phone number, how many times they&apos;ve called, and the date range. Use the inline <strong>Create Lead</strong> form to convert the caller to a lead &mdash; all past calls from that number are automatically linked to the new lead.</p>
      </HelpSection>

      {/* ── Lead Assignment System ── */}
      <HelpSection title="Lead Assignment System">
        <p>The assignment system automatically routes new leads to the right sales rep based on configurable rules. Configure rules in the <strong>Lead Assignment</strong> tab.</p>

        <p className="font-semibold text-gray-800 mt-2">Assignment Strategies</p>
        <div className="space-y-2 ml-2">
          <div><HelpBadge color="blue">Round Robin</HelpBadge> &mdash; Distributes leads evenly across all available reps. Fairest option. <em>Best for small teams (3-10 reps).</em></div>
          <div><HelpBadge color="purple">Performance Based</HelpBadge> &mdash; Routes leads to reps with the highest conversion rates (30-day window). <em>Best after 3+ months of data.</em></div>
          <div><HelpBadge color="green">Skill Based</HelpBadge> &mdash; Matches leads to reps based on product expertise (Storepecker vs UGCommerce). <em>Best when reps specialize by product.</em></div>
          <div><HelpBadge color="amber">Load Balanced</HelpBadge> &mdash; Assigns to the rep with the most available capacity. <em>Best combined with other strategies.</em></div>
          <div><HelpBadge color="red">Score Based</HelpBadge> &mdash; Routes HOT leads to top-performing reps; others via round-robin. <em>Best for maximizing conversion of high-value leads.</em></div>
          <div><HelpBadge>Hybrid</HelpBadge> &mdash; Combines multiple strategies with weighted scoring. <em>Best for mature teams with clear data.</em></div>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Rule Filters</p>
        <p>Each rule can be scoped to specific leads using filters:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Product:</strong> Apply only to Storepecker or UGCommerce leads</li>
          <li><strong>Lead Source:</strong> Apply only to Meta Ads, organic, referral, or CSV import leads</li>
          <li><strong>Quality Segment:</strong> Apply only to a specific quality segment &mdash; the available segments are loaded from your current scoring config (not a fixed list), so custom tiers you create appear here automatically</li>
          <li><strong>Priority:</strong> Lower number = higher priority. When multiple rules match a lead, the highest-priority rule wins.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">SLA (Service Level Agreement)</p>
        <p>Each rule has an SLA timer &mdash; the maximum time a rep has to make first contact after assignment.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><HelpBadge color="green">OK</HelpBadge> &mdash; Within SLA window</li>
          <li><HelpBadge color="amber">Warning</HelpBadge> &mdash; Past 50% of SLA time, no contact yet</li>
          <li><HelpBadge color="red">Breached</HelpBadge> &mdash; Past SLA deadline, lead may be auto-reassigned</li>
        </ul>
        <p className="mt-1">Recommended SLAs: <strong>15 min</strong> for HOT leads, <strong>4 hours</strong> for WARM, <strong>24 hours</strong> for COLD.</p>

        <p className="font-semibold text-gray-800 mt-3">Automated SLA Enforcement</p>
        <p>A background task runs every 5 minutes and automatically handles breached SLAs:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Evaluates every assigned lead that has not reached a terminal stage</li>
          <li>Updates the <code className="bg-gray-100 px-1 rounded text-xs">sla_status</code> on each lead (ok / warning / breached)</li>
          <li>Creates a breach notification in the notification center for the rep and their manager</li>
          <li>HOT breached leads are automatically reassigned to another available rep</li>
          <li>Escalates to the configured escalation user on the assignment rule</li>
          <li>No action is needed &mdash; this runs continuously in the background</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Load Default Rules</p>
        <p>Click <strong>&ldquo;Load Defaults&rdquo;</strong> in the Lead Assignment tab to set up pre-configured rules. A picker lets you choose specific rules to load &mdash; rules that already exist are skipped:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>HOT Leads &mdash; Score-Based Fast Track</strong> (priority 1, 30 min SLA) &mdash; Routes HOT leads to your highest-converting reps immediately.</li>
          <li><strong>UGCommerce Referrals &mdash; Performance Routing</strong> (priority 2, 60 min SLA) &mdash; Sends UGCommerce referrals to the reps with the best recent conversion rates.</li>
          <li><strong>Meta Ads WARM &mdash; Load-Balanced Response</strong> (priority 3, 4 hr SLA) &mdash; Distributes WARM Meta Ads leads evenly by capacity.</li>
          <li><strong>Storepecker Organic &mdash; Skill-Based Nurture</strong> (priority 4, 8 hr SLA) &mdash; Routes Storepecker organic leads to reps with matching product skills.</li>
          <li><strong>Catch-All &mdash; Round Robin</strong> (priority 100, 24 hr SLA) &mdash; Any lead not matched by a higher-priority rule goes here.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Workload Page</p>
        <p>Go to <strong>Team</strong> in the sidebar (admin/manager only) to see:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Each rep's active leads vs capacity (with progress bars)</li>
          <li>Conversion rates, response times, SLA compliance</li>
          <li>Toggle rep availability (reps marked unavailable won't receive new leads)</li>
          <li>Unassigned lead queue with manual assign and bulk auto-assign</li>
        </ul>
      </HelpSection>

      {/* ── Lead Queue & Parking ── */}
      <HelpSection title="Lead Queue &amp; Parking">
        <p>The <strong>Queue</strong> view (accessible from the Leads page) shows uncontacted or stalled leads that need outreach. Parking lets reps temporarily suspend contact attempts on a lead without losing it.</p>

        <p className="font-semibold text-gray-800 mt-3">Contact Attempts Counter</p>
        <p>Each lead tracks <strong>contact attempts</strong> — the number of outbound call attempts made to that lead. This is separate from the overall call count and is used to drive queue retry logic.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Increments on every outbound call attempt to the lead</li>
          <li>Resets automatically when an inbound connected call is received (the lead called back)</li>
          <li>At <strong>6 attempts</strong> without a connection, the lead is auto-parked</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Parking a Lead</p>
        <p>Parking pauses a lead in the queue so it is not surfaced for outreach. Use it when a lead needs to be tried again later but not right now.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Manual park:</strong> Click the <strong>Park</strong> button on a lead&apos;s queue card and enter a reason</li>
          <li><strong>Auto-park:</strong> Triggers automatically after 6 outbound contact attempts with no connection</li>
          <li>Parked leads show the parked-by name, timestamp, and reason on their card</li>
          <li><strong>Unpark:</strong> Click <strong>Unpark</strong> on a parked lead to return it to the active queue. The contact attempt count is preserved (not reset) so the history is not lost.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Park History</p>
        <p>Every park and unpark event is recorded in an immutable audit log with who parked/unparked the lead, when, the reason, and the contact attempt count at that moment. This is visible on the lead detail page.</p>
      </HelpSection>

      {/* ── AI Tools ── */}
      <HelpSection title="AI Content Generation Tools">
        <p>OpsNest includes 7 AI-powered tools for generating marketing content. All tools require an LLM provider configured in <strong>Settings &rarr; General</strong>.</p>

        <div className="space-y-2.5 mt-2">
          <div>
            <span className="font-semibold text-gray-800">Blog Post Generator</span>
            <p className="ml-2">Creates SEO-optimized blog posts with title, meta description, keywords, and estimated reading time. Includes an SEO score (0-100) and recommendations.</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Social Media Posts</span>
            <p className="ml-2">Generates platform-specific posts for Twitter/X, LinkedIn, and Instagram. Each post respects platform character limits and includes hashtags.</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Full Campaign Generator</span>
            <p className="ml-2">Creates a complete multi-channel campaign with blog posts, social posts, and email sequences from a single brief.</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Strategy & Planning</span>
            <p className="ml-2">Three AI strategists: <strong>Product Manager</strong> (PRDs, discovery, research), <strong>Marketing Strategist</strong> (campaign plans, GTM), <strong>Sales Strategist</strong> (playbooks, outreach, deal strategy).</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Image Generator</span>
            <p className="ml-2">Creates images using DALL-E (OpenAI) or Imagen (Gemini). Choose model, size, and quality.</p>
          </div>
          <div>
            <span className="font-semibold text-gray-800">Web Scraper</span>
            <p className="ml-2">Scrapes and structures content from URLs. Useful for competitive research and content inspiration.</p>
          </div>
        </div>

        <p className="mt-2 text-xs text-gray-400">All generated content is saved in <strong>Run History</strong> and can be reviewed, copied, or downloaded at any time.</p>
      </HelpSection>

      {/* ── Meta Ads ── */}
      <HelpSection title="Meta Ads Integration">
        <p>Connect your Facebook/Instagram ad account to automatically sync leads from lead gen forms and track campaign performance.</p>

        <p className="font-semibold text-gray-800 mt-2">Prerequisites</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>A Meta App (from <a href="https://developers.facebook.com" className="text-indigo-600 hover:underline" target="_blank" rel="noopener">developers.facebook.com</a>) &mdash; you can reuse an existing app (e.g., one already used for WhatsApp or Instagram)</li>
          <li>A Facebook Page connected to your ad account</li>
          <li>An active ad account with Lead Ads capability</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Step 1: App Permissions</p>
        <p>Your Meta App needs these permissions (check under App Review &rarr; Permissions):</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>leads_retrieval</strong> &mdash; Read leads from forms</li>
          <li><strong>pages_show_list</strong> &mdash; List linked pages</li>
          <li><strong>pages_read_engagement</strong> &mdash; Read page content</li>
          <li><strong>pages_manage_ads</strong> &mdash; Manage page ads</li>
          <li><strong>ads_read</strong> &mdash; Read campaign performance data</li>
        </ul>
        <p className="text-xs text-gray-500 mt-1">In Development mode, these work immediately for app admins/testers. For production, submit for App Review.</p>

        <p className="font-semibold text-gray-800 mt-3">Step 2: OAuth Redirect URI</p>
        <p>In your app&apos;s Facebook Login settings, add this as a Valid OAuth Redirect URI:</p>
        <code className="block bg-gray-100 px-3 py-1.5 rounded text-xs mt-1 mb-1">https://your-domain.com/api/meta/oauth/callback</code>
        <p className="text-xs text-gray-500">For local dev with ngrok, use your ngrok URL instead.</p>

        <p className="font-semibold text-gray-800 mt-3">Step 3: Connect in OpsNest</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Meta Ads</strong> &rarr; Connection tab</li>
          <li>Enter your <strong>App ID</strong> and <strong>App Secret</strong> (from App Settings &rarr; Basic)</li>
          <li>Click <strong>Authorize with Facebook</strong> &mdash; log in with an app admin/tester account</li>
          <li>Grant the requested permissions</li>
          <li>Select your <strong>Ad Account</strong> and <strong>Page</strong></li>
          <li>Go to <strong>Lead Forms</strong> tab &rarr; <strong>Discover Forms</strong> &rarr; map fields &rarr; enable Auto Sync</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Using an Existing App (WhatsApp/Instagram)</p>
        <p>If you already have a Meta App for WhatsApp Business or Instagram automation, you can reuse it. Meta Apps support multiple products. Just add the OAuth redirect URI and ensure the permissions above are granted. Your WhatsApp/Instagram setup will not be affected.</p>

        <p className="font-semibold text-gray-800 mt-3">Disconnecting Meta</p>
        <p>If you disconnect your Meta account, all previously synced campaign data, ad insights, and lead attribution records are preserved. Only the OAuth tokens are removed. You can reconnect at any time without losing historical analytics.</p>

        <p className="font-semibold text-gray-800 mt-3">Lead Form Mapping</p>
        <p>Each Meta lead form has fields (name, email, phone, etc.) that need to be mapped to OpsNest lead fields. The system auto-suggests mappings, but you can customize them. Enable <strong>Auto Sync</strong> on each form to automatically import new leads.</p>

        <p className="font-semibold text-gray-800 mt-3">How Lead Sync Works</p>
        <p>Leads from Meta forms are imported automatically in two ways:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Real-time webhook:</strong> When a lead fills your form, Meta sends an instant notification and OpsNest imports that lead within seconds</li>
          <li><strong>Background sync:</strong> A background task runs every 15 minutes as a safety net, pulling any leads that may have been missed by the webhook (e.g. during downtime)</li>
        </ul>
        <p className="mt-1">If a lead with the same phone number or email already exists in your CRM, the import updates their Meta attribution and lead ID rather than creating a duplicate. Phone deduplication handles country-code variants (e.g. <code className="bg-gray-100 px-1 rounded text-xs">+91 99608…</code> and <code className="bg-gray-100 px-1 rounded text-xs">99608…</code> are treated as the same number). Importing a form with one invalid lead no longer blocks the rest of the batch &mdash; valid leads are always saved.</p>

        <p className="font-semibold text-gray-800 mt-3">Campaign Tracking</p>
        <p>The <strong>Campaigns</strong> tab shows all your Meta campaigns with performance metrics: impressions, clicks, CTR, spend, CPC, and conversions. Click to drill down into ad sets and individual ads. A daily sync automatically fetches the latest spend, impressions, and clicks insights for the last 7 days.</p>

        <p className="font-semibold text-gray-800 mt-3">Lead Attribution</p>
        <p>When leads are imported from Meta, OpsNest automatically captures which campaign, ad set, and ad generated the lead. This attribution data appears on the lead detail page and in reports.</p>

        <p className="font-semibold text-gray-800 mt-3">Conversions API (CAPI)</p>
        <p>OpsNest sends conversion events back to Meta when leads progress through your pipeline. This helps Meta's algorithm optimize for lead <em>quality</em>, not just form fills.</p>

        <p className="font-semibold text-gray-700 mt-2">Events sent to Meta:</p>
        <div className="overflow-x-auto mt-1">
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">Event</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">Pipeline Stage</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">What it tells Meta</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="px-3 py-1.5">Lead</td><td className="px-3 py-1.5">New</td><td className="px-3 py-1.5">Lead received from form. Confirms delivery.</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">Contact</td><td className="px-3 py-1.5">Contacted</td><td className="px-3 py-1.5">First human contact made. Engagement quality signal.</td></tr>
              <tr><td className="px-3 py-1.5">QualifiedLead</td><td className="px-3 py-1.5">Qualified</td><td className="px-3 py-1.5">Lead qualified. Strong signal — helps Meta find similar leads.</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">Schedule</td><td className="px-3 py-1.5">Demo Scheduled</td><td className="px-3 py-1.5">Demo booked. High-intent signal.</td></tr>
              <tr><td className="px-3 py-1.5">InitiateCheckout</td><td className="px-3 py-1.5">Proposal Sent</td><td className="px-3 py-1.5">Proposal sent. Purchase intent.</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">Purchase</td><td className="px-3 py-1.5">Won</td><td className="px-3 py-1.5">Deal closed with value. ROAS optimization.</td></tr>
            </tbody>
          </table>
        </div>

        <p className="font-semibold text-gray-700 mt-3">Per-event controls</p>
        <p>In <strong>Settings &rarr; Integrations &rarr; Meta CAPI &rarr; Event Controls</strong>, you can enable or disable each event individually. This gives you control over which signals Meta receives:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Disable low-quality events (e.g., &ldquo;Lead&rdquo; for high-volume but low-conversion forms) to improve optimization signal</li>
          <li>Enable only downstream events (e.g., &ldquo;QualifiedLead&rdquo; and &ldquo;Purchase&rdquo;) to train Meta&apos;s algorithm on actual business outcomes</li>
          <li>The &ldquo;Purchase&rdquo; event sends deal value, enabling Meta&apos;s ROAS (Return on Ad Spend) optimization</li>
          <li>All events are enabled by default</li>
        </ul>

        <p className="font-semibold text-gray-700 mt-3">How CAPI works with the Pixel ID</p>
        <p>The Pixel ID configured in <strong>Settings &rarr; Integrations</strong> is the bridge between your server-side events and Meta&apos;s ad optimization. CAPI events are matched to Meta ad clicks via the Pixel ID + hashed user data (email, phone). This complements the browser-side Meta Pixel by providing server-side confirmation that cannot be blocked by ad blockers or browser privacy settings.</p>

        <p className="font-semibold text-gray-800 mt-3">Multi-Account Support</p>
        <p>OpsNest supports connecting multiple Meta Ad accounts for businesses that manage ads across several accounts or brands.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Account selector:</strong> Use the account dropdown on the Ad Analytics page to switch between connected accounts</li>
          <li><strong>Independent sync:</strong> Each account syncs campaigns, ad sets, and insights independently with its own credentials</li>
          <li><strong>Combined view:</strong> Select &ldquo;All Accounts&rdquo; in the dropdown to see combined totals across all connected accounts</li>
          <li><strong>Per-account filtering:</strong> Filter campaigns, spend, and leads by a specific ad account for focused analysis</li>
        </ul>
      </HelpSection>

      {/* ── Email ── */}
      <HelpSection title="Email">
        <p>Send emails to leads using templates or custom HTML. Track opens and clicks.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Templates:</strong> Create reusable email templates with variables like {'{{ lead_name }}'}, {'{{ lead_company }}'}, {'{{ sender_name }}'}</li>
          <li><strong>Categories:</strong> Organize templates as outreach, follow-up, proposal, or onboarding</li>
          <li><strong>Sending:</strong> Send to individual leads from the Email page or from a lead's detail page</li>
          <li><strong>Tracking:</strong> View open counts and click counts on each sent email</li>
          <li><strong>Provider:</strong> Configure Resend or AWS SES in <strong>Settings &rarr; Email</strong></li>
        </ul>
      </HelpSection>

      {/* ── VoIP Setup Guide ── */}
      <HelpSection title="VoIP Setup Guide (Plivo)">
        <p>OpsNest supports Plivo (primary), Twilio, SignalWire, and FreeSWITCH for VoIP calling. This guide covers Plivo setup for both outbound and inbound calls.</p>

        <p className="font-semibold text-gray-800 mt-3">Step 1: Plivo Account &amp; Phone Number</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Sign up at <strong>console.plivo.com</strong></li>
          <li>Go to <strong>Phone Numbers &rarr; Buy Numbers</strong> &mdash; buy a number with Voice capability</li>
          <li>Note your <strong>Auth ID</strong> and <strong>Auth Token</strong> from the Plivo Console dashboard</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Step 2: Create a Plivo Application</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Go to <strong>Voice &rarr; Applications &rarr; Add New Application</strong></li>
          <li>Name: <em>OpsNest</em> (or any name)</li>
          <li><strong>Answer URL</strong> (POST): <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">https://your-domain.com/api/voip/webhooks/plivo/answer</code></li>
          <li><strong>Hangup URL</strong> (POST): <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">https://your-domain.com/api/voip/webhooks/plivo</code></li>
          <li>Save the application</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Step 3: Assign Application to Phone Number</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Go to <strong>Phone Numbers &rarr; Your Number &rarr; Edit</strong></li>
          <li>Set <strong>Plivo Application</strong> to the application you just created</li>
          <li>This makes inbound calls to your number hit OpsNest&apos;s webhooks</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Step 4: Create an Endpoint (for Browser Calling)</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Go to <strong>Voice &rarr; Endpoints &rarr; Add New Endpoint</strong></li>
          <li>Set a username and password &mdash; these are used by the Plivo Browser SDK to log in</li>
          <li>Note down the endpoint username and password</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Step 5: Configure in OpsNest</p>
        <p>Go to <strong>Settings &rarr; VoIP &rarr; Plivo</strong> and fill in:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Auth ID</strong> &mdash; from Plivo Console dashboard</li>
          <li><strong>Auth Token</strong> &mdash; from Plivo Console dashboard</li>
          <li><strong>Phone Number</strong> &mdash; the number you bought (e.g. +914871XXXXXX)</li>
          <li><strong>Endpoint Username</strong> &mdash; from the endpoint you created</li>
          <li><strong>Endpoint Password</strong> &mdash; from the endpoint you created</li>
        </ul>
        <p className="mt-1">Under <strong>Settings &rarr; VoIP &rarr; Webhooks &amp; Storage</strong>, set the <strong>Webhook Base URL</strong> to your public domain (or ngrok URL for local dev).</p>

        <p className="font-semibold text-gray-800 mt-3">How Calls Work</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Outbound:</strong> Click the phone icon on a lead &rarr; browser SDK connects via WebRTC &rarr; Plivo bridges to the lead&apos;s phone number &rarr; call is recorded and logged</li>
          <li><strong>Inbound (known lead):</strong> Lead calls your number &rarr; system matches caller ID &rarr; VIP leads (HOT/score &gt; 80) skip IVR and ring the owner directly &rarr; normal leads ring their assigned owner</li>
          <li><strong>Inbound (unknown):</strong> Unknown caller &rarr; dynamic IVR menu plays (&ldquo;Press 1 for Sales...&rdquo;) &rarr; routes to team or user based on option config</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Call Features</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Call Recording:</strong> Calls are recorded automatically and stored to S3 (when configured) or locally. Listen to recordings from the call history or lead detail page. Configure storage in <strong>Settings &rarr; VoIP &rarr; Recording Storage</strong>.</li>
          <li><strong>Call Logging:</strong> Every completed call automatically creates a call log entry linked to the lead.</li>
          <li><strong>Caller ID Lookup:</strong> Inbound calls are matched against both the lead&apos;s primary phone and secondary phone numbers (with and without country prefix).</li>
          <li><strong>Sequential Ring:</strong> If the first agent doesn&apos;t answer, the system tries the next available agent in the team.</li>
          <li><strong>Voicemail Fallback:</strong> If no agent is available, callers can leave a voicemail.</li>
          <li><strong>Connection Monitoring:</strong> During active calls the browser monitors ICE connectivity. If the network drops silently (no audio but call stays &ldquo;connected&rdquo;), a <strong>&ldquo;Connection unstable&rdquo;</strong> warning appears. If the connection does not recover within 10 seconds, the call is automatically terminated so it is not stuck in a zombie state.</li>
          <li><strong>Proactive Re-registration:</strong> Before every outbound call, the system checks how long it has been since the Plivo session was last registered. Sessions older than 30 minutes are re-registered before dialing to prevent silent &ldquo;registered but unable to call&rdquo; failures.</li>
          <li><strong>Concurrent Inbound Handling:</strong> If a second inbound call arrives while an agent is already on a call, it is automatically rejected and a missed-call notification is created. The agent sees the notification after finishing the current call so they can follow up.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Call Notes Panel</p>
        <p>During an active call, a slide-out notes panel appears on the right side of the VoIP page. Notes auto-save every few seconds while you type. When the call ends or you close the panel, the notes are automatically copied to the lead&apos;s permanent note history.</p>

        <p className="font-semibold text-gray-800 mt-3">Post-Call Rating</p>
        <p>After every call ends, you are prompted to log the outcome and rate the call quality:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Outcome:</strong> Resolved, Escalated, Transferred, Voicemail, Abandoned</li>
          <li><strong>Quality Score:</strong> 1-5 stars</li>
          <li><strong>Outcome Notes:</strong> Free text for additional context</li>
        </ul>
        <p className="mt-1">On the VoIP page this appears inline. On other pages it appears as a pop-up modal when a call ends in the background. Ratings are stored on the call record and visible in call history.</p>

        <p className="font-semibold text-gray-800 mt-3">Unknown Caller Lead Creation</p>
        <p>When an inbound call comes from an unrecognized number, a &ldquo;Create Lead&rdquo; prompt appears after the call ends. Clicking it creates a new lead pre-filled with the caller&apos;s phone number, links all past calls from that number to the new lead, and copies any notes taken during the call into the lead&apos;s note history.</p>

        <p className="font-semibold text-gray-800 mt-3">Active Call Bar</p>
        <p>An active call bar appears at the bottom of every page while a call is in progress. Click the bar to navigate to the VoIP page. You can minimize it to a small pill and restore it by clicking the pill. All calls must be managed from the VoIP page.</p>

        <p className="font-semibold text-gray-800 mt-3">VoIP Endpoint Provisioning</p>
        <p>Plivo SIP endpoints can be automatically provisioned for agents so they can make and receive browser-based calls.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Bulk provisioning:</strong> On the <strong>Users</strong> page, click <strong>Provision VoIP</strong> to select users and create their Plivo SIP endpoints in one step</li>
          <li><strong>Per-user provisioning:</strong> Open a user&apos;s edit modal and provision their endpoint individually</li>
          <li><strong>Auto-provisioning:</strong> When new users are added, SIP endpoints are created for them automatically</li>
          <li>Admin-only feature &mdash; only admins can provision or manage endpoints</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Call Cost Tracking</p>
        <p>OpsNest automatically tracks call costs by syncing billing data from Plivo&apos;s CDR (Call Detail Records). Costs are stored in INR on each call record.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Automatic sync:</strong> A daily Celery task fetches call costs from Plivo for recent calls</li>
          <li><strong>Cost reports:</strong> View call costs in the <strong>Reports</strong> page &mdash; grand total, per-agent breakdown, per-team breakdown, and daily timeline</li>
          <li><strong>Per-call cost:</strong> Each call record shows its cost in the call history and lead detail page</li>
          <li><strong>Backfill:</strong> Use <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">POST /api/voip/backfill-recordings</code> to retroactively fetch costs and recordings for historical calls</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Recording Backfill</p>
        <p>If call recordings were missed (e.g. due to a webhook issue), admins can backfill them from Plivo&apos;s CDR (Call Detail Records). The system fetches recordings from Plivo and links them to the matching call records in OpsNest.</p>
      </HelpSection>

      {/* ── Teams ── */}
      <HelpSection title="Teams &amp; Call Routing">
        <p>Teams organize your users into groups (Sales, Support, Accounts, etc.) for call routing, workload management, and performance tracking.</p>

        <p className="font-semibold text-gray-800 mt-3">Quick Setup</p>
        <p>Go to <strong>Teams</strong> in the sidebar and click <strong>Load Defaults</strong> to create default Sales and Support teams. Then add your users as members.</p>

        <p className="font-semibold text-gray-800 mt-3">Team Configuration</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Team Type:</strong> Sales, Support, Accounts, or Custom</li>
          <li><strong>Routing Strategy:</strong> How inbound calls are distributed to team members (see below)</li>
          <li><strong>Routing Priority:</strong> Lower number = this team is tried first when multiple teams could handle a call</li>
          <li><strong>Max Concurrent Calls:</strong> Team-wide cap on simultaneous calls</li>
          <li><strong>Fallback Action:</strong> What happens when no one is available &mdash; voicemail or callback request</li>
          <li><strong>Fallback Team:</strong> Overflow to another team before falling back to voicemail</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Routing Strategies</p>
        <div className="space-y-2 ml-2">
          <div><HelpBadge color="blue">Round Robin</HelpBadge> &mdash; Each call goes to the agent who hasn&apos;t received a call the longest. Fair distribution. <em>Best for support teams.</em></div>
          <div><HelpBadge color="purple">Least Busy</HelpBadge> &mdash; Routes to the agent with fewest active calls. <em>Best during high-volume peaks.</em></div>
          <div><HelpBadge color="green">Skill Based</HelpBadge> &mdash; Matches calls to agents with specific skills (e.g. billing, POS, technical). Falls back to least busy if no skill match. <em>Best for specialized support.</em></div>
          <div><HelpBadge color="amber">Random</HelpBadge> &mdash; Random selection from available agents. <em>Rarely used, mainly for A/B testing.</em></div>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Team Members</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Roles:</strong> Member (receives calls), Lead (manages members + receives calls), Manager (full team control)</li>
          <li><strong>Skills:</strong> Tags like &ldquo;billing&rdquo;, &ldquo;technical&rdquo;, &ldquo;onboarding&rdquo; used by skill-based routing</li>
          <li><strong>Max Concurrent Calls:</strong> Per-agent cap (e.g. max 2 simultaneous calls)</li>
          <li><strong>Availability:</strong> Online, Offline, On Call, Break &mdash; only &ldquo;Online&rdquo; agents receive routed calls</li>
          <li>A user can belong to multiple teams (e.g. both Sales and Support)</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Availability Toggle</p>
        <p>Agents can set their availability from the <strong>colored status indicator</strong> in the top-right header bar. Click it to toggle between Online (green), Offline (gray), and Break (yellow). This updates availability across all teams the agent belongs to.</p>

        <p className="font-semibold text-gray-800 mt-3">Change History</p>
        <p>All team changes are tracked with immutable audit logs:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Member History:</strong> Who was added/removed, role changes, availability changes &mdash; with timestamps and who made the change</li>
          <li><strong>Config History:</strong> Routing strategy changes, priority changes, fallback changes &mdash; with old and new values</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Teams Are Optional</p>
        <p>You don&apos;t need to create teams to use VoIP. Without teams, outbound calls work normally and IVR menus can route directly to individual users using <strong>Route to User</strong>. Teams add availability-aware routing, load balancing, and sequential ring.</p>
      </HelpSection>

      {/* ── IVR Menus ── */}
      <HelpSection title="IVR Menus (Interactive Voice Response)">
        <p>IVR menus define what callers hear when they call your phone number and how their keypad presses route the call.</p>

        <p className="font-semibold text-gray-800 mt-3">Quick Setup</p>
        <p>Go to <strong>Settings &rarr; IVR Menus</strong> and click <strong>Load Defaults</strong> to create a &ldquo;Main Menu&rdquo; with Press 1 for Sales, Press 2 for Support. If teams exist, options auto-link to them. Otherwise, options route to the admin user directly.</p>

        <p className="font-semibold text-gray-800 mt-3">Menu Configuration</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Name:</strong> Internal name (e.g. &ldquo;Main Menu&rdquo;, &ldquo;After Hours&rdquo;)</li>
          <li><strong>Phone Number:</strong> The inbound DID this menu serves. When a call comes in to this number, this IVR menu plays.</li>
          <li><strong>Welcome Message:</strong> What callers hear first (text-to-speech)</li>
          <li><strong>Timeout:</strong> Seconds to wait for input before repeating or falling back</li>
          <li><strong>Fallback Action:</strong> What happens after max retries with no input &mdash; voicemail, transfer, or hangup</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">IVR Options (Digit Mapping)</p>
        <p>Each option maps a keypad digit (0-9, *, #) to an action:</p>
        <div className="space-y-2 ml-2 mt-1">
          <div><HelpBadge color="blue">Route to Team</HelpBadge> &mdash; Routes the caller to a team. The routing engine picks the best available agent based on the team&apos;s strategy (round robin, least busy, etc.). If no one answers, tries the next agent. If all busy, falls back to voicemail.</div>
          <div><HelpBadge color="green">Route to User</HelpBadge> &mdash; Routes directly to a specific user&apos;s browser. No team or routing engine needed. If the user doesn&apos;t answer, goes to voicemail. <em>Best for small teams or personal extensions.</em></div>
          <div><HelpBadge color="purple">Sub-menu</HelpBadge> &mdash; Navigates to another IVR menu (nested menus). E.g. &ldquo;Press 3 for more options&rdquo; &rarr; opens a second menu.</div>
          <div><HelpBadge color="amber">Transfer</HelpBadge> &mdash; Transfers the call to an external phone number (e.g. +914871234567). The call leaves your system. <em>Use for external offices, partners, or on-call numbers.</em></div>
          <div><HelpBadge color="red">Voicemail</HelpBadge> &mdash; Records a voicemail message. The recording is saved and linked to the call record.</div>
        </div>

        <p className="font-semibold text-gray-800 mt-3">You Can Mix Actions</p>
        <p>Within the same IVR menu, different digits can use different action types:</p>
        <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mt-1 space-y-0.5">
          <p>Press 1 &rarr; Route to Team (Sales team, round-robin)</p>
          <p>Press 2 &rarr; Route to User (Priya, direct)</p>
          <p>Press 3 &rarr; Route to Team (Support team, skill-based)</p>
          <p>Press 4 &rarr; Transfer (+914871234567, external accountant)</p>
          <p>Press 5 &rarr; Voicemail</p>
        </div>

        <p className="font-semibold text-gray-800 mt-3">VIP Routing (Automatic)</p>
        <p>Known leads with <HelpBadge color="red">HOT</HelpBadge> quality segment or lead score above 80 <strong>skip the IVR entirely</strong>. They are routed directly to their assigned owner. If the owner is offline, the call goes to the owner&apos;s team. This is automatic &mdash; no configuration needed.</p>

        <p className="font-semibold text-gray-800 mt-3">How IVR Connects to Plivo</p>
        <p>The IVR menu&apos;s <strong>Phone Number</strong> field must match the number configured in your Plivo Application&apos;s Answer URL. When Plivo receives an inbound call, it hits your Answer URL. OpsNest looks up the IVR menu for that phone number and returns the appropriate voice response.</p>

        <p className="font-semibold text-gray-800 mt-3">What Happens Without an IVR Menu?</p>
        <p>If no IVR menu is configured for the inbound phone number:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Known leads</strong> are still routed correctly via caller ID lookup &mdash; IVR is only for unknown callers</li>
          <li><strong>Unknown callers</strong> are routed directly to the admin user&apos;s browser. If the admin is unavailable, the call goes to voicemail.</li>
          <li>This ensures no call is ever stuck in a dead loop &mdash; every inbound call either reaches someone or gets a voicemail prompt</li>
          <li>To add proper IVR routing, go to <strong>Settings &rarr; IVR Menus</strong> and create a menu or click <strong>Load Defaults</strong></li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Default IVR Menu (Multi-DID Safety)</p>
        <p>If you have multiple phone numbers (DIDs), you can mark one IVR menu as the <strong>default</strong>. When an inbound call arrives on a phone number that does not match any IVR menu, the default menu is used instead of falling back to the admin user directly.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Set the default flag when creating or editing an IVR menu in <strong>Settings &rarr; IVR Menus</strong></li>
          <li>Only one menu can be the default at a time &mdash; setting a new default clears the previous one</li>
          <li>This prevents unknown callers from reaching voicemail or the admin user when a new DID is added but not yet configured</li>
        </ul>
      </HelpSection>

      {/* ── VoIP Calls ── */}
      <HelpSection title="VoIP Call History &amp; Tracking">
        <p>All VoIP calls (inbound and outbound) are tracked with full lifecycle details.</p>

        <p className="font-semibold text-gray-800 mt-3">Call History Filters (New)</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Agent filter:</strong> Admin/manager can filter by specific agent to see only their calls</li>
          <li><strong>Direction filter:</strong> Filter by Inbound or Outbound calls</li>
          <li><strong>Provider filter:</strong> Filter by VoIP provider (Plivo, Twilio, etc.)</li>
          <li><strong>Status filter:</strong> Filter by call status (Completed, Failed, etc.)</li>
          <li><strong>Date range:</strong> Filter by date from/to</li>
          <li><strong>Export CSV:</strong> Admin/manager can export the current filtered call history as CSV. The export includes agent name, lead name, duration, outcome, cost, and timestamps.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Call Status Reference</p>
        <p>Each call moves through a series of statuses. Understanding these helps you diagnose call issues:</p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Meaning</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Common Cause</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="px-3 py-1.5"><HelpBadge color="blue">Initiated</HelpBadge></td><td className="px-3 py-1.5">Call record created, waiting for provider</td><td className="px-3 py-1.5">Agent clicked Call but the browser SDK hasn&apos;t connected yet. If stuck here, the SDK may have failed silently (network issue, provider error).</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5"><HelpBadge color="purple">Ringing</HelpBadge></td><td className="px-3 py-1.5">Phone is ringing at the destination</td><td className="px-3 py-1.5">Outbound: lead&apos;s phone is ringing. Inbound: agent&apos;s browser is ringing. For team-routed calls, may retry multiple agents (sequential ring).</td></tr>
              <tr><td className="px-3 py-1.5"><HelpBadge color="amber">In Progress</HelpBadge></td><td className="px-3 py-1.5">Call is connected and active</td><td className="px-3 py-1.5">Both parties are on the line. Duration timer is running.</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5"><HelpBadge color="green">Completed</HelpBadge></td><td className="px-3 py-1.5">Call ended normally after being answered</td><td className="px-3 py-1.5">Successful call. A call log entry is auto-created and linked to the lead. Recording is saved.</td></tr>
              <tr><td className="px-3 py-1.5"><HelpBadge color="red">Failed</HelpBadge></td><td className="px-3 py-1.5">Call could not connect</td><td className="px-3 py-1.5">Network error, invalid number, provider rejection, or caller hung up during dial. Also set when browser crashes during a call or stale calls are cleaned up.</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5"><HelpBadge color="amber">No Answer</HelpBadge></td><td className="px-3 py-1.5">Phone rang but nobody picked up</td><td className="px-3 py-1.5">The call reached the destination but wasn&apos;t answered before timeout. For team calls, this means all agents in the team were tried and none answered.</td></tr>
              <tr><td className="px-3 py-1.5"><HelpBadge>Busy</HelpBadge></td><td className="px-3 py-1.5">Destination was busy</td><td className="px-3 py-1.5">The lead&apos;s phone line was occupied. Distinct from &ldquo;No Answer&rdquo; &mdash; the call was actively rejected by the network.</td></tr>
            </tbody>
          </table>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Outbound Call Lifecycle</p>
        <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mt-1">
          <p>Initiated &rarr; Ringing &rarr; In Progress &rarr; Completed</p>
          <p className="text-gray-400 mt-0.5">  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;  &darr; (no answer) &rarr; No Answer</p>
          <p className="text-gray-400">  &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;  &darr; (busy) &rarr; Busy</p>
          <p className="text-gray-400">  &nbsp;&nbsp;&darr; (error) &rarr; Failed</p>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Inbound Call Lifecycle</p>
        <div className="bg-gray-50 rounded-lg p-3 font-mono text-xs mt-1">
          <p>Ringing &rarr; In Progress &rarr; Completed</p>
          <p className="text-gray-400 mt-0.5">  &nbsp;&nbsp;&darr; (caller hangs up) &rarr; Failed</p>
          <p className="text-gray-400">  &nbsp;&nbsp;&darr; (no agent answers) &rarr; No Answer &rarr; Voicemail</p>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Why Some Calls Stay in &ldquo;Initiated&rdquo;</p>
        <p>A call stuck in &ldquo;Initiated&rdquo; means the browser SDK failed to connect to the VoIP provider after the call record was created. Common causes:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Browser tab was closed or crashed before the SDK could dial</li>
          <li>Network interruption between the browser and Plivo</li>
          <li>Contact masking sent an invalid number to the SDK (fixed in latest update)</li>
          <li>Plivo rejected the call at the SIP level (geo permissions, invalid caller ID)</li>
        </ul>
        <p className="mt-1">Stale calls (&gt; 30 minutes old) in &ldquo;Initiated&rdquo; or &ldquo;Ringing&rdquo; are automatically cleaned up and marked as &ldquo;Failed&rdquo;.</p>

        <p className="font-semibold text-gray-800 mt-3">Incoming Call Notifications</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Known leads:</strong> Popup and browser notification show the <strong>lead name</strong> prominently with the phone number below</li>
          <li><strong>Unknown callers:</strong> Popup shows the phone number only</li>
          <li><strong>Masking:</strong> When contact masking is enabled, the phone number is masked in both the popup and the notification (e.g. <code className="bg-gray-100 px-1 rounded text-xs">+91******3210</code>)</li>
          <li><strong>Caller lookup:</strong> The system does a real-time API lookup when an inbound call arrives to match the caller to a lead in your CRM</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Call Outcome Tiers</p>
        <p>Every completed call is automatically assigned an outcome tier based on how long the conversation lasted. This gives you a richer picture of call quality beyond just &ldquo;completed&rdquo; or &ldquo;missed&rdquo;:</p>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Tier</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">Duration</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-700">What it means</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="px-3 py-1.5"><HelpBadge>No Answer</HelpBadge></td><td className="px-3 py-1.5">0 s</td><td className="px-3 py-1.5">Call rang but nobody picked up, or was busy/abandoned</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5"><HelpBadge color="blue">Answered</HelpBadge></td><td className="px-3 py-1.5">1&ndash;10 s</td><td className="px-3 py-1.5">Picked up but ended very quickly &mdash; wrong number or immediate hang-up</td></tr>
              <tr><td className="px-3 py-1.5"><HelpBadge color="green">Connected</HelpBadge></td><td className="px-3 py-1.5">11&ndash;60 s</td><td className="px-3 py-1.5">A real conversation started but was brief</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5"><HelpBadge color="purple">Interacted</HelpBadge></td><td className="px-3 py-1.5">&gt;60 s</td><td className="px-3 py-1.5">Substantive conversation &mdash; the lead was engaged</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-2">Outcome tiers appear as coloured badges in call history, the Dashboard &ldquo;Today&apos;s Calls&rdquo; card (with a progress bar breakdown), and Team Analytics. Missed, busy, and abandoned calls now always create a call log entry so the full contact history is visible even when nobody answered.</p>

        <p className="font-semibold text-gray-800 mt-3">Other Features</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Post-Call Outcomes:</strong> Resolved, Escalated, Transferred, Voicemail, Abandoned &mdash; log these via the post-call rating prompt after each call</li>
          <li><strong>Recording Playback:</strong> Click the play button on any call to listen to the recording</li>
          <li><strong>Auto Call Log:</strong> When a call completes (or is missed/busy/abandoned), a call log entry is automatically created and linked to the lead</li>
          <li><strong>Manual Call Logs:</strong> Reps can log calls made outside the system (e.g. from a mobile phone). Manual entries show a pencil icon; VoIP calls show a phone icon. Direction badges (<strong>IN</strong> / <strong>OUT</strong>) show call direction in the call history.</li>
          <li><strong>Routing Audit:</strong> For team-routed calls, every routing attempt is logged &mdash; which agents were tried, who answered, who was skipped and why</li>
          <li><strong>Point-in-Time Snapshots:</strong> Every routed call captures a frozen snapshot of the routing context at call time: team name, routing strategy, team size, online members, and the agent&apos;s role</li>
        </ul>
      </HelpSection>

      {/* ── Browser Notifications ── */}
      <HelpSection title="Browser Notifications">
        <p>OpsNest can send real-time browser notifications for important events (new leads, inbound calls, assignment changes, etc.) so you don&apos;t miss activity while working in another tab.</p>

        <p className="font-semibold text-gray-800 mt-2">Setup</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Settings &rarr; Notifications</strong></li>
          <li>Click <strong>Enable browser notifications</strong></li>
          <li>Accept the browser permission prompt</li>
        </ol>
        <p className="mt-1">If notifications are blocked by the browser, update your browser&apos;s site permissions for OpsNest and then retry.</p>

        <p className="font-semibold text-gray-800 mt-3">How It Works</p>
        <p>The page polls for new notifications in the background (no page refresh needed). Each browser tab coordinates via a shared channel so only one tab shows each notification. Unread notification count appears in the bell icon in the header.</p>
      </HelpSection>

      {/* ── Reports ── */}
      <HelpSection title="Dashboard & Reports">
        <p>The <strong>Dashboard</strong> shows a real-time snapshot of your sales pipeline (last 30 days). The <strong>Reports</strong> page provides deeper analytics with customizable time periods.</p>

        <p className="font-semibold text-gray-800 mt-2">Key Metrics</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Pipeline Funnel:</strong> Visual breakdown of leads at each stage with conversion rates</li>
          <li><strong>Lead Quality:</strong> Score distribution gauge, HOT/WARM/COLD segment breakdown</li>
          <li><strong>Activity:</strong> Calls, emails, and notes by action type with daily heatmap</li>
          <li><strong>Lead Sources:</strong> Which sources (Meta Ads, organic, referral) produce the best leads</li>
          <li><strong>Revenue:</strong> Total revenue, deal count, avg deal size, monthly trends with growth indicators</li>
          <li><strong>Conversion Rates:</strong> Stage-to-stage conversion shown as color-coded cards (green &gt;50%, amber &gt;25%, red below)</li>
        </ul>
        <p className="mt-1 text-xs text-gray-500">Hover the <strong>?</strong> icon on any metric card for a tooltip explaining exactly what that data point measures.</p>

        <p className="font-semibold text-gray-800 mt-3">Custom Date Filters</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Reports and Social Analytics pages support custom date ranges</li>
          <li>Select <strong>&ldquo;Custom Range&rdquo;</strong> from the period dropdown to reveal date pickers for start and end dates</li>
          <li>All report metrics &mdash; lead counts, stage history, pipeline funnel &mdash; are now correctly filtered by the selected date range</li>
          <li>Filters persist in the URL &mdash; share the link with a colleague to show the exact same filtered view</li>
          <li>URL format: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">?period=custom&amp;date_from=2024-01-01&amp;date_to=2024-12-31</code></li>
        </ul>
      </HelpSection>

      {/* ── Social Analytics ── */}
      <HelpSection title="Social Analytics">
        <p><strong>Social Analytics</strong> gives you a unified view of how your social media content is performing across platforms. Access it from <strong>Social Analytics</strong> under the Insights section in the sidebar.</p>

        <p className="font-semibold text-gray-800 mt-3">Connecting Your Accounts</p>
        <p>Social Analytics uses your existing Meta connection (set up in <strong>Meta Ads</strong>) to pull Instagram data. No additional credentials are needed &mdash; if your Meta account is connected and has an Instagram Business or Creator account linked, Instagram metrics are available automatically.</p>

        <p className="font-semibold text-gray-800 mt-3">Dashboard Overview</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Cross-Platform KPIs:</strong> Top-level cards showing total followers, engagement rate, impressions, and reach across all connected platforms</li>
          <li><strong>Platform Cards:</strong> Each connected platform has its own card with platform-specific metrics (followers, posts, engagement, growth)</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Drill-Down Views</p>
        <p>Click into a platform card to access detailed analytics:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Trends:</strong> Line charts showing follower growth, engagement, reach, and impressions over time</li>
          <li><strong>Top &amp; Bottom Posts:</strong> Your best and worst performing posts ranked by engagement</li>
          <li><strong>Post Type Breakdown:</strong> Performance comparison across post types (image, video, carousel, reel, story)</li>
          <li><strong>Best Posting Times:</strong> Heatmap showing when your audience is most active and which posting times drive the highest engagement</li>
          <li><strong>Hashtag Performance:</strong> Which hashtags drive the most reach and engagement, sorted by effectiveness</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Data Sync</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Automatic sync:</strong> Data is refreshed daily at 03:30 UTC</li>
          <li><strong>Manual sync:</strong> Click the sync button on any connected platform to pull the latest data immediately</li>
          <li>Sync status and last sync time are shown on each platform card</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Audience Demographics</p>
        <p>The <strong>Audience</strong> tab on the platform detail view shows follower demographics for connected Instagram accounts. Requires the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">instagram_manage_insights</code> permission.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Gender distribution:</strong> Percentage bars showing male, female, and other follower breakdown</li>
          <li><strong>Age groups:</strong> Breakdown chart of follower age ranges (13-17, 18-24, 25-34, 35-44, 45-54, 55-64, 65+)</li>
          <li><strong>Top cities:</strong> The 10 cities with the most followers, with counts and percentages</li>
          <li><strong>Top countries:</strong> The 10 countries with the most followers, with counts and percentages</li>
        </ul>
      </HelpSection>

      {/* ── Companies ── */}
      <HelpSection title="Companies">
        <p>Track the organizations your leads belong to. Companies aggregate lead data and provide an account-level view.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Create companies with name, website, industry, size, and location</li>
          <li>View all leads associated with a company</li>
          <li>See average lead score and pipeline summary per company</li>
          <li>Leads can be linked to companies for account-based selling</li>
          <li><strong>Company Contacts:</strong> Designate a <strong>primary contact</strong> and <strong>secondary contact</strong> for each company from its associated leads. Primary contacts appear first in company views and are used for outreach.</li>
        </ul>
      </HelpSection>

      {/* ── Tasks ── */}
      <HelpSection title="Tasks">
        <p>Create and manage tasks for your team. Tasks can be linked to leads and assigned to specific team members.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Types:</strong> Call, email, follow-up, demo, proposal, custom</li>
          <li><strong>Priorities:</strong> Low, medium, high, urgent</li>
          <li><strong>Statuses:</strong> Pending, completed, overdue, cancelled</li>
          <li><strong>Recurring:</strong> Set tasks to repeat on a schedule (e.g., weekly on Mon/Wed/Fri)</li>
          <li><strong>Overdue tracking:</strong> Overdue tasks show a red badge count in the sidebar</li>
          <li><strong>Task Notes:</strong> Add append-only notes to any task for context, updates, or handoff details</li>
          <li><strong>Task Status History:</strong> Every status change (pending &rarr; completed, etc.) is recorded with timestamp and user, providing a full audit trail</li>
        </ul>
      </HelpSection>

      {/* ── CallPulse ── */}
      <HelpSection title="CallPulse &mdash; Guided Call Discovery">
        <p>CallPulse helps your team collect structured insights during sales calls without sounding scripted. Questions appear as &ldquo;Discussion Topics&rdquo; &mdash; conversation guides, not rigid scripts.</p>

        <p className="font-semibold text-gray-800 mt-2">How it works</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Admins create playbooks in <strong>Settings &rarr; CallPulse</strong> with questions for different call scenarios</li>
          <li><strong>Auto-trigger:</strong> When a VoIP call is answered (lead picks up), CallPulse automatically opens with the matching playbook</li>
          <li><strong>Manual trigger:</strong> Click &ldquo;CallPulse&rdquo; during a call from the active call widget or the bottom call bar. Use the dropdown arrow to choose a specific playbook</li>
          <li>The rep fills in answers during or after the call &mdash; answers auto-save on each field</li>
          <li>Click &ldquo;Submit&rdquo; when done. Responses are linked to the lead and the call record</li>
          <li>Past responses are visible on the lead&apos;s profile under &ldquo;CallPulse Responses&rdquo;</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Playbook Matching</p>
        <p>The system automatically selects the right playbook based on:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Call Number:</strong> which call this is (1st, 2nd, etc.) &mdash; counted from the lead&apos;s call log history</li>
          <li><strong>Pipeline Stage:</strong> new, contacted, qualified, demo_scheduled, proposal_sent, negotiation</li>
          <li><strong>Quality Segment:</strong> HOT, WARM, COLD</li>
          <li><strong>Score Range:</strong> minimum/maximum lead score</li>
          <li><strong>Priority:</strong> when multiple playbooks match, the highest priority wins</li>
          <li><strong>Fallback:</strong> if no playbook matches and the lead has never completed a CallPulse session, the lowest-priority playbook (General) is used automatically</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Default Playbooks</p>
        <p>Click <strong>&ldquo;Load Defaults&rdquo;</strong> in the CallPulse tab to set up pre-configured playbooks. You can select specific ones to load:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>First Call &mdash; Discovery</strong> (5 questions) &mdash; business overview, order volume, pain points, competition, timeline. Triggers on 1st call.</li>
          <li><strong>Follow-Up &mdash; Deepening</strong> (4 questions) &mdash; recap, success criteria, blockers (multiselect), decision authority. Triggers on 2nd-3rd call for contacted/qualified leads.</li>
          <li><strong>Demo &amp; Proposal</strong> (3 questions) &mdash; demo priorities, pricing feedback, next steps. Triggers for demo_scheduled/proposal_sent stages.</li>
          <li><strong>Closing &amp; Negotiation</strong> (3 questions) &mdash; 1-10 confidence scale, final blocker, yes/no close. Triggers for negotiation stage.</li>
          <li><strong>General &mdash; Quick Check-in</strong> (6 questions) &mdash; call purpose, key insight, engagement level, blockers, next step, business context. Works for any call at any stage.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Using the General Playbook</p>
        <p>The General playbook is designed as a versatile fallback that works for <em>any</em> call:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>It captures 6 key data points in about 60-90 seconds after a call</li>
          <li>3 of 6 questions are single-click (select/multiselect), only 2 require typing</li>
          <li>Use it for check-ins, support calls, upsells, or any call that doesn&apos;t match a stage-specific playbook</li>
          <li>To use manually: click the dropdown arrow next to &ldquo;CallPulse&rdquo; and select &ldquo;General &mdash; Quick Check-in&rdquo;</li>
          <li>If no other playbook matches and it&apos;s the lead&apos;s first CallPulse session, the General playbook loads automatically as a fallback</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Tips</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Questions are framed as &ldquo;Discussion Topics&rdquo; &mdash; guides, not scripts. Reps should use natural conversation.</li>
          <li>Reps can skip questions that came up naturally or aren&apos;t relevant</li>
          <li>Answers auto-save on each field &mdash; nothing is lost if the browser crashes or call drops. If you close and reopen CallPulse for the same call, it resumes the in-progress session automatically.</li>
          <li>Each question has helper text (visible to the rep) explaining the strategic intent</li>
          <li>Playbooks are versioned &mdash; editing a playbook creates a new version, old responses stay linked to the version they were answered against</li>
          <li>Deleted playbooks can be re-loaded from defaults at any time. Draft response sets are soft-deleted (recoverable) rather than permanently removed.</li>
          <li>For <strong>unknown callers</strong> (no matching lead), CallPulse shows a &ldquo;Create Lead&rdquo; step first. The playbook only opens after a lead is created or linked.</li>
          <li>CallPulse responses feed into future AI-powered conversion intelligence (lead scoring, sentiment analysis)</li>
        </ul>
      </HelpSection>

      {/* ── Lead Scoring Configuration ── */}
      <HelpSection title="Lead Scoring Configuration">
        <p>Lead scoring is fully configurable. Adjust categories, thresholds, and factor weights in <strong>Settings &rarr; Scoring</strong> (admin only).</p>

        <p className="font-semibold text-gray-800 mt-3">Category Budgets</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Default categories: <strong>Fit (40 pts)</strong>, <strong>Engagement (35 pts)</strong>, <strong>Urgency (25 pts)</strong> &mdash; total must equal 100</li>
          <li>Add, rename, or remove categories as needed &mdash; OpsNest will warn you if the budgets don&apos;t sum to 100</li>
          <li>Within each category, individual factor points are normalised to the category&apos;s budget</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Quality Segments (Thresholds)</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Default thresholds: <HelpBadge color="red">HOT</HelpBadge> 75+, <HelpBadge color="amber">WARM</HelpBadge> 45&ndash;74, <HelpBadge color="blue">COLD</HelpBadge> 0&ndash;44</li>
          <li>You can add a 4th tier (e.g. &ldquo;URGENT&rdquo;) with a custom color and threshold range</li>
          <li>Segment thresholds affect VIP call routing and assignment rule filters</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Scoring Factors</p>
        <p>Each category contains factors. Every factor has a toggle, a point value, and a type:</p>
        <div className="space-y-1.5 ml-2 mt-1">
          <div><HelpBadge color="blue">field_present</HelpBadge> &mdash; Awards points when a lead field is non-empty (e.g. has phone number)</div>
          <div><HelpBadge color="blue">field_absent</HelpBadge> &mdash; Awards points when a lead field is empty (e.g. no website = DM-only seller signal)</div>
          <div><HelpBadge color="purple">attribute_match</HelpBadge> &mdash; Awards points when a lead attribute equals one of a set of match values (e.g. <code className="bg-gray-100 px-1 rounded text-xs">product = storepecker</code>)</div>
          <div><HelpBadge color="purple">attribute_threshold</HelpBadge> &mdash; Awards points when a numeric attribute satisfies a comparison (&gt;=, &gt;, &lt;=, &lt;, ==, !=). E.g. <code className="bg-gray-100 px-1 rounded text-xs">call_count &gt;= 2</code></div>
          <div><HelpBadge color="green">attribute_boolean</HelpBadge> &mdash; Awards points when a boolean attribute is true</div>
          <div><HelpBadge color="red">penalty</HelpBadge> &mdash; Subtracts points when the condition is met. Useful for disqualifying signals.</div>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Derived Call Attributes</p>
        <p>The following attributes are computed at score time from call history &mdash; no extra configuration needed. Use them in <code className="bg-gray-100 px-1 rounded text-xs">attribute_threshold</code> factors:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><code className="bg-gray-100 px-1 rounded text-xs">outbound_call_count</code> &mdash; number of outbound calls to the lead</li>
          <li><code className="bg-gray-100 px-1 rounded text-xs">inbound_call_count</code> &mdash; number of inbound calls from the lead</li>
          <li><code className="bg-gray-100 px-1 rounded text-xs">connected_call_count</code> &mdash; calls where the lead actually picked up</li>
          <li><code className="bg-gray-100 px-1 rounded text-xs">days_since_last_call</code> &mdash; days elapsed since the most recent call (any direction)</li>
          <li><code className="bg-gray-100 px-1 rounded text-xs">total_call_duration</code> &mdash; cumulative talk time in seconds across all connected calls</li>
          <li><code className="bg-gray-100 px-1 rounded text-xs">days_since_creation</code> &mdash; days since the lead was created</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Pipeline Stages</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Settings &rarr; Scoring &rarr; Pipeline Stages</strong> tab to add, rename, reorder, or deactivate stages</li>
          <li>Each stage has a slug (URL-safe key), label (display name), color, and position</li>
          <li>Drag rows to reorder &mdash; changes are staged in draft mode and only committed when you click <strong>Save Order</strong></li>
          <li>Mark a stage as <strong>terminal</strong> (Won/Lost) or <strong>engaged</strong> (triggers segment auto-lock)</li>
          <li>Deactivating a stage preserves existing leads at that stage; new leads can no longer be placed there</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Business Types</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Settings &rarr; Scoring &rarr; Business Types</strong> to manage the list of business types available on lead records</li>
          <li>Default types are seeded from the <code className="bg-gray-100 px-1 rounded text-xs">BusinessType</code> enum on first startup</li>
          <li>Admins can add, rename, or remove types &mdash; changes are stored in <code className="bg-gray-100 px-1 rounded text-xs">app_settings</code> and take effect immediately</li>
          <li>Business type can be used as an <code className="bg-gray-100 px-1 rounded text-xs">attribute_match</code> scoring factor &mdash; the match value field has a <strong>searchable dropdown</strong> so you can select types without typing exact strings</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Score at Stage Transition</p>
        <p>Every time a lead moves to a new pipeline stage, its score at that moment is captured and stored alongside the stage change record. You can see this in the stage history on the lead detail page &mdash; it shows what the lead&apos;s score was when it entered each stage, useful for analysing pipeline quality over time.</p>

        <p className="font-semibold text-gray-800 mt-3">Impact Preview</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Before saving a config change, click <strong>Preview Impact</strong> to see how many leads would shift between segments</li>
          <li>The preview runs against your live leads without persisting anything</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Rescore All</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>After saving a new config, click <strong>Rescore All</strong> to immediately apply the new scoring to every active lead</li>
          <li>Rescore runs as a background Celery task &mdash; large pipelines may take a few minutes</li>
          <li>Only one rescore can run at a time &mdash; a second click while a job is in progress will be rejected with a message indicating the current job is still running</li>
          <li>Every rescored lead gets a row added to its score history with trigger type <code className="bg-gray-100 px-1 rounded text-xs">bulk_rescore</code></li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Version History &amp; Revert</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Every saved config is stored as a numbered snapshot &mdash; the active version is always the most recent</li>
          <li>View past versions in the <strong>Version History</strong> panel; click <strong>Revert</strong> to restore any previous config</li>
          <li>Reverting creates a new snapshot (not an edit), so the audit trail stays intact</li>
          <li>Config changes are rate-limited: a warning appears if you&apos;ve changed config within the last 7 days; a hard block applies if you&apos;ve changed it within the last 24 hours</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Segment Lock</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Click the <strong>lock icon</strong> next to a lead&apos;s segment badge on the lead detail page to pin it &mdash; rescoring will not change the segment while it is locked</li>
          <li>Entering an engaged-stage pipeline step (e.g. Demo Scheduled) auto-locks the segment to prevent a mid-funnel score drop from changing routing behavior</li>
          <li>Locks auto-expire after 30 days via a nightly Celery task</li>
          <li>Admins can unlock manually at any time</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">CallPulse Scoring Integration</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Enable scoring on a CallPulse playbook in <strong>Settings &rarr; CallPulse &rarr; Edit Playbook &rarr; Enable Scoring</strong></li>
          <li>Mark individual questions for scoring by setting a <strong>Scoring Attribute</strong> on the question (e.g. <code className="bg-gray-100 px-1 rounded text-xs">decision_authority</code>)</li>
          <li>When a rep submits CallPulse responses, each scored answer is stored as a lead attribute with the key <code className="bg-gray-100 px-1 rounded text-xs">{"cpq_{question_id}"}</code> and the lead is rescored immediately</li>
          <li>To target a CallPulse attribute in a scoring factor, use an <code className="bg-gray-100 px-1 rounded text-xs">attribute_match</code> or <code className="bg-gray-100 px-1 rounded text-xs">attribute_threshold</code> factor with the <code className="bg-gray-100 px-1 rounded text-xs">{"cpq_{id}"}</code> key &mdash; available keys appear in the attribute selector dropdown</li>
        </ul>
      </HelpSection>

      {/* ── Team Analytics ── */}
      <HelpSection title="Team Analytics">
        <p><strong>Team Analytics</strong> gives admins and managers a data-driven view of how each team and each agent is performing on calls. Access it via <strong>Analytics</strong> in the sidebar (admin and manager only).</p>

        <p className="font-semibold text-gray-800 mt-3">Period Selector</p>
        <p>Use the period selector in the top-right of the page to change the analysis window:</p>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {['7d — Last 7 days', '30d — Last 30 days', '90d — Last 90 days'].map(p => (
            <HelpBadge key={p} color="blue">{p}</HelpBadge>
          ))}
        </div>
        <p className="mt-1">All metrics on the page update together when you change the period.</p>

        <p className="font-semibold text-gray-800 mt-3">Cross-Team Comparison Table</p>
        <p>The default view lists all teams side-by-side. Each row in the table shows:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Team name &amp; type</strong> (Sales, Support, Accounts, Custom)</li>
          <li><strong>Total calls</strong> — inbound + outbound calls handled in the period</li>
          <li><strong>Connect rate</strong> — % of calls that were answered (vs missed or abandoned)</li>
          <li><strong>Avg quality score</strong> — average of post-call ratings (1-5) submitted by agents</li>
          <li><strong>Capacity utilization</strong> — active call count vs team's max concurrent call limit</li>
          <li><strong>Online members</strong> — number of agents currently in Online availability status</li>
        </ul>
        <p className="mt-1">Click any team row to open the team detail panel.</p>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Call Volume</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Total calls:</strong> All calls in the period (inbound + outbound)</li>
          <li><strong>Inbound / Outbound split:</strong> Breakdown by call direction</li>
          <li><strong>Missed calls:</strong> Calls that were not answered by any agent</li>
          <li><strong>VIP calls:</strong> HOT leads (score &gt; 80) who bypassed IVR and routed directly</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Performance</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Avg handle time:</strong> Average duration of answered calls (in minutes:seconds)</li>
          <li><strong>First-contact resolution (FCR):</strong> % of calls with outcome &ldquo;Resolved&rdquo; — no escalation or callback needed</li>
          <li><strong>Connect rate:</strong> % of calls answered out of all calls attempted to this team</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Quality</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Avg quality score:</strong> Average of 1-5 star ratings agents submitted via post-call rating, for calls handled by this team</li>
          <li><strong>Outcome breakdown:</strong> Count of each call outcome — Resolved, Escalated, Transferred, Voicemail, Abandoned</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Routing</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Avg queue wait:</strong> Average time callers waited before an agent was connected</li>
          <li><strong>Avg agents tried:</strong> Average number of ring attempts before the call was answered or dropped — lower is better</li>
          <li><strong>Strategy distribution:</strong> Which routing strategies were in effect during the period (from call snapshots, so reflects historical config even after changes)</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Capacity</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Online members:</strong> Agents currently in Online status</li>
          <li><strong>Total members:</strong> All agents in the team</li>
          <li><strong>Utilization:</strong> Current active calls vs team&apos;s max concurrent call limit</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Detail Panel — Trends Chart</p>
        <p>A daily bar chart showing call volume and connect rate over the selected period. Use this to spot day-of-week patterns (e.g. Monday spike), identify a bad week, or validate whether a team configuration change improved outcomes.</p>

        <p className="font-semibold text-gray-800 mt-3">Agent Performance (Drill-Down)</p>
        <p>Click any agent row within a team detail panel to see that agent&apos;s individual metrics for the period:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Calls handled:</strong> Total calls answered by this agent</li>
          <li><strong>Avg quality score:</strong> Average post-call rating for this agent&apos;s calls</li>
          <li><strong>Responsiveness:</strong> How quickly the agent answered routed calls (avg ring time before pickup)</li>
          <li><strong>Connect rate:</strong> % of routed calls this agent answered (vs let ring out)</li>
          <li><strong>vs Team average:</strong> Each metric is shown alongside the team average with a color-coded delta — green means the agent is above team average, red means below</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Automated Alerts</p>
        <p>The system checks for the following conditions every 60 seconds and creates a notification if a threshold is breached:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Missed calls:</strong> A call was not answered by any agent in the team</li>
          <li><strong>VIP missed:</strong> A HOT lead (score &gt; 80) called and no agent answered — highest priority alert</li>
          <li><strong>All agents offline:</strong> A team has no agents in Online status — inbound calls will go to fallback/voicemail immediately</li>
          <li><strong>Quality score drop:</strong> A team&apos;s rolling average quality score falls below the configured threshold</li>
        </ul>
        <p className="mt-1">Alerts appear in the bell icon notification center. Admin and manager roles receive them. Ensure browser notifications are enabled in <strong>Settings &rarr; Notifications</strong> to receive them in real time.</p>
      </HelpSection>

      {/* ── Import & Export ── */}
      <HelpSection title="Import &amp; Export">
        <p>Import leads in bulk from CSV and export your pipeline with a single click. All transfers are logged in the <strong>Data Transfers</strong> page (admin/manager only).</p>

        <p className="font-semibold text-gray-800 mt-2">CSV Import &mdash; 4-Step Wizard</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li><strong>Upload:</strong> Select a CSV file (max 5 MB, 10,000 rows). UTF-8 or UTF-8-BOM encoding; falls back to Latin-1 automatically.</li>
          <li><strong>Map Columns:</strong> Map each CSV column to an OpsNest lead field (name, email, phone, stage, etc.). Unmapped columns are ignored. Download the <strong>CSV Template</strong> to see exact column names.</li>
          <li><strong>Preview:</strong> OpsNest validates and cleans every row without writing to the database. Each row shows a status badge:
            <ul className="list-disc list-inside space-y-0.5 ml-4 mt-1">
              <li><HelpBadge color="green">Valid</HelpBadge> &mdash; Row is clean and ready to import. Shows the cleaned values (e.g. normalized phone).</li>
              <li><HelpBadge color="red">Invalid</HelpBadge> &mdash; Row has errors (e.g. missing required field, invalid email). Error details are shown per row. These rows are skipped.</li>
              <li><HelpBadge color="amber">Duplicate</HelpBadge> &mdash; Row matches an existing lead (by email or phone) or is a duplicate within the file itself. Shown with the matching lead&apos;s name. These rows are skipped.</li>
            </ul>
          </li>
          <li><strong>Import:</strong> Only VALID rows are committed. A summary shows how many were created, skipped, and why.</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Phone Normalization</p>
        <p>Phone numbers are normalized to E.164 format during import. Rules applied in order:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Numbers already starting with <code className="bg-gray-100 px-1 rounded text-xs">+</code> and 8-15 digits are kept as-is</li>
          <li>10-digit numbers starting with 6, 7, 8, or 9 &rarr; treated as Indian mobile, <code className="bg-gray-100 px-1 rounded text-xs">+91</code> prepended</li>
          <li>12-digit numbers starting with <code className="bg-gray-100 px-1 rounded text-xs">91</code> &rarr; Indian with country code, <code className="bg-gray-100 px-1 rounded text-xs">+</code> prepended</li>
          <li>11-digit numbers starting with <code className="bg-gray-100 px-1 rounded text-xs">0</code> &rarr; Indian trunk prefix, replaced with <code className="bg-gray-100 px-1 rounded text-xs">+91</code></li>
          <li>Numbers starting with a recognized country code (US/UK/UAE/Singapore/Australia and 25 others) &rarr; <code className="bg-gray-100 px-1 rounded text-xs">+</code> prepended</li>
          <li>Other 7-15 digit numbers &rarr; default country code <code className="bg-gray-100 px-1 rounded text-xs">+91</code> prepended with a warning</li>
          <li>Numbers in Excel scientific notation (e.g. <code className="bg-gray-100 px-1 rounded text-xs">9.19E+11</code>) are converted before processing</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">CSV Export</p>
        <p>Click <strong>Export CSV</strong> on the Leads page to download a CSV of the current filtered view. The export includes all fields: name, email, phone, company, stage, score, quality segment, lead source, call count, last call date, and more. Export filters (stage, segment, search) are saved in the audit log.</p>

        <p className="font-semibold text-gray-800 mt-3">Data Transfers Page</p>
        <p>Go to <strong>Data Transfers</strong> in the sidebar (admin/manager only) to see the full history of all imports and exports with file name, file size, record counts, creation/error/duplicate counts, and a link to the stored file.</p>
      </HelpSection>

      {/* ── Settings Overview ── */}
      <HelpSection title="Settings Overview">
        <p>Admin-only page with configuration tabs:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>General:</strong> LLM provider (OpenAI, Anthropic, Gemini) and API key.</li>
          <li><strong>Email:</strong> Email provider (Resend or AWS SES) for sending emails to leads.</li>
          <li><strong>VoIP:</strong> VoIP provider credentials (Plivo, Twilio, SignalWire, FreeSWITCH), webhook base URL, and Plivo Application ID for auto-provisioning.</li>
          <li><strong>IVR Menus:</strong> Configure inbound call menus &mdash; map keypad digits to teams, users, transfers, or voicemail.</li>
          <li><strong>Lead Assignment:</strong> Configure assignment rules, strategies, filters, and SLA timers.</li>
          <li><strong>CallPulse:</strong> Create and manage guided call playbooks with structured discussion topics for sales reps.</li>
          <li><strong>Scoring:</strong> Configure lead scoring categories, factor weights, quality segment thresholds, pipeline stages, and version history. See <em>Lead Scoring Configuration</em> below.</li>
          <li><strong>Integrations:</strong> Meta CAPI configuration (Pixel ID, enable/disable), webhook URLs, and <strong>Data Storage</strong> mode (see below).</li>
          <li><strong>Notifications:</strong> Enable or disable browser notifications for real-time alerts (new leads, call events, etc.).</li>
          <li><strong>Security:</strong> Data Privacy masking toggle, Blocked IPs management (view and unblock rate-limited IPs), and Access Log (track where users access OpsNest from with IP, location, device, and action type).</li>
          <li><strong>Help:</strong> This manual &mdash; feature documentation, workflows, and tips.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Data Storage (Settings &rarr; Integrations)</p>
        <p>Choose where imported CSV files and exported CSVs are stored after transfer:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><HelpBadge color="blue">Local</HelpBadge> &mdash; Files are saved to <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">data/transfers/</code> on the server. No extra configuration needed. <em>Best for self-hosted deployments.</em></li>
          <li><HelpBadge color="purple">S3</HelpBadge> &mdash; Files are uploaded to your AWS S3 bucket. Enter your Bucket Name, Region, Access Key, and Secret Key. S3 fields are only shown when S3 mode is selected. <em>Best for cloud deployments or when you need off-server backups.</em></li>
        </ul>
        <p className="mt-1">Storage failure is non-fatal for imports &mdash; the import still succeeds and the audit log is written even if the file cannot be stored.</p>
      </HelpSection>

      {/* ── Data Privacy & Masking ── */}
      <HelpSection title="Data Privacy &amp; Masking">
        <p>OpsNest supports system-wide contact information masking to protect lead PII.</p>

        <p className="font-semibold text-gray-800 mt-3">How to Enable</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Settings &rarr; General &rarr; Data Privacy</strong></li>
          <li>Toggle &ldquo;Mask contact information&rdquo; ON</li>
          <li>All users will now see masked data: phone (<code className="bg-gray-100 px-1 rounded text-xs">+91******3210</code>), email (<code className="bg-gray-100 px-1 rounded text-xs">p****@example.com</code>), addresses (<code className="bg-gray-100 px-1 rounded text-xs">****</code>)</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Per-User Override</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Admin can grant specific users full contact visibility</li>
          <li>Go to <strong>Users &rarr; Edit User &rarr; Data Privacy</strong> section</li>
          <li>Check &ldquo;Show full contact info (override masking)&rdquo;</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">What Gets Masked</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Phone numbers (last 4 digits visible)</li>
          <li>Email addresses (first character + domain visible)</li>
          <li>WhatsApp numbers</li>
          <li>Physical addresses and city</li>
          <li>Call history from/to numbers</li>
          <li>Inbound call popups and notifications</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">What Still Works</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Outbound calling</strong> &mdash; the system dials using the real number from the database</li>
          <li><strong>Inbound call routing</strong> &mdash; all routing uses real numbers internally</li>
          <li><strong>Inbound call popups</strong> &mdash; show lead name (if known) + masked phone number</li>
          <li><strong>Lead search by name</strong> (not by phone/email)</li>
          <li>All data is stored unmasked in the database</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">CSV Export</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Users with masking enabled cannot export leads (export is blocked)</li>
          <li>This prevents circumventing masking by downloading raw data</li>
        </ul>
      </HelpSection>

      {/* ── Data Security & Access Control ── */}
      <HelpSection title="Data Security &amp; Access Control">
        <p>OpsNest enforces role-based data isolation to ensure users only see data relevant to their role.</p>

        <p className="font-semibold text-gray-800 mt-3">Sales Reps See Only</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Leads assigned to them</li>
          <li>Their own call history (calls they made/received)</li>
          <li>Tasks assigned to them</li>
          <li>Companies where they own at least one lead</li>
          <li>Their own notifications</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Sales Reps Cannot Access</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Other reps&apos; leads, calls, or tasks</li>
          <li>Unknown Callers page</li>
          <li>Reports or Analytics</li>
          <li>Data import or export</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Managers See</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Data for all members of teams they lead or manage</li>
          <li>Their team&apos;s leads, calls, tasks, and companies</li>
          <li>Team analytics and reports</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Admins</p>
        <p className="ml-2">Admins see everything across all teams and users.</p>

        <p className="font-semibold text-gray-800 mt-3">Outbound Call Restrictions</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Sales reps can only call leads assigned to them</li>
          <li>Managers can call any lead in their team (including unassigned)</li>
          <li>Admins can call any lead</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Data Transfers</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Import (CSV upload):</strong> Admin and Manager only</li>
          <li><strong>Export (CSV download):</strong> Admin only</li>
          <li><strong>Per-user override:</strong> Admin can grant &ldquo;Allow data transfers&rdquo; to individual users in their profile</li>
          <li>All transfers are logged with who, when, file size, and record counts</li>
          <li>Exported files can be re-downloaded max 3 times from the Data Transfers page</li>
          <li>Downloads are tracked (who downloaded and when)</li>
        </ul>
      </HelpSection>

      {/* ── Presence & Online Tracking ── */}
      <HelpSection title="Presence &amp; Online Tracking">
        <p>OpsNest automatically tracks when users are online, idle, or offline &mdash; no manual action is required.</p>

        <p className="font-semibold text-gray-800 mt-3">How It Works</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>While you have OpsNest open, a heartbeat is sent in the background every 60 seconds</li>
          <li>The system records whether you are <HelpBadge color="green">Active</HelpBadge> (recently interacted with the page) or <HelpBadge color="amber">Idle</HelpBadge> (tab open but no recent interaction)</li>
          <li>If no heartbeat is received, your status is shown as <HelpBadge>Offline</HelpBadge> to other users</li>
          <li>Multiple open tabs are deduplicated &mdash; only one session is counted at a time</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Team Activity Dashboard</p>
        <p>Go to <strong>Team Activity</strong> in the sidebar (admin/manager only) to see:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>5 KPI cards:</strong> Online Now, Idle Now, Offline, On a Call, Team Coverage %</li>
          <li><strong>Agent status table:</strong> Live view of who is online with status dots, device type, last action, and last seen time. Click a row to see that agent&apos;s timeline.</li>
          <li><strong>Timeline tab:</strong> Hourly bar chart for the selected agent showing active (green) and idle (yellow) time for each hour of the current day</li>
          <li><strong>Daily Report tab:</strong> Per-user daily online time, active/idle split, session count, and login/logout times</li>
          <li><strong>Weekly Trend tab:</strong> Weekly aggregated online hours, days active, and average daily time per user</li>
          <li><strong>Heatmap tab:</strong> 7-day x 24-hour grid showing idle ratio by hour and day of week. Green = mostly active, yellow = mixed, red = mostly idle</li>
          <li><strong>Correlation tab:</strong> Online hours vs call count per user &mdash; shows calls per online hour to identify productivity patterns</li>
        </ul>
        <p className="mt-1">The live status table auto-refreshes every 30 seconds. Report tabs load data for the selected period (7, 14, or 30 days).</p>

        <p className="font-semibold text-gray-800 mt-3">Last Active on Users Page</p>
        <p>The <strong>Users</strong> page shows a <strong>Last Active</strong> column with live presence status for each user (green dot = active, yellow = idle, gray = offline) with relative time (e.g. &ldquo;5m ago&rdquo;). This refreshes every 60 seconds. Admin and manager only.</p>

        <p className="font-semibold text-gray-800 mt-3">Presence vs Availability</p>
        <p>Presence tracking (automatic) and the availability toggle (manual) are <strong>independent</strong>:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Presence</strong> = is my browser/app open? (automatic, for monitoring and analytics)</li>
          <li><strong>Availability toggle</strong> = am I ready for calls? (manual, controls call routing)</li>
          <li>An agent can be &ldquo;Online&rdquo; in presence (browser open) but set to &ldquo;Break&rdquo; in availability (no calls routed)</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Mobile App</p>
        <p>The mobile CRM app sends the same heartbeat while the app is in the foreground. Presence automatically stops when the app moves to the background and resumes with an immediate heartbeat when the app returns to the foreground. Device type is recorded as &ldquo;mobile&rdquo; and shown separately from web sessions in reports.</p>
      </HelpSection>

      {/* ── Two-Factor Authentication ── */}
      <HelpSection title="Two-Factor Authentication (OTP)">
        <p>OpsNest supports optional OTP-based two-factor authentication via SMS. When enabled for a user, they must enter a 6-digit code sent to their phone after their password.</p>

        <p className="font-semibold text-gray-800 mt-3">How to Enable</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Go to <strong>Users</strong> page and edit the user</li>
          <li>Enter their phone number in the <strong>Phone &amp; Two-Factor Authentication</strong> section</li>
          <li>Check <strong>&ldquo;Enable OTP two-factor authentication&rdquo;</strong></li>
          <li>Save changes</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Login Flow with 2FA</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>User enters username and password</li>
          <li>If correct, a 6-digit OTP is sent to their phone via SMS</li>
          <li>User enters the OTP code on the verification screen</li>
          <li>If correct, login is complete</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Security Details</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>OTP expires</strong> after 5 minutes</li>
          <li><strong>3 verification attempts</strong> per OTP &mdash; after that, the user must start over from the password step</li>
          <li><strong>Rate limited:</strong> max 3 OTPs per phone per 15 minutes</li>
          <li><strong>Resend:</strong> click &ldquo;Resend OTP&rdquo; on the verification screen to get a new code</li>
          <li>OTP codes are stored as SHA-256 hashes, never in plaintext</li>
          <li>All OTP events (sent, verified, failed) are logged for audit</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Admin Notes</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Only admins can enable/disable 2FA for users</li>
          <li>A phone number must be set before 2FA can be enabled</li>
          <li>If a user is locked out, the admin can disable their 2FA flag</li>
          <li>2FA works for both web and mobile logins</li>
          <li>SMS is sent via Fast2SMS (DLT route)</li>
        </ul>
      </HelpSection>

      {/* ── Blocked IPs ── */}
      <HelpSection title="Blocked IPs &amp; Rate Limiting">
        <p>OpsNest protects against brute-force login attacks by rate-limiting login attempts per IP address.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Limit:</strong> 5 login attempts per IP address per 15 minutes</li>
          <li><strong>After 5 failures:</strong> the IP is blocked from further login attempts for 15 minutes</li>
          <li><strong>Admin management:</strong> go to <strong>Settings &rarr; Security</strong> to view and unblock rate-limited IPs</li>
          <li>Rate limiting is stored in Redis (persists across server restarts)</li>
          <li>Only login endpoints are rate-limited &mdash; normal API usage and webhooks are not affected</li>
        </ul>
      </HelpSection>

      {/* ── Mobile API Authentication ── */}
      <HelpSection title="Mobile API Authentication">
        <p>Mobile clients (iOS, Android, or third-party integrations) use JWT tokens instead of session cookies to authenticate with the OpsNest API.</p>

        <p className="font-semibold text-gray-800 mt-2">Login</p>
        <p>Send a <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">POST /api/mobile/auth/login</code> request with your username and password. On success, the response includes two tokens:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>access_token</strong> &mdash; valid for 1 hour. Use this for all API requests.</li>
          <li><strong>refresh_token</strong> &mdash; valid for 30 days. Use this to get a new access token when it expires.</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Making API Requests</p>
        <p>Include the access token in the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">Authorization</code> header of every request:</p>
        <code className="block bg-gray-100 px-3 py-1.5 rounded text-xs mt-1">Authorization: Bearer &lt;access_token&gt;</code>

        <p className="font-semibold text-gray-800 mt-3">Refreshing Tokens</p>
        <p>When the access token expires (HTTP 401), call <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">POST /api/mobile/auth/refresh</code> with the refresh token to get a new access token without re-entering credentials.</p>

        <p className="font-semibold text-gray-800 mt-3">Logout</p>
        <p>Call <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">POST /api/mobile/auth/logout</code> to invalidate both tokens. After logout, the client must log in again to obtain new tokens.</p>

        <p className="font-semibold text-gray-800 mt-3">Compatibility</p>
        <p>All existing API endpoints (leads, calls, tasks, reports, etc.) work with JWT tokens. The same roles and permissions apply &mdash; a sales_rep JWT has the same access as a sales_rep browser session.</p>
      </HelpSection>

      {/* ── Access Tracking ── */}
      <HelpSection title="Access Location Tracking">
        <p>OpsNest automatically tracks the IP address and geographic location of users when they perform security-sensitive actions.</p>

        <p className="font-semibold text-gray-800 mt-3">What Is Tracked</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Login</strong> (web and mobile) &mdash; IP, location, browser/device info</li>
          <li><strong>VoIP call initiation</strong> &mdash; where the agent was when they started a call</li>
          <li><strong>CSV import</strong> &mdash; who imported data and from where</li>
          <li><strong>CSV export</strong> &mdash; who exported data and from where</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">How It Works</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>The server extracts the real client IP from the request (handles reverse proxies like AWS ALB and CloudFront)</li>
          <li>IP is resolved to a geographic location (country, city, region, ISP) using ipapi.co</li>
          <li>Geolocation is resolved asynchronously &mdash; no impact on page load speed</li>
          <li>Results are cached to minimize external API calls (Redis 24h + database indefinitely)</li>
          <li>No action is required from users &mdash; tracking is automatic and transparent</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Viewing Access Logs</p>
        <p>Go to <strong>Settings &rarr; Security</strong> (admin/manager only) and scroll to the <strong>Access Log</strong> section. The table shows:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>User:</strong> who performed the action</li>
          <li><strong>Action:</strong> login, mobile login, VoIP call, CSV import, or CSV export (color-coded)</li>
          <li><strong>IP Address:</strong> the real client IP (handles proxies correctly)</li>
          <li><strong>Location:</strong> city, region, and country (resolved via ipapi.co)</li>
          <li><strong>ISP/Org:</strong> the organization owning the IP address</li>
          <li><strong>Device:</strong> browser or mobile app detected from user agent</li>
          <li><strong>Time:</strong> when the action occurred</li>
        </ul>
        <p className="mt-1">Use the action filter dropdown to focus on specific event types. Managers see only their team&apos;s logs.</p>

        <p className="font-semibold text-gray-800 mt-3">Mobile Apps</p>
        <p>No mobile code changes are needed. The server captures the IP from the HTTP request headers automatically when the mobile app calls any tracked endpoint.</p>
      </HelpSection>

      {/* ── Instagram Seller Discovery ── */}
      <HelpSection title="Instagram Seller Discovery">
        <p>The <strong>IG Discovery</strong> page (<code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">/ig-discovery</code>) helps you find Instagram sellers who could benefit from Storepecker or UGCommerce &mdash; small businesses selling via DMs who need a proper online store.</p>

        <p className="font-semibold text-gray-800 mt-3">How It Works</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li><strong>Search Queries</strong> &mdash; Configure Google search queries that target Instagram seller profiles (e.g. <code className="bg-gray-100 px-1 rounded text-xs">site:instagram.com &quot;DM to order&quot; india</code>). Click <strong>Seed Defaults</strong> to load 53 pre-built queries covering 10 categories: Indian DM sellers, fashion, jewelry, food, beauty, home decor, kids/electronics/pets, 12 city-specific queries, regional language, and hashtag-based Google searches.</li>
          <li><strong>Run Discovery</strong> &mdash; Launch a discovery job that runs two phases: (1) Google Search via RapidAPI fetches 2&ndash;3 pages per query (20&ndash;30 results) and extracts Instagram handles from profile URLs and snippet text; (2) optional hashtag search across Instagram (enable with <code className="bg-gray-100 px-1 rounded text-xs">IG_HASHTAG_ENABLED=true</code>). All discovered handles are created as prospects in <HelpBadge>Pending Review</HelpBadge>. Supports <code className="bg-gray-100 px-1 rounded text-xs">?async=true</code> for background execution via Celery.</li>
          <li><strong>Review Queue</strong> &mdash; Every discovered prospect lands here first. Click the handle or the <strong>external link icon</strong> to open the Instagram profile in a new tab for quick review. <strong>Approve</strong> (enters the pipeline) or <strong>Reject</strong> (archived with a reason). Bulk approve and bulk reject are available. If the API has issues, a red or amber alert appears with details.</li>
          <li><strong>Prospect Management</strong> &mdash; View enriched profile data (followers, engagement rate, last post date, posts per week, bio, business status), move prospects through pipeline stages, log outreach touchpoints, and add niche tags. Click the <strong>IG</strong> link or handle to open the profile on Instagram.</li>
          <li><strong>Convert to Lead</strong> &mdash; When a prospect is qualified, click Convert to create a lead in the main CRM with one click. The lead source is set to <code className="bg-gray-100 px-1 rounded text-xs">instagram_discovery</code> and the prospect retains a link back to the lead.</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Pipeline Stages</p>
        <div className="space-y-1.5 ml-2">
          <div><HelpBadge>Pending Review</HelpBadge> &mdash; Just discovered &mdash; awaiting human approval. Nothing proceeds without a human decision.</div>
          <div><HelpBadge color="blue">Discovered</HelpBadge> &mdash; Approved, entered pipeline. Profile enrichment is triggered.</div>
          <div><HelpBadge color="purple">Researching</HelpBadge> &mdash; Team is reviewing the profile and deciding on fit.</div>
          <div><HelpBadge color="amber">Qualified</HelpBadge> &mdash; Meets criteria for outreach. Ready to assign.</div>
          <div><HelpBadge color="amber">Outreach Sent</HelpBadge> &mdash; DM, email, or WhatsApp sent. Log the touchpoint.</div>
          <div><HelpBadge color="green">Responded</HelpBadge> &mdash; Prospect replied. Schedule demo or continue conversation.</div>
          <div><HelpBadge color="green">Converted</HelpBadge> &mdash; Lead created in the main CRM.</div>
          <div><HelpBadge color="red">Rejected</HelpBadge> &mdash; Not a fit. Archived with a reason.</div>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Prospect Enrichment</p>
        <p>Click <strong>Enrich</strong> on any prospect to fetch live Instagram profile data via the RapidAPI Instagram scraper (<code className="bg-gray-100 px-1 rounded text-xs">instagram-public-bulk-scraper.p.rapidapi.com</code>):</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Follower count, following count, post count</li>
          <li>Bio text, profile picture, external URL, business category</li>
          <li>Average likes, average comments, engagement rate</li>
          <li>Is business account, is verified</li>
          <li>Last post date and posts per week (activity signals)</li>
          <li>Recent posts and top hashtags extracted from captions and bio</li>
          <li>Email and phone extracted from bio text</li>
        </ul>
        <p className="mt-1 text-xs text-gray-500">Enrichment uses <code className="bg-gray-100 px-1 rounded text-xs">/v1/user_info</code> for profile data and <code className="bg-gray-100 px-1 rounded text-xs">/v1/user_posts</code> for recent post metrics. Rate-limited to 30 requests per minute. Bulk enrichment queues requests automatically.</p>

        <p className="font-semibold text-gray-800 mt-3">Hashtag Tracking</p>
        <p>The system automatically tracks hashtags discovered during searches and enrichment:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Discovery</strong> &mdash; #hashtags found in Google search snippets are extracted and stored with occurrence counts</li>
          <li><strong>Enrichment</strong> &mdash; Top hashtags from each prospect&apos;s recent posts are stored in their profile</li>
          <li><strong>Hashtag API</strong> &mdash; When enabled, the hashtag search API returns <code className="bg-gray-100 px-1 rounded text-xs">media_count</code> (total posts for that hashtag on Instagram), which is saved for trend analysis</li>
          <li><strong>Stats tab</strong> &mdash; Shows the most popular hashtags across all prospects, with frequency and media count where available</li>
        </ul>
        <p className="mt-1 text-xs text-gray-500">This data grows over time and can be used to identify high-yield hashtags for new search queries.</p>

        <p className="font-semibold text-gray-800 mt-3">Prospect Scoring (0-100)</p>
        <p>Prospects are automatically scored after enrichment based on seller signals:</p>
        <div className="overflow-x-auto mt-1">
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">Signal</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">Points</th>
                <th className="px-3 py-1.5 text-left font-semibold text-gray-700">Logic</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="px-3 py-1.5">Bio contains &ldquo;DM to order&rdquo;, &ldquo;WhatsApp&rdquo;, or &ldquo;COD&rdquo;</td><td className="px-3 py-1.5">+30</td><td className="px-3 py-1.5">Keyword match in bio text</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">No external website</td><td className="px-3 py-1.5">+25</td><td className="px-3 py-1.5">No linktree/website = no existing store</td></tr>
              <tr><td className="px-3 py-1.5">Follower count (10-50K = max)</td><td className="px-3 py-1.5">+15</td><td className="px-3 py-1.5">1-5K=5, 5-10K=10, 10-50K=15</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">Engagement rate (5%+ = max)</td><td className="px-3 py-1.5">+15</td><td className="px-3 py-1.5">&lt;1%=0, 1-3%=5, 3-5%=10, 5%+=15</td></tr>
              <tr><td className="px-3 py-1.5">Post frequency &gt;3/week</td><td className="px-3 py-1.5">+10</td><td className="px-3 py-1.5">Active seller signal (computed from <code className="bg-gray-100 px-1 rounded text-xs">posts_per_week</code>)</td></tr>
              <tr className="bg-gray-50/50"><td className="px-3 py-1.5">Has email in bio</td><td className="px-3 py-1.5">+5</td><td className="px-3 py-1.5">Outreach channel available</td></tr>
            </tbody>
          </table>
        </div>
        <div className="flex gap-2 mt-2">
          <HelpBadge color="red">HOT 60+</HelpBadge>
          <HelpBadge color="amber">WARM 30-59</HelpBadge>
          <HelpBadge color="blue">COLD &lt;30</HelpBadge>
        </div>

        <p className="font-semibold text-gray-800 mt-3">Rejection Reasons</p>
        <p>When rejecting a prospect, select a reason to keep the archive useful:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Not a seller</strong> &mdash; personal account, no commerce signals</li>
          <li><strong>Fake account</strong> &mdash; bot, purchased followers, or inactive</li>
          <li><strong>Already a customer</strong> &mdash; already using Storepecker or UGCommerce</li>
          <li><strong>Irrelevant niche</strong> &mdash; product category we don&apos;t serve</li>
          <li><strong>Duplicate</strong> &mdash; already exists as a lead or prospect</li>
          <li><strong>Other</strong> &mdash; custom reason in notes</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Outreach Log</p>
        <p>Log every touchpoint with a prospect (DM, email, WhatsApp, call, note). Each log entry is immutable &mdash; it cannot be edited or deleted. This gives you a complete history of all outreach attempts even if ownership changes.</p>

        <p className="font-semibold text-gray-800 mt-3">Convert to Lead</p>
        <p>Available to admin and manager roles. Converting a prospect:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Creates a new lead in the main CRM with <code className="bg-gray-100 px-1 rounded text-xs">lead_source = instagram_discovery</code></li>
          <li>Sets the prospect&apos;s stage to <HelpBadge color="green">Converted</HelpBadge> and records the conversion timestamp</li>
          <li>Links the prospect record to the lead via FK &mdash; the prospect survives lead deletion (FK is <code className="bg-gray-100 px-1 rounded text-xs">SET NULL</code>)</li>
          <li>The full outreach history and enrichment data remain on the prospect record for reference</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Access</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>View prospects:</strong> Admin, Manager, Marketing, Sales Rep</li>
          <li><strong>Query management, review, enrichment, tags:</strong> Admin, Manager, Marketing</li>
          <li><strong>Convert to lead:</strong> Admin, Manager only</li>
          <li>Sales reps can view and log outreach but cannot approve/reject or convert</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Environment Setup</p>
        <p>Only one API key is required &mdash; it is shared across all three RapidAPI services:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">IG_ENRICHMENT_API_KEY</code> &mdash; Your RapidAPI key. Subscribes to three APIs: <strong>google-search116</strong> (Google search), <strong>instagram-public-bulk-scraper</strong> (profile enrichment), and optionally <strong>instagram-scraper-api14</strong> (hashtag search).</li>
        </ul>
        <p className="mt-2">Optional tuning variables:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">GOOGLE_SEARCH_PAGES</code> &mdash; Pages to fetch per query (default: 3, each page = 10 results). Increase to 5&ndash;10 for higher yield at the cost of more API calls.</li>
          <li><code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">IG_HASHTAG_ENABLED</code> &mdash; Set to <code className="bg-gray-100 px-1 rounded text-xs">true</code> to enable Instagram hashtag search (default: disabled). When enabled, each discovery run also searches 5 seller hashtags via the hashtag API.</li>
        </ul>
        <p className="mt-2 text-xs text-gray-500">If the API encounters errors (rate limit, expired subscription, or key issues), a coloured alert appears on the discovery page with details. Red = job failed. Amber = partial results. Green = success.</p>

        <p className="font-semibold text-gray-800 mt-3">Prospect Assignment</p>
        <p>In the prospect detail modal, use the <strong>Assigned To</strong> dropdown to assign a prospect to a team member. Assignments feed into the Team Performance analytics. Typical workflow:</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Discovery finds prospects &rarr; marketing team reviews and approves</li>
          <li>Approved prospects are enriched and scored automatically</li>
          <li>Marketing manager assigns qualified prospects to team members</li>
          <li>Team members log outreach (DMs, emails, WhatsApp) as touchpoints</li>
          <li>When a prospect responds positively, convert them to a CRM lead</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Analytics (Stats Tab)</p>
        <p>The Stats tab has four sub-views:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Overview</strong> &mdash; Conversion rate, pipeline at risk, discovery efficiency, conversion funnel with stage-to-stage drop-off %, score distribution histogram, follower tier breakdown (micro/mid/macro) with avg score and conversion rates per tier, top search queries by results</li>
          <li><strong>Trends</strong> &mdash; Weekly line chart showing new prospects, conversions, and outreach touchpoints over 12 weeks. Helps identify if the pipeline is growing or stalling</li>
          <li><strong>Hashtags</strong> &mdash; Most common hashtags across prospects, hashtag-to-quality correlation (which hashtags produce sellers who convert), and suggested new queries based on high-performing hashtags not yet in your query list</li>
          <li><strong>Team</strong> &mdash; Per-member outreach count, prospects assigned, conversions, and conversion rate. Admin/manager only. Identifies top performers and coaching opportunities</li>
        </ul>

        <p className="font-semibold text-gray-800 mt-3">Self-Improving Discovery Loop</p>
        <p>The system gets smarter over time:</p>
        <ol className="list-decimal list-inside space-y-0.5 ml-2">
          <li>Discovery runs extract hashtags from Google snippets and prospect bios</li>
          <li>Analytics shows which hashtags correlate with converting sellers</li>
          <li>Suggested Queries in the Hashtags tab recommends adding these as search queries</li>
          <li>Click &quot;Add to Queries&quot; to include them in the next discovery run</li>
          <li>Repeat &mdash; each cycle finds better-targeted prospects</li>
        </ol>

        <p className="font-semibold text-gray-800 mt-3">Data Retention</p>
        <p>No data is ever hard-deleted from the system:</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li>Search queries: soft-deleted (set inactive) &mdash; historical results preserved for analytics</li>
          <li>Prospects: soft-deleted &mdash; stage history and outreach logs preserved</li>
          <li>Outreach log: append-only, immutable &mdash; touchpoints can never be edited or deleted</li>
          <li>Discovery jobs: permanent &mdash; full audit trail of every search run</li>
          <li>Hashtag data: accumulated &mdash; discovery_count incremented on each occurrence</li>
        </ul>
      </HelpSection>

      {/* ── Keyboard Shortcuts / Tips ── */}
      <HelpSection title="Connect Claude (MCP Server)">
        <p>OpsNest exposes a <strong>read-only MCP server</strong> so you can connect Claude (Claude Code or Claude Desktop) and query your data conversationally &mdash; &ldquo;how many HOT leads are in my pipeline?&rdquo;, &ldquo;what&apos;s this month&apos;s Meta cost-per-lead?&rdquo;, &ldquo;show me overdue tasks&rdquo;.</p>
        <p className="font-semibold text-gray-800 mt-2">What Claude can see</p>
        <p>A token authenticates as <em>you</em>: results respect your role exactly like the rest of OpsNest (sales reps see their own leads, managers see their team, admins see everything). It is <strong>read-only</strong> &mdash; Claude can query but never change data. Contact info is masked when masking is enabled for you.</p>
        <p className="font-semibold text-gray-800 mt-3">Available queries</p>
        <ul className="list-disc list-inside space-y-0.5 ml-2">
          <li><strong>Leads:</strong> search, full lead detail (notes, calls, stage history), pipeline summary</li>
          <li><strong>Calls:</strong> volume and outcome breakdown over a recent window</li>
          <li><strong>Tasks:</strong> tasks assigned within your scope</li>
          <li><strong>Meta Ads:</strong> campaign spend, leads, and cost-per-lead (admin / manager / marketing)</li>
        </ul>
        <p className="font-semibold text-gray-800 mt-3">Option A &mdash; claude.ai or Claude Desktop (easiest)</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>In Claude: <strong>Settings &rarr; Connectors &rarr; Add custom connector</strong></li>
          <li>Enter the URL <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">https://&lt;your-opsnest-host&gt;/mcp</code></li>
          <li>You&apos;ll be sent to an OpsNest consent screen &mdash; log in and click <strong>Allow access</strong></li>
        </ol>
        <p className="text-xs text-gray-500 mt-1">Requires OpsNest hosted on a public HTTPS domain (Claude connects from Anthropic&apos;s cloud, not your device) with <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">MCP_PUBLIC_BASE_URL</code> set. No token needed &mdash; uses OAuth.</p>
        <p className="font-semibold text-gray-800 mt-3">Option B &mdash; Claude Code (token)</p>
        <ol className="list-decimal list-inside space-y-1 ml-2">
          <li>Create a token in the <strong>MCP Access</strong> tab (copy it &mdash; shown once)</li>
          <li><code className="text-xs bg-gray-100 px-1 py-0.5 rounded">claude mcp add --transport http opsnest https://&lt;host&gt;/mcp --header &quot;Authorization: Bearer opsn_...&quot;</code></li>
          <li>Ask Claude about your CRM, calls, pipeline, or ad performance</li>
        </ol>
        <p className="text-xs text-gray-500 mt-2">See <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">docs/mcp-server.md</code> for full details.</p>
      </HelpSection>

      <HelpSection title="Tips & Best Practices">
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li><strong>Speed-to-contact matters:</strong> Leads contacted within 15 minutes are 9x more likely to convert than those contacted after 30 minutes.</li>
          <li><strong>Start with Round Robin:</strong> The simplest assignment strategy is the most fair. Add complexity only after you have 3+ months of conversion data.</li>
          <li><strong>Use CAPI:</strong> Enabling Meta Conversions API feedback improves lead quality within 6-8 weeks as Meta learns which leads actually convert.</li>
          <li><strong>Set SLAs per segment:</strong> Create separate assignment rules for HOT (15 min SLA), WARM (4 hr), and COLD (24 hr) leads.</li>
          <li><strong>Check Run History:</strong> All AI-generated content is saved. Use it to compare outputs and refine your prompts.</li>
          <li><strong>Map all Meta form fields:</strong> Unmapped fields mean lost data. Ensure every useful form field is mapped to a lead field.</li>
          <li><strong>Monitor team capacity:</strong> Use the Team Workload page to prevent any single rep from being overwhelmed.</li>
          <li><strong>Start without teams:</strong> If you&apos;re a small team (1-3 people), use &ldquo;Route to User&rdquo; in IVR options. Add teams when you have 4+ people and need round-robin or load balancing.</li>
          <li><strong>Set availability:</strong> Agents should toggle their status to Online (green) in the header when they&apos;re ready to take calls. Offline agents won&apos;t receive routed calls.</li>
          <li><strong>Keep IVR menus short:</strong> Max 3-4 options. Every extra option increases caller abandonment.</li>
          <li><strong>Track CPQL, not just CPL:</strong> Cost Per Qualified Lead is the metric that predicts revenue. A cheap lead that never qualifies costs more than an expensive one that closes.</li>
        </ul>
      </HelpSection>

      <div className="text-xs text-gray-400 text-center pt-4 pb-2">
        OpsNest v0.1.0 &mdash; Built for Storepecker &amp; UGCommerce by TECH101 Informatics
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: CallPulse
// ---------------------------------------------------------------------------

const STAGE_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'demo_scheduled', label: 'Demo Scheduled' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'negotiation', label: 'Negotiation' },
]

const SEGMENT_OPTIONS = ['HOT', 'WARM', 'COLD']

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'select', label: 'Single Select' },
  { value: 'multiselect', label: 'Multi Select' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'scale', label: 'Scale (1-10)' },
]

interface PlaybookFormQuestion {
  text: string
  question_type: string
  options: string
  required: boolean
  helper_text: string
  ai_tags: string
  sort_order: number
  contributes_to_scoring: boolean
}

interface PlaybookForm {
  name: string
  description: string
  product: string
  priority: string
  is_active: boolean
  contributes_to_scoring: boolean
  call_numbers: string
  stages: string[]
  segments: string[]
  score_min: string
  score_max: string
  questions: PlaybookFormQuestion[]
}

const EMPTY_QUESTION: PlaybookFormQuestion = {
  text: '',
  question_type: 'text',
  options: '',
  required: false,
  helper_text: '',
  ai_tags: '',
  sort_order: 1,
  contributes_to_scoring: false,
}

const EMPTY_PLAYBOOK_FORM: PlaybookForm = {
  name: '',
  description: '',
  product: 'all',
  priority: '10',
  is_active: true,
  contributes_to_scoring: false,
  call_numbers: '',
  stages: [],
  segments: [],
  score_min: '',
  score_max: '',
  questions: [{ ...EMPTY_QUESTION }],
}

function CallPulseGuide() {
  const [openSection, setOpenSection] = useState<string | null>('what-is-callpulse')

  const sections = [
    {
      id: 'what-is-callpulse',
      title: 'What is CallPulse?',
      content: 'CallPulse playbooks are structured questionnaires that reps fill during calls. Each playbook has questions, trigger conditions, and optional scoring integration. Responses are saved per-call and linked to the lead.',
    },
    {
      id: 'setting-up',
      title: 'Setting Up Playbooks',
      items: [
        'Create playbooks for different call scenarios \u2014 discovery, follow-up, qualification, demo',
        'Set trigger conditions to auto-select the right playbook: call number (1st call vs 3rd), lead stage, segment, score range',
        'Higher priority playbooks are checked first. Use priority to control fallback order',
        'Only active playbooks are shown to reps during calls',
      ],
    },
    {
      id: 'scoring',
      title: 'Scoring Integration',
      items: [
        "Enable 'Contributes to Lead Scoring' on a playbook to feed answers into the lead's score",
        "Mark individual questions with 'Use for scoring' \u2014 only these questions affect the score",
        'The system auto-generates a stable key (cpq_{id}) for each scoring question',
        'After submission, the lead is automatically rescored with the new data',
        "Draft saves don't trigger scoring \u2014 only submitted responses count",
      ],
    },
    {
      id: 'question-types',
      title: 'Question Types',
      items: [
        'Text / Textarea \u2014 free-form answers, not used for scoring',
        'Select / Multiselect \u2014 dropdown options, great for scoring (attribute_match)',
        'Yes/No \u2014 boolean, good for scoring (attribute_boolean)',
        'Scale \u2014 numeric rating, good for threshold scoring',
      ],
    },
    {
      id: 'tips',
      title: 'Tips',
      items: [
        "Keep playbooks short (5-8 questions). Reps won't fill 20 questions during a call",
        'Put the most important scoring questions first \u2014 reps may not finish all questions',
        'Use helper text to guide reps on what to ask the customer',
        'Review submitted responses weekly to identify patterns and adjust questions',
      ],
    },
  ]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">CallPulse Guide</h3>
      <div className="space-y-1">
        {sections.map(section => (
          <div key={section.id} className="border-b border-gray-50 last:border-0">
            <button
              onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
              className="w-full flex items-center justify-between py-2 text-left"
            >
              <span className="text-xs font-medium text-gray-700">{section.title}</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} />
            </button>
            {openSection === section.id && (
              <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                {section.content && <p>{section.content}</p>}
                {section.items && (
                  <ul className="list-disc list-outside ml-3.5 space-y-1.5">
                    {section.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function CallPulseTab() {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<PlaybookForm>({ ...EMPTY_PLAYBOOK_FORM })
  const [error, setError] = useState('')

  const { data: playbooksData, isLoading } = useQuery({
    queryKey: ['playbooks'],
    queryFn: playbookApi.list,
  })

  const playbooks = (playbooksData?.data ?? []) as any[]

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => playbookApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      closeModal()
    },
    onError: (err: Error) => setError(err.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      playbookApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      closeModal()
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => playbookApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
    },
  })

  const [seedMsg, setSeedMsg] = useState('')
  const [showDefaultPicker, setShowDefaultPicker] = useState(false)
  const [selectedDefaults, setSelectedDefaults] = useState<string[]>([])

  const { data: defaultsData } = useQuery({
    queryKey: ['playbook-defaults'],
    queryFn: () => playbookApi.getDefaults(),
    enabled: showDefaultPicker,
  })
  const defaultPlaybooks = (defaultsData?.data || []) as { name: string; exists: boolean }[]

  const seedMutation = useMutation({
    mutationFn: (names?: string[]) => playbookApi.seedDefaults(names && names.length > 0 ? names : undefined),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] })
      queryClient.invalidateQueries({ queryKey: ['playbook-defaults'] })
      const d = res.data as Record<string, unknown>
      if ((d.seeded as number) > 0) {
        setSeedMsg(`Loaded ${d.seeded} default playbook(s)`)
      } else {
        setSeedMsg((d.message as string) || 'All selected playbooks already exist')
      }
      setShowDefaultPicker(false)
      setSelectedDefaults([])
      setTimeout(() => setSeedMsg(''), 4000)
    },
    onError: (err: Error) => setSeedMsg(`Error: ${err.message}`),
  })

  const closeModal = () => {
    setModalOpen(false)
    setEditingId(null)
    setForm({ ...EMPTY_PLAYBOOK_FORM })
    setError('')
  }

  const openCreate = () => {
    setEditingId(null)
    setForm({ ...EMPTY_PLAYBOOK_FORM, questions: [{ ...EMPTY_QUESTION }] })
    setError('')
    setModalOpen(true)
  }

  const openEdit = (playbook: any) => {
    setEditingId(playbook.id)
    const triggers = playbook.trigger_conditions || {}
    const questions: PlaybookFormQuestion[] = (playbook.questions || []).map((q: any) => ({
      text: q.text || '',
      question_type: q.question_type || 'text',
      options: (q.options || []).join(', '),
      required: q.required ?? false,
      helper_text: q.helper_text || '',
      ai_tags: (q.ai_tags || []).join(', '),
      sort_order: q.sort_order ?? 1,
      contributes_to_scoring: q.contributes_to_scoring ?? false,
    }))
    if (questions.length === 0) questions.push({ ...EMPTY_QUESTION })

    setForm({
      name: playbook.name || '',
      description: playbook.description || '',
      product: playbook.product || 'all',
      priority: String(playbook.priority ?? 10),
      is_active: playbook.is_active ?? true,
      contributes_to_scoring: playbook.contributes_to_scoring ?? false,
      call_numbers: (triggers.call_numbers || []).join(', '),
      stages: triggers.stages || [],
      segments: triggers.segments || [],
      score_min: triggers.score_min != null ? String(triggers.score_min) : '',
      score_max: triggers.score_max != null ? String(triggers.score_max) : '',
      questions,
    })
    setError('')
    setModalOpen(true)
  }

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError('Name is required')
      return
    }
    if (form.questions.length === 0 || !form.questions.some((q) => q.text.trim())) {
      setError('Add at least one question')
      return
    }

    const triggerConditions: Record<string, unknown> = {}
    if (form.call_numbers.trim()) {
      triggerConditions.call_numbers = form.call_numbers.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n))
    }
    if (form.stages.length > 0) triggerConditions.stages = form.stages
    if (form.segments.length > 0) triggerConditions.segments = form.segments
    if (form.score_min) triggerConditions.min_score = parseInt(form.score_min)
    if (form.score_max) triggerConditions.max_score = parseInt(form.score_max)

    const questions = form.questions
      .filter((q) => q.text.trim())
      .map((q) => ({
        question_text: q.text.trim(),
        question_type: q.question_type,
        options: q.options.trim() ? q.options.split(',').map((s) => s.trim()).filter(Boolean) : [],
        is_required: q.required,
        helper_text: q.helper_text.trim() || null,
        ai_tags: q.ai_tags.trim() ? q.ai_tags.split(',').map((s) => s.trim()).filter(Boolean) : [],
        contributes_to_scoring: form.contributes_to_scoring ? q.contributes_to_scoring : false,
      }))

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      product: form.product,
      priority: parseInt(form.priority) || 10,
      is_active: form.is_active,
      contributes_to_scoring: form.contributes_to_scoring,
      trigger_conditions: triggerConditions,
      questions,
    }

    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const addQuestion = () => {
    const maxOrder = Math.max(0, ...form.questions.map((q) => q.sort_order))
    setForm((f) => ({
      ...f,
      questions: [...f.questions, { ...EMPTY_QUESTION, sort_order: maxOrder + 1 }],
    }))
  }

  const removeQuestion = (index: number) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.filter((_, i) => i !== index),
    }))
  }

  const updateQuestion = (index: number, field: keyof PlaybookFormQuestion, value: unknown) => {
    setForm((f) => ({
      ...f,
      questions: f.questions.map((q, i) => (i === index ? { ...q, [field]: value } : q)),
    }))
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading playbooks...</div>
  }

  const triggerSummary = (pb: any) => {
    const t = pb.trigger_conditions || {}
    const parts: string[] = []
    if (t.call_numbers?.length) parts.push(`Calls: ${t.call_numbers.join(',')}`)
    if (t.stages?.length) parts.push(`${t.stages.length} stages`)
    if (t.segments?.length) parts.push(t.segments.join(', '))
    if (t.score_min != null || t.score_max != null) {
      parts.push(`Score ${t.score_min ?? 0}-${t.score_max ?? 100}`)
    }
    return parts.length > 0 ? parts.join(' | ') : 'Any call'
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
      <div className="space-y-6">
      {/* Playbooks List */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <MessageSquare size={18} className="text-gray-400" /> CallPulse Playbooks
          </h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setShowDefaultPicker(!showDefaultPicker)}>
              Load Defaults
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} /> Create Playbook
            </Button>
          </div>
        </div>

        {seedMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
            {seedMsg}
          </div>
        )}

        {/* Default playbook picker */}
        {showDefaultPicker && (
          <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Select Default Playbooks to Load</h4>
            <div className="space-y-2 mb-4">
              {defaultPlaybooks.map((dp) => (
                <label key={dp.name} className={`flex items-center gap-3 p-2 rounded-lg ${dp.exists ? 'opacity-50' : 'hover:bg-gray-50 cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    disabled={dp.exists}
                    checked={selectedDefaults.includes(dp.name)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedDefaults(prev => [...prev, dp.name])
                      else setSelectedDefaults(prev => prev.filter(n => n !== dp.name))
                    }}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-800">{dp.name}</span>
                  {dp.exists && <span className="text-xs text-gray-400">(already exists)</span>}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => seedMutation.mutate(selectedDefaults)} disabled={selectedDefaults.length === 0 || seedMutation.isPending} loading={seedMutation.isPending}>
                Load Selected ({selectedDefaults.length})
              </Button>
              <Button size="sm" onClick={() => seedMutation.mutate(undefined)} variant="secondary" disabled={seedMutation.isPending}>
                Load All Available
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowDefaultPicker(false); setSelectedDefaults([]) }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {playbooks.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No playbooks yet"
            description="Create your first playbook to guide your team through call conversations with structured discussion topics."
            action={
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={openCreate}>
                  <Plus size={14} /> Create your first playbook
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowDefaultPicker(true)}
                >
                  Load Default Playbooks
                </Button>
              </div>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Name</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Product</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Triggers</th>
                  <th className="text-center py-2 px-2 font-medium text-gray-500">Priority</th>
                  <th className="text-center py-2 px-2 font-medium text-gray-500">Version</th>
                  <th className="text-center py-2 px-2 font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 px-2 font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {playbooks.map((pb: any) => (
                  <tr key={pb.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2.5 px-2">
                      <span className="font-medium text-gray-900">{pb.name}</span>
                      {pb.description && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{pb.description}</p>
                      )}
                    </td>
                    <td className="py-2.5 px-2">
                      <Badge variant={pb.product === 'all' ? 'default' : 'info'}>
                        {pb.product === 'all' ? 'All' : pb.product}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-2 text-xs text-gray-500 max-w-[200px] truncate">
                      {triggerSummary(pb)}
                    </td>
                    <td className="py-2.5 px-2 text-center text-gray-600">{pb.priority}</td>
                    <td className="py-2.5 px-2 text-center text-gray-400">v{pb.version}</td>
                    <td className="py-2.5 px-2 text-center">
                      <Badge variant={pb.is_active ? 'success' : 'default'}>
                        {pb.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(pb)}>
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Delete playbook "${pb.name}"?`)) {
                              deleteMutation.mutate(pb.id)
                            }
                          }}
                        >
                          <Trash2 size={14} className="text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? 'Edit Playbook' : 'Create Playbook'}
        size="xl"
      >
        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., First Call - Discovery"
              required
            />
            <Select
              label="Product"
              options={[
                { value: 'all', label: 'All Products' },
                { value: 'storepecker', label: 'Storepecker' },
                { value: 'ugcommerce', label: 'UGCommerce' },
              ]}
              value={form.product}
              onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
            />
          </div>

          <Textarea
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Brief description of when to use this playbook..."
          />

          <div className="grid grid-cols-3 gap-4">
            <Input
              label="Priority"
              type="number"
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
            />
            <div className="flex items-end pb-1 gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.contributes_to_scoring}
                  onChange={(e) => setForm((f) => ({ ...f, contributes_to_scoring: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                Contributes to Lead Scoring
              </label>
            </div>
          </div>

          {/* Trigger Conditions */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Trigger Conditions</h3>
            <p className="text-xs text-gray-400 mb-4">
              Define when this playbook should be shown to the rep. Leave blank for "any".
            </p>

            <div className="space-y-4">
              <Input
                label="Call Numbers (comma-separated, e.g. 1,2,3)"
                value={form.call_numbers}
                onChange={(e) => setForm((f) => ({ ...f, call_numbers: e.target.value }))}
                placeholder="Leave empty for any call number"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pipeline Stages</label>
                <div className="flex flex-wrap gap-2">
                  {STAGE_OPTIONS.map((stage) => (
                    <label key={stage.value} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={form.stages.includes(stage.value)}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            stages: e.target.checked
                              ? [...f.stages, stage.value]
                              : f.stages.filter((s) => s !== stage.value),
                          }))
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {stage.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lead Segments</label>
                <div className="flex flex-wrap gap-3">
                  {SEGMENT_OPTIONS.map((seg) => (
                    <label key={seg} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={form.segments.includes(seg)}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            segments: e.target.checked
                              ? [...f.segments, seg]
                              : f.segments.filter((s) => s !== seg),
                          }))
                        }}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {seg}
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Min Score"
                  type="number"
                  value={form.score_min}
                  onChange={(e) => setForm((f) => ({ ...f, score_min: e.target.value }))}
                  placeholder="0"
                />
                <Input
                  label="Max Score"
                  type="number"
                  value={form.score_max}
                  onChange={(e) => setForm((f) => ({ ...f, score_max: e.target.value }))}
                  placeholder="100"
                />
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Questions</h3>
              <Button variant="secondary" size="sm" onClick={addQuestion}>
                <Plus size={14} /> Add Question
              </Button>
            </div>

            <div className="space-y-4">
              {form.questions.map((q, idx) => (
                <div key={idx} className="border border-gray-100 rounded-lg p-4 bg-gray-50/50 relative">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center gap-1 pt-7 text-gray-300 flex-shrink-0">
                      <GripVertical size={14} />
                      <Input
                        type="number"
                        value={String(q.sort_order)}
                        onChange={(e) => updateQuestion(idx, 'sort_order', parseInt(e.target.value) || 1)}
                        className="!w-12 !py-1 !px-1.5 text-center text-xs"
                      />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <Input
                            label="Question"
                            value={q.text}
                            onChange={(e) => updateQuestion(idx, 'text', e.target.value)}
                            placeholder="What do you want to ask?"
                          />
                        </div>
                        <Select
                          label="Type"
                          options={QUESTION_TYPES}
                          value={q.question_type}
                          onChange={(e) => updateQuestion(idx, 'question_type', e.target.value)}
                        />
                      </div>

                      {(q.question_type === 'select' || q.question_type === 'multiselect') && (
                        <Input
                          label="Options (comma-separated)"
                          value={q.options}
                          onChange={(e) => updateQuestion(idx, 'options', e.target.value)}
                          placeholder="Option 1, Option 2, Option 3"
                        />
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Helper Text"
                          value={q.helper_text}
                          onChange={(e) => updateQuestion(idx, 'helper_text', e.target.value)}
                          placeholder="Guidance for the rep..."
                        />
                        <Input
                          label="AI Tags (comma-separated)"
                          value={q.ai_tags}
                          onChange={(e) => updateQuestion(idx, 'ai_tags', e.target.value)}
                          placeholder="pain_point, budget"
                        />
                      </div>

                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={q.required}
                            onChange={(e) => updateQuestion(idx, 'required', e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          Required
                        </label>
                        {form.contributes_to_scoring && (
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={q.contributes_to_scoring}
                              onChange={(e) => updateQuestion(idx, 'contributes_to_scoring', e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            Use for scoring
                          </label>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeQuestion(idx)}
                      className="p-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0 mt-6"
                      title="Remove question"
                    >
                      <XIcon size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isSaving}>
              <Save size={14} /> {editingId ? 'Update Playbook' : 'Create Playbook'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
    <div className="hidden xl:block">
      <div className="sticky top-6">
        <CallPulseGuide />
      </div>
    </div>
    </div>
  )
}


function ScoringGuide() {
  const [openSection, setOpenSection] = useState<string | null>('how-it-works')

  const sections = [
    {
      id: 'how-it-works',
      title: 'How Scoring Works',
      content: 'Every lead gets a score from 0-100 based on Business Fit (how well they match your ideal customer), Engagement (how actively they interact), and Urgency (how soon they\'re likely to buy). Each category has a budget — its share of 100 points. Factors within each category compete for that budget based on their relative weights. The final score determines HOT, WARM, or COLD.',
    },
    {
      id: 'category-tips',
      title: 'Category Budget Tips',
      items: [
        'Budgets are each category\'s share of 100 points. All budgets must sum to exactly 100.',
        'Increase Fit budget when reps waste time on leads that engage but never convert — you\'re chasing the wrong type of buyer.',
        'Increase Engagement budget when many leads look similar on paper but you need to identify who\'s serious.',
        'Increase Urgency budget when timing drives your deals — festival seasons, expiring contracts, etc.',
      ],
    },
    {
      id: 'weight-tips',
      title: 'Factor Weight Tips',
      items: [
        'Weights are relative within a category, not absolute. If factors have weights 10, 5, 5 — the first gets 50% of the budget.',
        'Give high weight to signals that reliably predict conversion. Look at your closed-won deals.',
        'Give low weight to nice-to-have signals like "has business email" or "metro city".',
        'Use penalties sparingly — over-penalizing clusters too many leads at the bottom.',
      ],
    },
    {
      id: 'segment-tips',
      title: 'Segment Thresholds',
      items: [
        'Start with defaults (HOT >=75, WARM >=45) and adjust after 2-4 weeks of live data.',
        'Add a 4th tier only if your sales process has a distinct stage for it (e.g., SDR qualification).',
        'Changing thresholds reclassifies all leads — check impact preview first.',
      ],
    },
    {
      id: 'best-practices',
      title: 'Best Practices',
      items: [
        'Prioritize "current selling channel" as a top fit factor for ecommerce leads.',
        'Use CallPulse questions that directly feed scoring — every call becomes a scoring event.',
        'Lock segments during active negotiation to prevent score drops mid-deal.',
        'Limit to 4-6 factors per category. Not every signal should add points.',
        'Revisit scoring config monthly for the first quarter, then quarterly.',
        'Watch for score clustering — if 70% of leads are 50-60, widen the weight spread.',
      ],
    },
    {
      id: 'quick-ref',
      title: 'Quick Reference',
      items: [
        'Most leads HOT → raise threshold or reduce universal-match factors',
        'Reps waste time on bad leads → increase Business Fit budget',
        'Good leads stuck in COLD → collect missing data via CallPulse',
        'Scores don\'t change over time → add recency/activity factors',
        'Lead dropped tier mid-deal → use segment lock',
      ],
    },
  ]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Scoring Guide</h3>
      <div className="space-y-1">
        {sections.map(section => (
          <div key={section.id} className="border-b border-gray-50 last:border-0">
            <button
              onClick={() => setOpenSection(openSection === section.id ? null : section.id)}
              className="w-full flex items-center justify-between py-2 text-left"
            >
              <span className="text-xs font-medium text-gray-700">{section.title}</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} />
            </button>
            {openSection === section.id && (
              <div className="pb-3 text-xs text-gray-600 leading-relaxed">
                {section.content && <p>{section.content}</p>}
                {section.items && (
                  <ul className="list-disc list-outside ml-3.5 space-y-1.5">
                    {section.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ScoringTab() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<any>(null)
  const [reason, setReason] = useState('')
  const [description, setDescription] = useState('')
  const [previewResult, setPreviewResult] = useState<any>(null)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const [addingFactorCategory, setAddingFactorCategory] = useState<string | null>(null)
  const [origLabels, setOrigLabels] = useState<{ categories: Record<string, string>; thresholds: string[] }>({ categories: {}, thresholds: [] })
  const [saveValidationError, setSaveValidationError] = useState<string | null>(null)
  const [isRescoring, setIsRescoring] = useState(false)

  const { data: configData, isLoading } = useQuery({
    queryKey: ['scoring-config'],
    queryFn: async () => {
      const { pipelineStagesApi, scoringConfigApi } = await import('../../api/endpoints')
      const [config, stages] = await Promise.all([
        scoringConfigApi.getActive(),
        pipelineStagesApi.list(),
      ])
      return { config: config?.data, stages: stages?.data }
    },
  })

  const { data: versionsData, isLoading: versionsLoading } = useQuery({
    queryKey: ['scoring-config-versions'],
    queryFn: async () => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.listVersions()
    },
  })

  const { data: attrsData } = useQuery({
    queryKey: ['scoring-available-attributes'],
    queryFn: async () => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.availableAttributes()
    },
    enabled: editing,
  })
  const availableAttrs = attrsData?.data
  const allAttributes = [...(availableAttrs?.built_in || []), ...(availableAttrs?.callpulse || [])]
  const factorTypes = availableAttrs?.factor_types || []

  const rescoreMutation = useMutation({
    mutationFn: async () => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.rescoreAll()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
    },
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.save(draft, description, reason)
    },
    onSuccess: () => {
      setEditing(false)
      setDraft(null)
      setReason('')
      setDescription('')
      setPreviewResult(null)
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['scoring-config-versions'] })
      queryClient.invalidateQueries({ queryKey: ['scoring-segments'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
    },
  })

  const previewMutation = useMutation({
    mutationFn: async () => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.preview(draft)
    },
    onSuccess: (resp) => {
      setPreviewResult(resp?.data)
    },
  })

  const revertMutation = useMutation({
    mutationFn: async (version: number) => {
      const { scoringConfigApi } = await import('../../api/endpoints')
      return scoringConfigApi.revert(version)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['scoring-config-versions'] })
      queryClient.invalidateQueries({ queryKey: ['scoring-segments'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
    },
  })

  if (isLoading) return <div className="flex items-center justify-center h-32 text-gray-400">Loading scoring config...</div>

  const raw = configData?.config?.config
  const config = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
  const stages = configData?.stages || []
  const version = configData?.config?.version || '\u2014'
  const versions = versionsData?.data || []

  const startEditing = () => {
    const cloned = JSON.parse(JSON.stringify(config))
    setDraft(cloned)
    setEditing(true)
    setReason('')
    setDescription('')
    setPreviewResult(null)
    // Capture original labels for rename confirmation
    const catLabels: Record<string, string> = {}
    for (const [key, cat] of Object.entries(cloned.categories || {})) {
      catLabels[key] = (cat as any).label || key
    }
    const thresholdLabels = ((cloned.thresholds || []) as any[]).map((t: any) => t.label || t.segment || '')
    setOrigLabels({ categories: catLabels, thresholds: thresholdLabels })
  }

  const cancelEditing = () => {
    setEditing(false)
    setDraft(null)
    setReason('')
    setDescription('')
    setPreviewResult(null)
  }

  // --- Draft mutators ---
  const updateCategoryMax = (key: string, value: number) => {
    setDraft((d: any) => ({ ...d, categories: { ...d.categories, [key]: { ...d.categories[key], max: value } } }))
    setPreviewResult(null)
  }

  const updateCategoryLabel = (key: string, label: string) => {
    setDraft((d: any) => ({ ...d, categories: { ...d.categories, [key]: { ...d.categories[key], label } } }))
  }

  const addCategory = () => {
    const key = `category_${Date.now()}`
    setDraft((d: any) => ({
      ...d,
      categories: { ...d.categories, [key]: { max: 0, label: 'New Category', description: '' } },
    }))
    setPreviewResult(null)
  }

  const removeCategory = (key: string) => {
    setDraft((d: any) => {
      const removedBudget = d.categories[key]?.max || 0
      const { [key]: _, ...rest } = d.categories
      // Also remove factors in this category
      const factors = { ...d.factors }
      for (const [fk, fv] of Object.entries(factors)) {
        if ((fv as any).category === key) delete factors[fk]
      }
      // Redistribute removed budget proportionally to remaining categories
      const remainingKeys = Object.keys(rest)
      if (remainingKeys.length > 0 && removedBudget > 0) {
        const currentTotal = remainingKeys.reduce((s, k) => s + (rest[k]?.max || 0), 0)
        if (currentTotal > 0) {
          // Proportional redistribution
          let distributed = 0
          remainingKeys.forEach((k, i) => {
            const share = i === remainingKeys.length - 1
              ? removedBudget - distributed
              : Math.round(removedBudget * ((rest[k]?.max || 0) / currentTotal))
            rest[k] = { ...rest[k], max: (rest[k]?.max || 0) + share }
            distributed += share
          })
        } else {
          // All remaining are 0 — split evenly
          const perCat = Math.floor(removedBudget / remainingKeys.length)
          const remainder = removedBudget - perCat * remainingKeys.length
          remainingKeys.forEach((k, i) => {
            rest[k] = { ...rest[k], max: perCat + (i === 0 ? remainder : 0) }
          })
        }
      }
      return { ...d, categories: rest, factors }
    })
    setPreviewResult(null)
  }

  const addThreshold = () => {
    setDraft((d: any) => ({
      ...d,
      thresholds: [...d.thresholds, { segment: `TIER_${d.thresholds.length + 1}`, label: 'New Tier', min_score: 0, color: '#9ca3af' }],
    }))
    setPreviewResult(null)
  }

  const removeThreshold = (idx: number) => {
    setDraft((d: any) => ({ ...d, thresholds: d.thresholds.filter((_: any, i: number) => i !== idx) }))
    setPreviewResult(null)
  }

  const addFactor = (categoryKey: string, attribute: string, factorType: string, label: string) => {
    const key = `${attribute}_${Date.now()}`
    setDraft((d: any) => ({
      ...d,
      factors: {
        ...d.factors,
        [key]: {
          points: factorType === 'penalty' ? 0 : 5,
          category: categoryKey,
          label,
          active: true,
          type: factorType,
          attribute,
          operator: '>=',
          threshold: factorType === 'attribute_threshold' ? 1 : undefined,
          penalty_pct: factorType === 'penalty' ? 0.2 : 0,
        },
      },
    }))
    setAddingFactorCategory(null)
    setPreviewResult(null)
  }

  const removeFactor = (key: string) => {
    setDraft((d: any) => {
      const { [key]: _, ...rest } = d.factors
      return { ...d, factors: rest }
    })
    setPreviewResult(null)
  }

  const updateThreshold = (idx: number, field: string, value: any) => {
    setDraft((d: any) => {
      const thresholds = [...d.thresholds]
      thresholds[idx] = { ...thresholds[idx], [field]: value }
      return { ...d, thresholds }
    })
    setPreviewResult(null)
  }

  const updateFactor = (key: string, field: string, value: any) => {
    setDraft((d: any) => ({ ...d, factors: { ...d.factors, [key]: { ...d.factors[key], [field]: value } } }))
    setPreviewResult(null)
  }

  const budgetSum = draft ? Object.values(draft.categories as Record<string, any>).reduce((s: number, c: any) => s + (c.max || 0), 0) : 0
  const budgetValid = Math.abs(budgetSum - 100) < 0.01

  const displayConfig = editing ? draft : config

  const handleSave = () => {
    setSaveValidationError(null)
    if (draft?.factors) {
      const emptyMatchFactors = Object.entries(draft.factors).filter(
        ([, f]: [string, any]) => f.type === 'attribute_match' && (!f.match_values || f.match_values.length === 0)
      )
      if (emptyMatchFactors.length > 0) {
        const labels = emptyMatchFactors.map(([, f]: [string, any]) => f.label || f.attribute).join(', ')
        setSaveValidationError(`These attribute_match factors have no match values: ${labels}. Add at least one value to each.`)
        return
      }
    }
    saveMutation.mutate()
  }

  const handleRescore = () => {
    setIsRescoring(true)
    rescoreMutation.mutate()
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-8">
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Scoring Configuration</h2>
          <p className="text-xs text-gray-500 mt-0.5">Version {version} — controls how leads are scored and classified</p>
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <>
              <Button onClick={startEditing} variant="secondary" size="sm" disabled={!config}>
                <Pencil size={14} className="mr-1.5" />
                Edit
              </Button>
              <Button
                onClick={handleRescore}
                loading={rescoreMutation.isPending || isRescoring}
                disabled={rescoreMutation.isPending || isRescoring}
                variant="secondary"
                size="sm"
              >
                <RefreshCw size={14} className="mr-1.5" />
                {isRescoring ? 'Rescoring...' : 'Rescore All'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={cancelEditing} variant="secondary" size="sm">
                <XIcon size={14} className="mr-1.5" />
                Cancel
              </Button>
              <Button
                onClick={() => previewMutation.mutate()}
                loading={previewMutation.isPending}
                variant="secondary"
                size="sm"
                disabled={!budgetValid}
              >
                Preview Impact
              </Button>
              <Button
                onClick={handleSave}
                loading={saveMutation.isPending}
                size="sm"
                disabled={!budgetValid || !reason.trim()}
              >
                <Save size={14} className="mr-1.5" />
                Save
              </Button>
            </>
          )}
        </div>
      </div>

      {rescoreMutation.isSuccess && (
        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          All leads have been rescored with the current configuration.
        </div>
      )}
      {saveMutation.isSuccess && (
        <div className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          Scoring configuration saved. Use "Rescore All" to apply to existing leads.
        </div>
      )}
      {saveValidationError && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {saveValidationError}
        </div>
      )}
      {saveMutation.isError && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {(saveMutation.error as any)?.response?.status === 429
            ? (saveMutation.error as any)?.response?.data?.detail || 'Config was changed recently. Include "force" in the reason to override.'
            : 'Failed to save configuration. Check that category budgets sum to 100.'}
        </div>
      )}

      {/* Save section — at top when editing for visibility */}
      {editing && displayConfig && (
        <div className="bg-indigo-50 rounded-xl border border-indigo-200 p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Reason for change *</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Increased weight on engagement signals"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Description (optional)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g. Q2 scoring rebalance"
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white"
                maxLength={200}
              />
            </div>
          </div>
          {previewResult && (
            <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">Impact Preview</p>
              <p className="text-blue-700">
                {previewResult.total_changed} of {previewResult.total_leads} leads would change segment
              </p>
              {previewResult.tier_changes && Object.entries(previewResult.tier_changes).length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(previewResult.tier_changes).map(([change, count]: [string, any]) => (
                    <li key={change} className="text-xs text-blue-600">
                      {change.replace('_to_', ' \u2192 ')}: {count} leads
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!budgetValid && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              Category budgets must sum to exactly 100 (currently {budgetSum}).
            </div>
          )}
          {previewMutation.isError && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Preview failed. Check that category budgets sum to 100 and thresholds are descending.
            </div>
          )}
        </div>
      )}

      {displayConfig ? (
        <>
          {/* Category Budgets */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Category Budgets
                {editing && (
                  <span className={`ml-2 font-normal normal-case ${budgetValid ? 'text-green-600' : 'text-red-500'}`}>
                    (sum = {budgetSum}{budgetValid ? ' \u2713' : ' \u2014 must equal 100'})
                  </span>
                )}
              </h3>
              {editing && (
                <button onClick={addCategory} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus size={12} /> Add Category
                </button>
              )}
            </div>
            <div className="space-y-3">
              {Object.entries(displayConfig.categories || {}).map(([key, cat]: [string, any]) => (
                <div key={key}>
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={cat.label || ''}
                        onChange={(e) => updateCategoryLabel(key, e.target.value)}
                        onBlur={() => {
                          const orig = origLabels.categories[key]
                          if (orig && cat.label && cat.label !== orig) {
                            if (!confirm('Renaming will affect reports. Continue?')) {
                              updateCategoryLabel(key, orig)
                            }
                          }
                        }}
                        className="text-sm font-medium text-gray-800 border border-gray-200 rounded-lg px-2 py-1 w-36 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <input
                        type="range"
                        min={0}
                        max={100}
                        value={cat.max || 0}
                        onChange={(e) => updateCategoryMax(key, Number(e.target.value))}
                        className="flex-1 h-2 accent-blue-600"
                      />
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={cat.max || 0}
                        onChange={(e) => updateCategoryMax(key, Number(e.target.value))}
                        className="w-14 text-sm text-center font-bold border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <button onClick={() => { if (confirm(`Remove "${cat.label || key}"? All factors in this category will also be removed.`)) removeCategory(key) }} className="p-1 text-gray-400 hover:text-red-600" title="Remove category">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700 w-40">{cat.label || key}</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: `${cat.max}%` }} />
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-16 text-center">{cat.max}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Quality Segments */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Quality Segments</h3>
              {editing && (
                <button onClick={addThreshold} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus size={12} /> Add Segment
                </button>
              )}
            </div>
            <div className="space-y-2">
              {(displayConfig.thresholds || []).map((t: any, idx: number) => (
                <div key={idx} className="flex items-center gap-3">
                  {editing ? (
                    <>
                      <input
                        type="color"
                        value={t.color || '#9ca3af'}
                        onChange={(e) => updateThreshold(idx, 'color', e.target.value)}
                        className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5"
                      />
                      <input
                        type="text"
                        value={t.label || ''}
                        onChange={(e) => updateThreshold(idx, 'label', e.target.value)}
                        onBlur={() => {
                          const orig = origLabels.thresholds[idx]
                          if (orig && t.label && t.label !== orig) {
                            if (!confirm('Renaming will affect reports. Continue?')) {
                              updateThreshold(idx, 'label', orig)
                            }
                          }
                        }}
                        placeholder="Label"
                        className="text-sm font-medium border border-gray-200 rounded-lg px-2 py-1 w-24 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <input
                        type="text"
                        value={t.segment || ''}
                        onChange={(e) => updateThreshold(idx, 'segment', e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
                        placeholder="KEY"
                        className="text-xs font-mono border border-gray-200 rounded-lg px-2 py-1 w-20 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <span className="text-xs text-gray-400">{'\u2265'}</span>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={t.min_score}
                        onChange={(e) => updateThreshold(idx, 'min_score', Number(e.target.value))}
                        className="w-14 text-sm text-center border border-gray-200 rounded-lg py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                      <button onClick={() => { if (confirm(`Remove segment "${t.label || t.segment}"?`)) removeThreshold(idx) }} className="p-1 text-gray-400 hover:text-red-600" title="Remove segment">
                        <Trash2 size={14} />
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color || '#9ca3af' }} />
                      <span className="text-sm font-medium text-gray-700">{t.label || t.segment}</span>
                      <span className="text-xs text-gray-400">{'\u2265'} {t.min_score}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Scoring Factors */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Scoring Factors
              <span className="ml-2 font-normal normal-case text-gray-500">
                {Object.values(displayConfig.factors || {}).filter((f: any) => f.active !== false).length} active across {Object.keys(displayConfig.categories || {}).length} categories
              </span>
            </h3>
            <div className="space-y-1">
              {Object.keys(displayConfig.categories || {}).map((catKey) => {
                const catLabel = (displayConfig.categories[catKey] as any)?.label || catKey
                const catFactors = Object.entries(displayConfig.factors || {}).filter(
                  ([, f]: [string, any]) => f.category === catKey
                )
                const isExpanded = expandedCategory === catKey

                return (
                  <div key={catKey} className="border border-gray-100 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : catKey)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-left"
                    >
                      <span className="text-sm font-medium text-gray-700">{catLabel}</span>
                      <span className="flex items-center gap-2">
                        {catFactors.filter(([, f]: [string, any]) => f.active !== false).length === 0 && catFactors.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">All disabled — category scores 0</span>
                        )}
                        {catFactors.length === 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">No factors — add factors to use this budget</span>
                        )}
                        <span className="text-xs text-gray-400">
                          {catFactors.filter(([, f]: [string, any]) => f.active !== false).length}/{catFactors.length} active
                          {!editing && <span className="text-[10px] text-gray-400 ml-1">
                            (weights: {catFactors.reduce((s, [, f]: [string, any]) => s + (f.active !== false ? (f.points || 0) : 0), 0)} &rarr; {displayConfig.categories[catKey]?.max})
                          </span>}
                        </span>
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="divide-y divide-gray-50">
                        {catFactors.map(([fKey, factor]: [string, any]) => (
                          <div key={fKey} className="px-3 py-2">
                            {editing ? (<>
                              <div className="flex items-center gap-2">
                                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                                  <input
                                    type="checkbox"
                                    checked={factor.active !== false}
                                    onChange={(e) => updateFactor(fKey, 'active', e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-8 h-4 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
                                </label>
                                <span className={`text-sm flex-1 ${factor.active !== false ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                  {factor.label || fKey}
                                </span>
                                {factor.type === 'penalty' ? (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className="text-xs text-gray-400">penalty</span>
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      step={5}
                                      value={Math.round((factor.penalty_pct || 0) * 100)}
                                      onChange={(e) => updateFactor(fKey, 'penalty_pct', Number(e.target.value) / 100)}
                                      className="w-14 text-sm text-center border border-gray-200 rounded-lg py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                    <span className="text-xs text-gray-400">%</span>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center flex-shrink-0">
                                    <input
                                      type="number"
                                      min={0}
                                      max={100}
                                      value={factor.points || 0}
                                      onChange={(e) => updateFactor(fKey, 'points', Number(e.target.value))}
                                      className={`w-14 text-sm text-center border rounded-lg py-0.5 focus:outline-none focus:ring-1 ${
                                        (factor.points || 0) > (displayConfig.categories[catKey]?.max || 0)
                                          ? 'border-red-400 focus:ring-red-400 text-red-600'
                                          : 'border-gray-200 focus:ring-blue-400'
                                      }`}
                                    />
                                    <span className="text-[9px] text-gray-400 mt-0.5">weight</span>
                                    {(factor.points || 0) > (displayConfig.categories[catKey]?.max || 0) && (
                                      <span className="text-[9px] text-red-500 mt-0.5">Exceeds budget ({displayConfig.categories[catKey]?.max})</span>
                                    )}
                                  </div>
                                )}
                                {factor.type === 'attribute_match' && (
                                  <MatchValuesEditor
                                    values={factor.match_values || []}
                                    onChange={(vals) => updateFactor(fKey, 'match_values', vals)}
                                    attribute={factor.attribute}
                                  />
                                )}
                                {factor.type === 'attribute_threshold' && (
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <select
                                      value={factor.operator || '>='}
                                      onChange={(e) => updateFactor(fKey, 'operator', e.target.value)}
                                      className="text-xs border border-gray-200 rounded py-0.5 px-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    >
                                      <option value=">=">&ge;</option>
                                      <option value=">">&gt;</option>
                                      <option value="<=">&le;</option>
                                      <option value="<">&lt;</option>
                                      <option value="==">=</option>
                                      <option value="!=">&ne;</option>
                                    </select>
                                    <input
                                      type="number"
                                      value={factor.threshold ?? 0}
                                      onChange={(e) => updateFactor(fKey, 'threshold', Number(e.target.value))}
                                      className="w-16 text-sm text-center border border-gray-200 rounded-lg py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                  </div>
                                )}
                                <button onClick={() => removeFactor(fKey)} className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0" title="Remove factor">
                                  <Trash2 size={12} />
                                </button>
                              </div>
                              {/* Rule subtitle */}
                              {factor.attribute && (
                                <p className="text-[10px] text-gray-400 ml-12 -mt-1 mb-0.5 font-mono">
                                  {humanize(factor.attribute)}{' '}
                                  {factor.type === 'attribute_threshold' && <span>{factor.operator || '>='} {factor.threshold ?? 0}</span>}
                                  {factor.type === 'attribute_match' && <span>in [{(factor.match_values || []).length} values]</span>}
                                  {factor.type === 'field_present' && <span>is present</span>}
                                  {factor.type === 'field_absent' && <span>is empty</span>}
                                  {factor.type === 'attribute_boolean' && <span>is true</span>}
                                  {factor.type === 'penalty' && <span>triggers penalty</span>}
                                </p>
                              )}
                            </>) : (
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${factor.active !== false ? 'bg-green-400' : 'bg-gray-300'}`} />
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm ${factor.active !== false ? 'text-gray-700' : 'text-gray-400'}`}>
                                      {factor.label || fKey}
                                    </span>
                                    {factor.attribute && (
                                      <p className="text-[10px] text-gray-400 font-mono">
                                        {humanize(factor.attribute)}{' '}
                                        {factor.type === 'attribute_threshold' && <span>{factor.operator || '>='} {factor.threshold ?? 0}</span>}
                                        {factor.type === 'attribute_match' && <span>in [{(factor.match_values || []).length} values]</span>}
                                        {factor.type === 'field_present' && <span>is present</span>}
                                        {factor.type === 'field_absent' && <span>is empty</span>}
                                        {factor.type === 'attribute_boolean' && <span>is true</span>}
                                        {factor.type === 'penalty' && <span>triggers penalty</span>}
                                      </p>
                                    )}
                                  </div>
                                  <span className="text-sm font-mono text-gray-500 flex-shrink-0">
                                    {factor.type === 'penalty' ? `${Math.round((factor.penalty_pct || 0) * 100)}% penalty` : `wt ${factor.points}`}
                                  </span>
                                </div>
                                {factor.type === 'attribute_match' && factor.match_values?.length > 0 && (
                                  <div className="ml-4 mt-1">
                                    <MatchValuesTags values={factor.match_values} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        {editing && (
                          <div className="px-3 py-2">
                            {addingFactorCategory === catKey ? (
                              <AddFactorForm
                                attributes={allAttributes}
                                factorTypes={factorTypes}
                                existingFactorAttrs={Object.values(displayConfig.factors || {}).filter((f: any) => f.category === catKey).map((f: any) => f.attribute)}
                                onAdd={(attr, type, label) => addFactor(catKey, attr, type, label)}
                                onCancel={() => setAddingFactorCategory(null)}
                              />
                            ) : (
                              <button onClick={() => setAddingFactorCategory(catKey)} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                                <Plus size={12} /> Add Factor
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

        </>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-400">No scoring config found. Using default values.</p>
        </div>
      )}

      {/* Pipeline Stages */}
      <PipelineStagesEditor stages={stages} queryClient={queryClient} />

      {/* Business Types */}
      <BusinessTypesEditor />

      {/* Version History — at bottom */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Version History</h2>
        {versionsLoading ? (
          <p className="text-sm text-gray-400">Loading versions...</p>
        ) : versions.length > 0 ? (
          <div className="space-y-2">
            {versions.map((v: any) => {
              const isCurrent = v.version === configData?.config?.version
              return (
                <div key={v.version} className={`flex items-center justify-between py-2 px-3 rounded-lg ${isCurrent ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 hover:bg-gray-100'}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-mono font-semibold text-gray-700 flex-shrink-0">v{v.version}</span>
                    <span className="text-sm text-gray-600 truncate">{v.description || 'No description'}</span>
                    {isCurrent && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded font-medium flex-shrink-0">Current</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                    <span className="text-xs text-gray-400">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                    {!isCurrent && (
                      <button
                        onClick={() => { if (window.confirm(`Revert scoring config to version ${v.version}? This will replace the current active configuration.`)) revertMutation.mutate(v.version) }}
                        disabled={revertMutation.isPending}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                      >
                        <RotateCcw size={12} />
                        Revert
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No version history available.</p>
        )}
        {revertMutation.isSuccess && (
          <div className="mt-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            Configuration reverted successfully. All leads will use the restored version.
          </div>
        )}
        {revertMutation.isError && (
          <div className="mt-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            Failed to revert configuration. Please try again.
          </div>
        )}
      </div>
    </div>

    {/* Help Guide - sticky sidebar on desktop */}
    <div className="hidden xl:block">
      <div className="sticky top-6 space-y-4">
        <ScoringGuide />
      </div>
    </div>
    </div>
  )
}


// ---------------------------------------------------------------------------
// Match Values — read-only tag display (collapsible) + editor
// ---------------------------------------------------------------------------

/** Convert snake_case to human-readable: "apparel_and_clothing" → "Apparel & Clothing" */
function humanize(s: string): string {
  return s.replace(/_/g, ' ').replace(/\band\b/g, '&').replace(/\b\w/g, c => c.toUpperCase())
}

function MatchValuesTags({ values }: { values: string[] }) {
  const [expanded, setExpanded] = useState(false)
  const show = expanded ? values : values.slice(0, 3)
  const remaining = values.length - 3

  return (
    <div className="flex flex-wrap gap-1 items-center ml-1">
      {show.map((v, i) => (
        <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{humanize(v)}</span>
      ))}
      {!expanded && remaining > 0 && (
        <button onClick={() => setExpanded(true)} className="text-[10px] text-blue-500 hover:text-blue-700">
          +{remaining} more
        </button>
      )}
      {expanded && values.length > 3 && (
        <button onClick={() => setExpanded(false)} className="text-[10px] text-blue-500 hover:text-blue-700">
          show less
        </button>
      )}
    </div>
  )
}

function MatchValuesEditor({ values, onChange, attribute }: { values: string[]; onChange: (vals: string[]) => void; attribute?: string }) {
  const [input, setInput] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Fetch business types when attribute is business_type
  const { data: btData } = useQuery({
    queryKey: ['business-types'],
    queryFn: async () => {
      const { businessTypesApi } = await import('../../api/endpoints')
      return businessTypesApi.list()
    },
    enabled: attribute === 'business_type',
  })

  const businessTypes: { value: string; label: string }[] = btData?.data || []
  const hasSuggestions = attribute === 'business_type' && businessTypes.length > 0

  // Filter suggestions by input text and exclude already-selected values
  const filtered = hasSuggestions
    ? businessTypes.filter(
        (bt) =>
          !values.includes(bt.value) &&
          (bt.label.toLowerCase().includes(input.toLowerCase()) ||
            bt.value.toLowerCase().includes(input.toLowerCase()))
      )
    : []

  const addValue = (val?: string) => {
    const trimmed = (val || input).trim().toLowerCase()
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed])
    }
    setInput('')
    setShowSuggestions(false)
  }

  const removeValue = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx))
  }

  return (
    <div className="flex-1 min-w-0 relative">
      <div className="flex flex-wrap gap-1 items-center">
        {values.map((v, i) => {
          const bt = businessTypes.find((b) => b.value === v)
          return (
            <span key={i} className="inline-flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">
              {bt?.label || humanize(v)}
              <button onClick={() => removeValue(i)} className="text-blue-400 hover:text-red-500 ml-0.5">&times;</button>
            </span>
          )
        })}
        <input
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); setShowSuggestions(true) }}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addValue() } }}
          onBlur={() => { setTimeout(() => setShowSuggestions(false), 150); if (!hasSuggestions) addValue() }}
          onFocus={() => { if (hasSuggestions) setShowSuggestions(true) }}
          placeholder={values.length === 0 ? (hasSuggestions ? 'Search business types...' : 'Type value + Enter') : ''}
          className="text-xs border-0 outline-none bg-transparent w-24 py-0.5"
        />
      </div>
      {/* Searchable dropdown for business types */}
      {showSuggestions && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 mt-1 w-56 max-h-40 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
          {filtered.map((bt) => (
            <button
              key={bt.value}
              onMouseDown={(e) => { e.preventDefault(); addValue(bt.value) }}
              className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 flex items-center justify-between"
            >
              <span>{bt.label}</span>
              <span className="text-[9px] text-gray-400 font-mono">{bt.value}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Add Factor Form (sub-component of ScoringTab)
// ---------------------------------------------------------------------------

function AddFactorForm({
  attributes,
  factorTypes,
  existingFactorAttrs,
  onAdd,
  onCancel,
}: {
  attributes: any[]
  factorTypes: any[]
  existingFactorAttrs: string[]
  onAdd: (attribute: string, type: string, label: string) => void
  onCancel: () => void
}) {
  const [selectedAttr, setSelectedAttr] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [label, setLabel] = useState('')

  const handleAttrChange = (attrKey: string) => {
    setSelectedAttr(attrKey)
    const attr = attributes.find((a: any) => a.key === attrKey)
    if (attr) {
      setSelectedType(attr.type || 'field_present')
      setLabel(attr.label || attrKey.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()))
    }
  }

  // Group attributes
  const builtIn = attributes.filter((a: any) => !a.source)
  const callpulse = attributes.filter((a: any) => a.source === 'callpulse')

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={selectedAttr}
          onChange={(e) => handleAttrChange(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white flex-1 min-w-[180px]"
        >
          <option value="">Select attribute...</option>
          <optgroup label="Built-in">
            {builtIn.map((a: any) => (
              <option key={a.key} value={a.key} disabled={existingFactorAttrs.includes(a.key)}>
                {a.label}{existingFactorAttrs.includes(a.key) ? ' (already added)' : ''}
              </option>
            ))}
          </optgroup>
          {callpulse.length > 0 && (
            <optgroup label="CallPulse Collected">
              {callpulse.map((a: any) => (
                <option key={a.key} value={a.key} disabled={existingFactorAttrs.includes(a.key)}>
                  {a.label}{existingFactorAttrs.includes(a.key) ? ' (already added)' : ''}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white w-40"
        >
          <option value="">Type...</option>
          {factorTypes.map((t: any) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <button onClick={onCancel} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
        <Button
          onClick={() => onAdd(selectedAttr, selectedType, label)}
          size="sm"
          disabled={!selectedAttr || !selectedType}
        >
          Add
        </Button>
      </div>
      {selectedAttr && (
        <p className="text-[10px] text-gray-500">
          {attributes.find((a: any) => a.key === selectedAttr)?.description}
        </p>
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Business Types Editor (sub-component of ScoringTab)
// ---------------------------------------------------------------------------

function BusinessTypesEditor() {
  const queryClient = useQueryClient()
  const [newValue, setNewValue] = useState('')
  const [newLabel, setNewLabel] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['business-types'],
    queryFn: async () => {
      const { businessTypesApi } = await import('../../api/endpoints')
      return businessTypesApi.list()
    },
  })

  const saveMutation = useMutation({
    mutationFn: async (types: { value: string; label: string }[]) => {
      const { businessTypesApi } = await import('../../api/endpoints')
      return businessTypesApi.save(types)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-types'] })
    },
  })

  const types: { value: string; label: string }[] = data?.data || []

  const addType = () => {
    const slug = newValue.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')
    const label = newLabel.trim() || slug.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
    if (!slug || types.some(t => t.value === slug)) return
    saveMutation.mutate([...types, { value: slug, label }])
    setNewValue('')
    setNewLabel('')
  }

  const removeType = (value: string) => {
    saveMutation.mutate(types.filter(t => t.value !== value))
  }

  if (isLoading) return null

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">Business Types</h2>
        <span className="text-xs text-gray-400">{types.length} types</span>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {types.map(t => (
          <span key={t.value} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
            {t.label}
            <button onClick={() => removeType(t.value)} className="text-gray-400 hover:text-red-500">&times;</button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder="slug (e.g. pet_grooming)"
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          onKeyDown={(e) => { if (e.key === 'Enter') addType() }}
        />
        <input
          type="text"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Label (auto from slug)"
          className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
          onKeyDown={(e) => { if (e.key === 'Enter') addType() }}
        />
        <Button onClick={addType} size="sm" disabled={!newValue.trim() || saveMutation.isPending}>
          <Plus size={14} className="mr-1" /> Add
        </Button>
      </div>
      {saveMutation.isError && (
        <p className="text-xs text-red-500 mt-2">Failed to save. Please try again.</p>
      )}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Pipeline Stages Editor (sub-component of ScoringTab)
// ---------------------------------------------------------------------------

function PipelineStagesEditor({ stages, queryClient }: { stages: any[]; queryClient: any }) {
  const [addOpen, setAddOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState({ slug: '', label: '', color: '#6b7280', is_terminal: false, counts_as_engaged: false })
  const [deactivateModal, setDeactivateModal] = useState<{ stageId: number; stageLabel: string; leadCount: number } | null>(null)

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const { pipelineStagesApi } = await import('../../api/endpoints')
      return pipelineStagesApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      setAddOpen(false)
      setForm({ slug: '', label: '', color: '#6b7280', is_terminal: false, counts_as_engaged: false })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Record<string, unknown> }) => {
      const { pipelineStagesApi } = await import('../../api/endpoints')
      return pipelineStagesApi.update(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      setEditingId(null)
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: async (id: number) => {
      const { pipelineStagesApi } = await import('../../api/endpoints')
      return pipelineStagesApi.deactivate(id)
    },
    onSuccess: (resp) => {
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
      const leadCount = resp?.data?.leads_in_stage ?? 0
      if (leadCount > 0) {
        setDeactivateModal({
          stageId: 0,
          stageLabel: resp?.data?.stage?.label || 'this stage',
          leadCount,
        })
      }
    },
  })

  const reorderMutation = useMutation({
    mutationFn: async (items: { id: number; position: number }[]) => {
      const { pipelineStagesApi } = await import('../../api/endpoints')
      return pipelineStagesApi.reorder(items)
    },
    onSuccess: () => {
      setDraftStages(null)
      queryClient.invalidateQueries({ queryKey: ['scoring-config'] })
      queryClient.invalidateQueries({ queryKey: ['pipeline-stages'] })
    },
  })

  // Draft-based reorder — changes are local until Save
  const [draftStages, setDraftStages] = useState<any[] | null>(null)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null)

  const displayStages = draftStages || stages
  const hasReorderChanges = draftStages !== null

  const swapStages = (idx: number, direction: 'up' | 'down') => {
    const current = [...(draftStages || stages)]
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1
    if (targetIdx < 0 || targetIdx >= current.length) return
    const temp = current[idx]
    current[idx] = current[targetIdx]
    current[targetIdx] = temp
    setDraftStages(current)
  }

  const handleDragEnd = (fromIdx: number, toIdx: number) => {
    if (fromIdx === toIdx) return
    const reordered = [...(draftStages || stages)]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    setDraftStages(reordered)
    setDragIdx(null)
    setDragOverIdx(null)
  }

  const saveReorder = () => {
    if (!draftStages) return
    const updated = draftStages.map((s: any, i: number) => ({ id: s.id, position: i + 1 }))
    reorderMutation.mutate(updated)
  }

  const cancelReorder = () => {
    setDraftStages(null)
    setDragIdx(null)
    setDragOverIdx(null)
  }

  const startEdit = (s: any) => {
    setEditingId(s.id)
    setForm({ slug: s.slug, label: s.label, color: s.color || '#6b7280', is_terminal: s.is_terminal, counts_as_engaged: s.counts_as_engaged })
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-900">Pipeline Stages</h2>
        <div className="flex items-center gap-2">
          {hasReorderChanges && (
            <>
              <Button onClick={cancelReorder} variant="secondary" size="sm">
                <XIcon size={14} className="mr-1" /> Cancel
              </Button>
              <Button onClick={saveReorder} loading={reorderMutation.isPending} size="sm">
                <Save size={14} className="mr-1" /> Save Order
              </Button>
            </>
          )}
          <Button onClick={() => { setAddOpen(true); setForm({ slug: '', label: '', color: '#6b7280', is_terminal: false, counts_as_engaged: false }) }} variant="secondary" size="sm">
            <Plus size={14} className="mr-1" /> Add Stage
          </Button>
        </div>
      </div>
      {hasReorderChanges && (
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          Stage order changed. Click "Save Order" to apply, or "Cancel" to discard.
        </div>
      )}

      {createMutation.isError && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Failed to create stage. Slug may already exist.
        </div>
      )}
      {deactivateMutation.isError && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Cannot deactivate: at least 1 non-terminal + 1 terminal stage must remain.
        </div>
      )}

      {/* Add stage form */}
      {addOpen && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <input type="text" placeholder="Slug (e.g. discovery_call)" value={form.slug} onChange={(e) => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
            <input type="text" placeholder="Label (e.g. Discovery Call)" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} className="text-sm border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400" />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0.5" />
              <span className="text-xs text-gray-500">Color</span>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={form.is_terminal} onChange={(e) => setForm(f => ({ ...f, is_terminal: e.target.checked }))} className="rounded" />
              Terminal
            </label>
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={form.counts_as_engaged} onChange={(e) => setForm(f => ({ ...f, counts_as_engaged: e.target.checked }))} className="rounded" />
              Counts as Engaged
            </label>
            <div className="flex-1" />
            <Button onClick={() => setAddOpen(false)} variant="secondary" size="sm">Cancel</Button>
            <Button onClick={() => createMutation.mutate(form)} loading={createMutation.isPending} size="sm" disabled={!form.slug || !form.label}>Create</Button>
          </div>
        </div>
      )}

      {displayStages.length > 0 ? (
        <div className="space-y-1">
          {displayStages.map((s: any, i: number) => (
            <div key={s.id || i}>
              {editingId === s.id ? (
                <div className="p-2 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      {i > 0 && (
                        <button onClick={() => swapStages(i, 'up')} className="p-0.5 text-gray-400 hover:text-blue-600" title="Move up"><ChevronUp size={14} /></button>
                      )}
                      {i < displayStages.length - 1 && (
                        <button onClick={() => swapStages(i, 'down')} className="p-0.5 text-gray-400 hover:text-blue-600" title="Move down"><ChevronDown size={14} /></button>
                      )}
                    </div>
                    <input type="color" value={form.color} onChange={(e) => setForm(f => ({ ...f, color: e.target.value }))} className="w-7 h-7 rounded border border-gray-200 cursor-pointer p-0.5" />
                    <input type="text" value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} className="text-sm font-medium border border-gray-200 rounded-lg px-2 py-1 flex-1 focus:outline-none focus:ring-1 focus:ring-blue-400" />
                    <span className="text-[10px] text-gray-400 font-mono">{s.slug}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={form.is_terminal} onChange={(e) => setForm(f => ({ ...f, is_terminal: e.target.checked }))} className="rounded" />
                      Terminal
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={form.counts_as_engaged} onChange={(e) => setForm(f => ({ ...f, counts_as_engaged: e.target.checked }))} className="rounded" />
                      Counts as Engaged
                    </label>
                    <div className="flex-1" />
                    <Button onClick={() => setEditingId(null)} variant="secondary" size="sm">Cancel</Button>
                    <Button onClick={() => updateMutation.mutate({ id: s.id, data: { label: form.label, color: form.color, is_terminal: form.is_terminal, counts_as_engaged: form.counts_as_engaged } })} loading={updateMutation.isPending} size="sm">Save</Button>
                  </div>
                </div>
              ) : (
                <div
                  className={`flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-gray-50 group cursor-grab active:cursor-grabbing ${dragOverIdx === i ? 'border-t-2 border-blue-400' : ''} ${dragIdx === i ? 'opacity-40' : ''}`}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIdx(i) }}
                  onDragLeave={() => setDragOverIdx(null)}
                  onDrop={() => { if (dragIdx !== null) handleDragEnd(dragIdx, i) }}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null) }}
                >
                  <GripVertical size={14} className="text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
                  <span className="text-xs text-gray-400 w-4 text-center flex-shrink-0">{i + 1}</span>
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#9ca3af' }} />
                  <span className="text-sm font-medium text-gray-800 flex-1">{s.label}</span>
                  <span className="text-[10px] text-gray-400 font-mono">{s.slug}</span>
                  {s.is_terminal && <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">Terminal</span>}
                  {s.counts_as_engaged && <span className="text-[10px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded">Engaged</span>}
                  {!s.is_active && <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">Inactive</span>}
                  <div className="hidden group-hover:flex items-center gap-1">
                    <button onClick={() => startEdit(s)} className="p-1 text-gray-400 hover:text-blue-600"><Pencil size={12} /></button>
                    {s.is_active && (
                      <button onClick={() => { if (confirm(`Deactivate "${s.label}"?`)) deactivateMutation.mutate(s.id) }} className="p-1 text-gray-400 hover:text-red-600"><Trash2 size={12} /></button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400">No pipeline stages configured.</p>
      )}

      {/* Deactivation warning modal */}
      <Modal open={!!deactivateModal} onClose={() => setDeactivateModal(null)} title="Stage Deactivated">
        {deactivateModal && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              <strong>{deactivateModal.stageLabel}</strong> has been deactivated. {deactivateModal.leadCount} lead{deactivateModal.leadCount !== 1 ? 's' : ''} need{deactivateModal.leadCount === 1 ? 's' : ''} to be moved to another stage.
            </p>
            <p className="text-xs text-gray-500">
              You can move these leads from the Leads page using bulk actions.
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setDeactivateModal(null)} size="sm">OK</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
